/**
 * DecisionFlow — CompAdminShell
 * ui/src/shells/CompAdminShell.jsx
 * S73-UI-07
 *
 * Shell for user_type = "comp_admin". Route root: /admin/
 *
 * Migrated from DecisionFlowEnterpriseApp in App.jsx (S72 WB-Step3).
 * Integer stage switcher replaced with route-based nav per spec v1.3 §3.1.
 * Governance stages (2, 3) removed — belong to deferred TenantAdminShell (spec §3.1 note).
 *
 * Nav groups (spec v1.3 §3.1):
 *   Configuration: fiscal-calendar(4), organization(5), sales-roles(6),
 *                  plan-wizard(9), plan-approval(10)
 *   Data Setup:    position-load(7), position-detail(8), plan-assignment(11),
 *                  quota-load(12), credit-config(13), credit-load(14)
 *   Execution:     readiness(15), calculation(16), statements(17),
 *                  payroll(18), certification(19)
 *
 * ContextHeader (spec §6.2):
 *   - Group breadcrumb, stage ID (dev only), fiscal period, user name, role
 *   - Progress bars derived from wizard session step completion
 *   - Source: GET /api/v1/wizard/session?fiscal_period_id={id}  (OI-S208-03: was /wizard-session)
 *   - If wizard session unavailable: bars at 0% — no mock values (§6.2)
 *
 * Workspace block (spec §3.1):
 *   - Tenant name from auth context
 *   - Fiscal period from GET /api/v1/fiscal-periods?period_status=open
 *   - Subscription tier from fiscal-periods response (NOT from JWT — spec §3.1)
 *
 * DD-06: All panel values from live API. Dev mode retains mock fallback where marked.
 * DD-05: Role from JWT user_type. Dev role switcher only when VITE_APP_MODE=dev.
 *
 * S230 SECOND: OPERATE wiring — ExecuteFlowShell + OperateDashboard added.
 *   - Route /admin/operate → OperateDashboard (period selector + ExecuteFlowShell)
 *   - Shell-level scenario set fetch added; passed to ExecuteFlowShell
 *   - "Execute Flow" nav item added to OPERATE group
 *   - ExecuteFlowShell imported from tenant-admin/ (relocated to admin/ — S231 SECOND)
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
  AlertTriangle, BadgeDollarSign, BookOpen, Calculator, CalendarDays,
  CheckCircle2, ChevronDown, ChevronRight, ClipboardCheck,
  DatabaseZap, FileCheck2, FileText, MapPin, Menu, MessageSquareText,
  Moon, Network, PanelRightOpen, Search, Settings, Shield, ShieldAlert,
  Sparkles, Sun,
  Target, Upload, UserRound, WalletCards, Workflow, XCircle,
  Bell, X, AlertCircle, ShieldCheck,
  Users2, Globe, Trophy, Layers3, Star, FlaskConical, BarChart3, GitBranch,
  Lock, LockOpen, Clock, RefreshCw, Camera, Send, ThumbsUp,
  ListChecks, TriangleAlert, CircleSlash, ArrowRight, Info,
  ChevronUp, ChevronsUpDown, Eye, GitPullRequest, Plus, History,
} from "lucide-react";

import { useAuth } from "../auth/AuthContext.jsx";
import { useFeatures } from "../auth/FeatureContext.jsx";
import Button from "../components/shared/Button.jsx";
import Card from "../components/shared/Card.jsx";
import Field from "../components/shared/Field.jsx";
import Pill from "../components/shared/Pill.jsx";
import TopBar from "../components/shared/TopBar.jsx";
import CompException from "../components/admin/CompException.jsx";
import CompDispute from "../components/admin/CompDispute.jsx";
import TrustCenter from "../components/admin/TrustCenter.jsx";

import AdminFlowShell    from "../components/tenant-admin/AdminFlowShell.jsx";
import ParameterConfigEditor from "../components/tenant-admin/ParameterConfigEditor.jsx";
import PayoutResultsViewer from "../components/tenant-admin/PayoutResultsViewer.jsx"; // S573
import DrawPlanManager   from "../components/tenant-admin/DrawPlanManager.jsx";
import DealSplitManager from "../components/tenant-admin/DealSplitManager.jsx";
import SpiffPlanManager from "../components/tenant-admin/SpiffPlanManager.jsx";
import TransactionPlanManager from "../components/tenant-admin/TransactionPlanManager.jsx";

import AcceleratorCapManager    from "../components/tenant-admin/AcceleratorCapManager.jsx";
import KickerPlanManager        from "../components/tenant-admin/KickerPlanManager.jsx";
import RevTypeRateTableManager  from "../components/tenant-admin/RevTypeRateTableManager.jsx";
import ClawbackPolicyManager    from "../components/tenant-admin/ClawbackPolicyManager.jsx";
import TrueUpManager            from "../components/tenant-admin/TrueUpManager.jsx";
import CoSellManager            from "../components/tenant-admin/CoSellManager.jsx";
import OverlayAttributionManager from "../components/tenant-admin/OverlayAttributionManager.jsx";
import PlansLandingPage         from "../components/tenant-admin/PlansLandingPage.jsx";
import PlanDetailView           from "../components/tenant-admin/PlanDetailView.jsx";

// Phase 4 — Pattern B standalone managers (S377)
import TerritoryManager         from "../components/tenant-admin/TerritoryManager.jsx";
import PlanVersioningManager    from "../components/tenant-admin/PlanVersioningManager.jsx";
import PharmaManager            from "../components/tenant-admin/PharmaManager.jsx";
import MarketShareManager       from "../components/tenant-admin/MarketShareManager.jsx";
// Phase 4 — S378 (stubs — wired now, built next session)
import PodManager               from "../components/tenant-admin/PodManager.jsx";
import ChannelManager           from "../components/tenant-admin/ChannelManager.jsx";
import ContestManager           from "../components/tenant-admin/ContestManager.jsx";
import BonusPoolManager         from "../components/tenant-admin/BonusPoolManager.jsx";
import NonCashAwardManager      from "../components/tenant-admin/NonCashAwardManager.jsx";

import ExecuteFlowShell  from "../components/admin/ExecuteFlowShell.jsx"; // S230 OPERATE wiring — relocated to admin/ — S231 SECOND
import AmendmentBeginModal from "../components/admin/AmendmentBeginModal.jsx"; // OI-S387-01: component approval queue
import CertificationPanel from "../shells/CertificationPanel.jsx"; // extracted S200
import CopilotPanel from "../components/shared/CopilotPanel.jsx"; // S239 — extracted agentic panel
import WizardStageSteps from "../components/admin/WizardStageSteps.jsx"; // S247 Phase 10

// S540 — Compensation Policies workspace (Step 2)
import CompensationPoliciesWorkspace from "../components/admin/CompensationPoliciesWorkspace.jsx";

// S552 — New panels
import ImportDataPanel from "../components/admin/ImportDataPanel.jsx";
import SettingsPanel   from "../components/admin/SettingsPanel.jsx";

// S615 — Commercial Role Library (replaces CommercialRoleLibraryStub)
import CommercialRoleLibrary from "../components/admin/CommercialRoleLibrary.jsx";

// S620 — Change Management Panel (CCM spec §7–8)
import ChangeManagementPanel from "../components/admin/ChangeManagementPanel.jsx";

// S660 — Seat limit banner (OI-SEAT-IMPLEMENTATION)
import { SeatLimitBanner } from "../components/SeatLimitBanner.jsx";

const API_BASE = "/api/v1";

const ROLE_PROFILES = {
  comp_admin: { label: "Comp Admin" },
  finance:    { label: "Finance" },
  manager:    { label: "Manager" },
  rep:        { label: "Rep" },
  tenant_super_admin: { label: "Tenant Super Admin" },
};

// ---------------------------------------------------------------------------
// Nav structure — S228 four-section redesign (§10.2, §10.10)
// SETUP / OPERATE / GOVERN / FINANCE
// S230 SECOND: "Execute Flow" added as first item in OPERATE
// ---------------------------------------------------------------------------

// S623: NAV_GROUPS — 7 groups (DESIGN / SETUP / IMPORT / EXECUTE / RESOLVE / GOVERN / FINANCE)
// Settings gear at rail footer — role-aware — NOT in NAV_GROUPS.
// All removed/renamed routes remain in <Routes> below — deep links preserved.

// ---------------------------------------------------------------------------
// NAV_GROUPS — S623 restructure
//
// DESIGN   — Period-agnostic. Build-order enforced by item sequence:
//              1. Organization first  (segment_id + bu_id are NN on plan_component)
//              2. Commercial Role Library (role_id is NN on plan_component)
//              3. Compensation Policies  (plan_component requires both org + role to exist)
//            All three surfaces receive no period prop — they are tenant-level design
//            artifacts stable across periods.
//
// SETUP    — Period-aware scaffold for the current cycle.
//              Fiscal Calendar: period creation + roll-forward
//              Positions: period-scoped roster (role_id FK references DESIGN layer)
//
// IMPORT   — Cross-cutting bulk load utility. Single-item group signals it is a
//            mechanism, not a workflow stage. Feeds quota, credit, rate tables,
//            ramp schedules, OTE history, exchange rates.
//
// EXECUTE  — The repeating monthly operating cycle. Dependency order:
//              Assignments → Quotas → Credits → Readiness (cross-gate validator)
//              → Calculate → Statements → Payroll View → Payroll Export → Certify & Close
//            Quotas before Credits: quota is the attainment denominator (plan design
//            artefact); credit is the numerator (actuals). Denominator logically
//            precedes numerator in setup sequence.
//
// RESOLVE / GOVERN / FINANCE — unchanged from S622.
// ---------------------------------------------------------------------------

const NAV_GROUPS = [
  {
    name: "DESIGN",
    items: [
      // Build order: org structure → roles → plans. Each depends on the one above.
      { label: "Organization",            icon: Network,        to: "/admin/organization",     stageId: null },
      { label: "Commercial Role Library", icon: BookOpen,       to: "/admin/commercial-roles", stageId: null },
      { label: "Compensation Policies",   icon: FileCheck2,     to: "/admin/compensation",     stageId: null,
        // Sub-nav: shown indented when /admin/compensation is active.
        // Each child sets ?tab= on the same route — matches CompensationPoliciesWorkspace useSearchParams.
        // AD-174: Library before Plans. AD-176: Plans and Approvals carry badges (computed in shell).
        children: [
          { label: "Library",     icon: Sparkles,     tab: "templates"   },
          { label: "Plans",       icon: FileCheck2,   tab: "plans",       badge: "plans"     },
          { label: "Components",  icon: Layers3,      tab: "components"  },
          { label: "Assignments", icon: Workflow,     tab: "assignments" },
          { label: "Approvals",   icon: CheckCircle2, tab: "approvals",   badge: "approvals" },
          { label: "History",     icon: History,      tab: "history"     },
        ],
      },
    ],
  },
  {
    name: "SETUP",
    items: [
      { label: "Fiscal Calendar", icon: CalendarDays, to: "/admin/fiscal-calendar",  stageId: null },
      { label: "Positions",       icon: UserRound,    to: "/admin/position-detail",  stageId: null },
    ],
  },
  {
    name: "IMPORT",
    items: [
      { label: "Import Data", icon: Upload, to: "/admin/import", stageId: null },
    ],
  },
  {
    name: "EXECUTE",
    items: [
      { label: "Assignments",      icon: Workflow,       to: "/admin/plan-assignment", stageId: null },
      { label: "Quotas",           icon: Target,         to: "/admin/quota-load",      stageId: null },
      { label: "Credits",          icon: DatabaseZap,    to: "/admin/credit-config",   stageId: null },
      { label: "Readiness",        icon: AlertTriangle,  to: "/admin/readiness",       stageId: null },
      { label: "Calculate",        icon: Calculator,     to: "/admin/calculation",     stageId: null },
      { label: "Statements",       icon: FileText,       to: "/admin/statements",      stageId: null },
      { label: "Payroll - View",   icon: BarChart3,      to: "/admin/payroll-view",    stageId: null },
      { label: "Payroll - Export", icon: WalletCards,    to: "/admin/payroll",         stageId: null },
      { label: "Certify & Close",  icon: CheckCircle2,   to: "/admin/certification",   stageId: null },
    ],
  },
  {
    name: "RESOLVE",
    items: [
      { label: "Exceptions", icon: ShieldAlert, to: "/admin/exceptions", stageId: null },
      { label: "Disputes",   icon: AlertCircle, to: "/admin/disputes",   stageId: null },
    ],
  },
  {
    name: "GOVERN",
    items: [
      { label: "Change Management",  icon: GitPullRequest, to: "/admin/change-management", stageId: null },
      { label: "Admin",              icon: ShieldCheck,    to: "/admin/governance",         stageId: null },
      { label: "Agent Trust Center", icon: Shield,         to: "/admin/trust-center",       stageId: null },
    ],
  },
  {
    name: "FINANCE",
    items: [
      { label: "Finance View", icon: BadgeDollarSign, to: "/admin/finance-view", stageId: null },
    ],
  },
];

// Stage ID → group name lookup
const STAGE_GROUP = {};
NAV_GROUPS.forEach((g) => g.items.forEach((item) => { STAGE_GROUP[item.stageId] = g.name; }));

// Route → nav item lookup
const ROUTE_TO_NAV = {};
NAV_GROUPS.forEach((g) => g.items.forEach((item) => { ROUTE_TO_NAV[item.to] = item; }));

// OI-CCM-BREADCRUMB (S622):
// STAGE_GROUP[null] returns "" for all stageId:null routes (GOVERN, RESOLVE, DESIGN, FINANCE items).
// The breadcrumb div was suppressed because groupName was always falsy for these routes.
// Fix: build a direct route→group-name lookup so we can always surface the section label.
const ROUTE_TO_GROUP = {};
NAV_GROUPS.forEach((g) => g.items.forEach((item) => { ROUTE_TO_GROUP[item.to] = g.name; }));

function useFetch(url) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

function ValidationItem({ ok, warn, text }) {
  return (
    <div className="flex items-start gap-2 text-tok-sm">
      {ok
        ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
        : warn
          ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-danger))]" />}
      <span className="text-[rgb(var(--color-text))]">{text}</span>
    </div>
  );
}



// ---------------------------------------------------------------------------
// PeriodSelector — S602 OI-PERIOD-CONTEXT-SWITCHER
// Renders as an inline <select> in CompAdminContextHeader.
// Reads the already-fetched periods array from the shell.
// Groups periods by fiscal_year_id for clean display.
// period_type (monthly | quarterly | annual) shown as suffix for clarity.
// ---------------------------------------------------------------------------

function PeriodSelector({ periods, selectedPeriodId, defaultPeriod, onSelect }) {
  if (!periods || periods.length === 0) return null;

  const currentId = selectedPeriodId ?? defaultPeriod?.fiscal_period_id;

  // Group by fiscal_year_id to support multi-year tenants cleanly.
  const grouped = periods.reduce((acc, p) => {
    const yr = p.fiscal_year_id ?? "other";
    if (!acc[yr]) acc[yr] = [];
    acc[yr].push(p);
    return acc;
  }, {});

  const yearKeys = Object.keys(grouped).sort();
  const multiYear = yearKeys.length > 1;

  function statusSuffix(p) {
    if (p.period_status === "locked") return " · locked";
    if (p.period_status === "closed") return " · closed";
    return "";
  }

  return (
    <select
      value={currentId ?? ""}
      onChange={(e) => {
        const val = e.target.value;
        onSelect(val ? Number(val) : null);
      }}
      className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text))] focus:outline-none cursor-pointer"
      aria-label="Select fiscal period"
    >
      {multiYear
        ? yearKeys.map((yr) => (
            <optgroup key={yr} label={`FY ${yr}`}>
              {grouped[yr].map((p) => (
                <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                  {p.period_label ?? p.period_code}{statusSuffix(p)}
                </option>
              ))}
            </optgroup>
          ))
        : periods.map((p) => (
            <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
              {p.period_label ?? p.period_code}{statusSuffix(p)}
            </option>
          ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// CompAdmin ContextHeader (spec §6.2)
// ---------------------------------------------------------------------------

function CompAdminContextHeader({ navItem, mode, period, periods, selectedPeriodId, defaultPeriod, isNonDefaultPeriod, onPeriodSelect, wizardSteps, userName, userRole, tenantName, previewRunCompleted, location }) {
  const stepsMap = useMemo(() => {
    const m = {};
    (wizardSteps ?? []).forEach((s) => { m[s.stage_id] = s.step_status; });
    return m;
  }, [wizardSteps]);

  // Breadcrumb group name resolution.
  //
  // Bug: STAGE_GROUP is keyed by stageId. All stageId:null items collide on the same
  // STAGE_GROUP[null] key — last writer wins, which is always the last NAV_GROUPS entry.
  // This caused every stageId:null route to show that last group name (e.g. "FINANCE")
  // regardless of which group the item actually belongs to.
  //
  // Fix: only consult STAGE_GROUP when stageId is a real integer (wizard stages 4-19).
  // For all other routes use ROUTE_TO_GROUP which is keyed by path and always correct.
  const groupName = navItem
    ? (typeof navItem.stageId === "number"
        ? (STAGE_GROUP[navItem.stageId] || ROUTE_TO_GROUP[navItem.to] || "")
        : (ROUTE_TO_GROUP[navItem.to] || ""))
    : "";

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          {groupName && (
            <div className="mb-0.5 flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              <span>{groupName}</span>
              {navItem && mode === "dev" && typeof navItem.stageId === "number" && (
                <><ChevronRight className="h-3 w-3" /><span className="font-mono">Stage {navItem.stageId}</span></>
              )}
            </div>
          )}
          <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
            {location?.pathname?.startsWith('/admin/plans') ? 'Plans' : navItem?.label ?? 'Dashboard'}
          </h1>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            {/* S602 — OI-PERIOD-CONTEXT-SWITCHER: interactive period selector replaces static label */}
            {periods && periods.length > 0 && (
              <PeriodSelector
                periods={periods}
                selectedPeriodId={selectedPeriodId}
                defaultPeriod={defaultPeriod}
                onSelect={onPeriodSelect}
              />
            )}
            {/* Non-current-period warning — persistent, always visible when viewing a non-default period */}
            {isNonDefaultPeriod && period && (
              <span className="inline-flex items-center gap-1 rounded-md bg-[rgb(var(--color-warning))]/10 px-2 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-warning))] border border-[rgb(var(--color-warning))]/30">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                Not the active period
                {period.period_status !== "open" && (
                  <span className="font-normal opacity-80">· {period.period_status}</span>
                )}
              </span>
            )}
            {userName && <><span>·</span><span>{userName}</span></>}
          </div>
        </div>
      </div>

      <WizardStageSteps
        wizardSteps={wizardSteps}
        fiscalPeriodId={period?.fiscal_period_id ?? null}
        previewRunCompleted={previewRunCompleted}
        role={userRole}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompAdmin Left Nav — grouped (spec §3.1). Groups always expanded in v1.
// ---------------------------------------------------------------------------

// S552: CompAdminNav — SETUP/DESIGN/EXECUTE/RESOLVE/GOVERN/FINANCE groups.
// Readiness nav item shows badge: open exceptions + disputes count.
// Settings gear at rail footer — role-aware — routes to /admin/settings.
const NAV_MIN_WIDTH = 160;
const NAV_MAX_WIDTH = 360;
const NAV_DEFAULT_WIDTH = 224; // w-56 equivalent

function CompAdminNav({ mobileOpen, onClose, mode, role, resolveBadge, navWidth, onNavWidthChange, stmtBadge, navOpen, compPlansBadge, compApprovalsBadge }) {
  // S669 — Unified feature gating via FeatureContext (replaces tenantContext.features booleans).
  // hasFeature returns true while loading (optimistic) — no flash of missing nav items.
  const { hasFeature, hasPlanType } = useFeatures();
  const location = useLocation();
  const canSeeSettings = role === "comp_admin" || role === "tenant_super_admin";
  const dragRef = useRef(null);

  // Resolve badge value for a comp sub-nav child by its badge key
  function compChildBadge(badgeKey) {
    if (badgeKey === "plans"     && compPlansBadge     > 0) return compPlansBadge;
    if (badgeKey === "approvals" && compApprovalsBadge > 0) return compApprovalsBadge;
    return 0;
  }

  // Whether the current route is inside the Compensation Policies workspace
  const isCompActive = location.pathname === "/admin/compensation" ||
                       location.pathname.startsWith("/admin/compensation");

  // Which tab is active — read from ?tab= query param
  const compActiveTab = new URLSearchParams(location.search).get("tab") ?? "plans";

  function handleDragMouseDown(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = navWidth;

    function onMouseMove(e) {
      const delta = e.clientX - startX;
      const next = Math.min(NAV_MAX_WIDTH, Math.max(NAV_MIN_WIDTH, startWidth + delta));
      onNavWidthChange(next);
    }

    function onMouseUp() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  // S657 OI-LEFT-NAV-HIDEABLE: hide nav when toggled off (mobile menu overrides)
  if (!navOpen && !mobileOpen) return null;

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={onClose} />
      )}
      <aside
        style={{ width: navWidth }}
        className={`relative fixed inset-y-0 left-0 z-40 flex flex-col overflow-y-auto border-r border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pt-16 lg:static lg:pt-0 transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 shrink-0`}
      >
        {/* Drag handle */}
        <div
          ref={dragRef}
          onMouseDown={handleDragMouseDown}
          className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-[rgb(var(--color-primary))]/30 active:bg-[rgb(var(--color-primary))]/50 transition-colors z-50"
          title="Drag to resize"
        />
        <nav className="flex flex-1 flex-col justify-between overflow-y-auto p-3">
          <div className="space-y-4">
          {NAV_GROUPS.map((group) => {
            // S669 — Pattern 1 (hidden): Axis 1 tier feature gates via FeatureContext.
            // hasFeature returns true while loading — no flash of missing nav items (fail-open).
            const visibleItems = group.items.filter(({ to }) => {
              if (to === "/admin/finance-view"      && !hasFeature("feature.tier.crm_quota_sync"))      return false;
              if (to === "/admin/trust-center"      && !hasFeature("feature.tier.agent_trust_center"))  return false;
              if (to === "/admin/governance"        && !hasFeature("feature.tier.aop_governance"))      return false;
              if (to === "/admin/draw-plans"        && !hasPlanType("feature.plan.draw"))               return false;
              return true;
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.name}>
                <div className="mb-1 px-2 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                  {group.name}
                </div>
                {visibleItems.map(({ label, icon: Icon, to, children }) => {
                  // S657 OI-STATEMENT-ACKNOWLEDGEMENT: badge on Statements when unacknowledged > 0
                  const badge = to === "/admin/statements" && stmtBadge > 0 ? stmtBadge : 0;
                  return (
                    <div key={to}>
                      <NavLink
                        to={to}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                            isActive
                              ? "bg-[rgb(var(--color-primary))] text-white"
                              : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate flex-1" title={label}>{label}</span>
                        {badge > 0 && (
                          <span className="ml-auto shrink-0 rounded-full bg-[rgb(var(--color-danger))] px-1.5 py-0.5 text-tok-xs font-semibold text-white leading-none">
                            {badge}
                          </span>
                        )}
                      </NavLink>

                      {/* Sub-nav children — shown when parent route is active (§4.2–§4.5) */}
                      {children && isCompActive && (
                        <div className="ml-3 mt-0.5 mb-1 space-y-0.5 border-l border-[rgb(var(--color-border))] pl-2">
                          {children.map(({ label: childLabel, icon: ChildIcon, tab, badge: badgeKey }) => {
                            const childBadge = compChildBadge(badgeKey);
                            const isChildActive = compActiveTab === tab ||
                              (tab === "plans" && !new URLSearchParams(location.search).get("tab"));
                            return (
                              <NavLink
                                key={tab}
                                to={`/admin/compensation?tab=${tab}`}
                                onClick={onClose}
                                className={() =>
                                  `flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-tok-sm font-medium transition ${
                                    isChildActive
                                      ? "bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
                                      : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]"
                                  }`
                                }
                              >
                                <ChildIcon className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate flex-1 text-tok-xs" title={childLabel}>{childLabel}</span>
                                {childBadge > 0 && (
                                  <span className="ml-auto shrink-0 rounded-full bg-[rgb(var(--color-warning))]/15 px-1.5 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-warning))] leading-none">
                                    {childBadge}
                                  </span>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Settings gear — rail footer, role-gated */}
        {canSeeSettings && (
          <div className="border-t border-[rgb(var(--color-border))] pt-3 mt-4">
            <NavLink
              to="/admin/settings"
              onClick={onClose}
              className={({ isActive }) =>
                `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                  isActive
                    ? "bg-[rgb(var(--color-primary))] text-white"
                    : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`
              }
            >
              <Settings className="h-4 w-4 shrink-0" />
              <span className="truncate flex-1">Settings</span>
            </NavLink>
          </div>
        )}
        </nav>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage panels — each owns its data fetch (DD-06)
// ---------------------------------------------------------------------------

function StagePlaceholder({ label, stageId }) {
  return (
    <Card title={label} action={<span className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">Stage {stageId}</span>}>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Live data wiring for this stage is in progress.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// SystemIntelligencePanel — S579
// Per-panel wrapper: no-op render. Content is registered by route in SIShellPanel.
// Kept as a mount point so per-panel JSX is co-located with the panel that owns it.
// ---------------------------------------------------------------------------
function SystemIntelligencePanel({ children }) {
  // Content is rendered in SIShellPanel keyed by route — this is a no-op wrapper.
  return null;
}

// ---------------------------------------------------------------------------
// SI_CONTENT — static content map keyed by route path
// Populated from the original per-panel System Intelligence card text.
// ---------------------------------------------------------------------------
const SI_CONTENT = {
  "/admin/position-detail": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Position edits (role changes, effective dates) are applied via position load. Mid-year role transitions set end_date on existing MBO definitions.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Each import is idempotent — re-uploading the same file is safe. Duplicate rows are skipped via hash dedup.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">employee_id is the identity anchor. role_code and segment_code are resolved to IDs server-side — use human-readable codes, not UUIDs.</p>
    </>
  ),
  "/admin/plan-assignment": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Each position must have an active plan assignment for the current period before calculation can run. plan_code is resolved to plan_id at assignment time (NN-35).</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Mid-year role changes: upload a new CSV with updated plan_code and effective_date. The import service supersedes the old assignment automatically.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">fiscal_period_code and business_unit_code are resolved server-side — use human-readable codes, not IDs.</p>
    </>
  ),
  "/admin/quota-load": (
    <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Quota CSV must include fiscal_period_code, quota_local, and quota_local_currency_code. employee_id resolves to position via C-IDENTITY (enforced server-side). One row per position per BU per period. quota_reporting and quota_reporting_currency_code are server-generated — do not include in CSV.</p>
  ),
  "/admin/credit-config": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Credit CSV must include employee_id (resolves to position_id server-side), source_reference_id, selling_motion (motion_code), business_unit_code, and fiscal_period_code. source_system = direct_api for bootstrap loads.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Credit de-duplication enforced by source_row_hash — re-uploading is safe. overlay_flag defaults to non-overlay when absent.</p>
    </>
  ),
  "/admin/calculation": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">A <strong>Preview</strong> run calculates payouts without locking results. A <strong>Final</strong> run requires all readiness gates to pass and triggers statement generation.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]"><strong>completed_with_exclusions</strong> — a terminal state produced when the run used an approved population snapshot with excluded positions. Excluded positions receive NOT_CALCULATED records. Statements are generated for the calculated population only.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]"><strong>Proceed to Statements with exclusions</strong> requires a different comp admin than the run initiator (NN-36 actor separation). The authorization is recorded in the audit log as <code className="font-mono text-tok-xs">calculation.exclusions.authorized</code>.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]"><strong>Supplemental run</strong> — calculates excluded positions separately. Each is classified as Scenario A (first-time), B (pre-cert amendment), or C (post-cert amendment).</p>
    </>
  ),
  "/admin/statements": (
    <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Statement batches require a completed final calculation run. Release requires a different comp admin than the creator (4-eyes). Released batches are visible to reps and managers.</p>
  ),

  // ── Routes populated for OI-SI-CONTENT-GUIDE (S624) ──────────────────────

  "/admin/organization": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Organization defines the segment and business unit structure that underpins all plan design. Segments and business units are required before plan components can be created — segment_id and business_unit_id are non-nullable on plan_component.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Changes to org structure are additive only during an active period. Removing a segment or BU that is referenced by live plan components requires a period close first.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Common actions: add a new business unit for a product line expansion, update BU weights for quota currency calculations, or review segment assignments before the next plan cycle.</p>
    </>
  ),

  "/admin/commercial-roles": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Commercial Role Library defines the role archetypes available to this tenant. Roles are required before plan components can be built — role_id is non-nullable on plan_component.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Roles sourced from the global reference library (ref_role_library) are platform defaults — their archetype and mandate text cannot be modified, but they can be activated or deactivated per tenant.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Common actions: activate a new role archetype for an expanding team, review quota-bearing flags before plan assignment, or configure selling motion associations.</p>
    </>
  ),

  "/admin/compensation": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Compensation Policies is where plan templates and plan components are designed. A plan component defines the payout mechanics for a given role, segment, and business unit combination.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Plans must be approved by a second comp admin (four-eyes) before they can be assigned to positions. Draft plans are invisible to the calculation engine.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Common actions: design a new ARR plan component for an expanded territory, clone an existing plan for a new segment, or update MBO definitions before period open.</p>
    </>
  ),

  "/admin/fiscal-calendar": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Fiscal Calendar manages fiscal years and their periods. Each period has a status of open, locked, or closed — only open periods accept new data loads and amendments.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Roll Forward advances the active period. Only the chronologically last locked period with no open or active successor can be rolled forward — this prevents gaps in the period chain.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Common actions: create the next fiscal year and generate its monthly periods, or roll forward after a final calculation run is certified.</p>
    </>
  ),

  "/admin/import": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Import Data is the bulk load surface for positions, quotas, credits, and plan assignments. All imports are idempotent — re-uploading the same file is safe due to hash-based deduplication.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Each CSV type has required and optional columns. Human-readable codes (role_code, segment_code, period_code) are resolved to IDs server-side — never send raw UUIDs in import CSVs.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Import errors are returned per-row with the failing constraint. Fix the source data and re-upload — partial rows are never partially applied.</p>
    </>
  ),

  "/admin/readiness": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Readiness checks every position in the active period against a set of eligibility gates: plan assignment exists and is approved, quota loaded, credits loaded, and calculation mode configured.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Positions that fail readiness receive holds. A comp admin can release a hold manually after resolving the blocking dimension, or place an approved hold to exclude a position from the current run.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">All positions must be eligible (or explicitly excluded via approved hold) before a final calculation run can be initiated.</p>
    </>
  ),

  "/admin/payroll-view": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Payroll View provides a read-only summary of payout results from the latest final calculation run, formatted for payroll reconciliation review before export.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Results are broken down by position, plan component, and currency. FX rates applied during the run are shown for audit traceability.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">This view is accessible to the finance role in read-only mode. No edits are possible here — corrections require an amendment or a new calculation run.</p>
    </>
  ),

  "/admin/payroll": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Payroll Export generates the batch file for your payroll system from a completed final calculation run. The export requires a released statement batch as a prerequisite.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Export approval follows four-eyes: the batch must be approved by a different comp admin than the creator before export can be triggered.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Once exported, a reconciliation record is created. Variance flags are raised automatically if the payroll system returns a different accepted amount.</p>
    </>
  ),

  "/admin/certification": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Certify &amp; Close is the final gate in the monthly compensation cycle. It requires dual approval — CA One submits the period for certification (after verifying all prerequisites), and a different CA Two approves and closes it. Self-approval is blocked server-side.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Three prerequisites must be met before submission: a completed final calculation run, a released statement batch, and an approved or exported payroll batch. These are checked on submit and re-verified on approve.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">After Certify &amp; Close, the period status advances to closed. Use Roll Forward on the same screen to activate the next period — plans, assignments, and calculation configs carry forward automatically as drafts.</p>
    </>
  ),

  "/admin/exceptions": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Exceptions adjust compensation for reps affected by ramp periods, leave, territory changes, clawbacks, or retro adjustments. Each exception must be approved before the engine applies it.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">An approved exception is applied by the engine on the next corrective calculation run. <strong>Blocking exceptions</strong> gate the calculation until resolved.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Four-eyes rule: the approver must differ from the submitter. Withdrawal requests also require a second comp admin to approve before the exception is reversed.</p>
    </>
  ),

  "/admin/disputes": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Disputes capture compensation challenges raised against territory credits, system errors, MBO ambiguity, FX rates, eligibility, or other issues. Each dispute follows a tracked lifecycle from submission to close.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Lifecycle: Submitted → Under Review → Escalated → Resolved → Closed. Financial impact is <strong>required</strong> before a dispute can be closed — the engine enforces this server-side.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">SLA breach is flagged automatically by the engine when the resolution target date passes. Escalated disputes are surfaced to senior comp admins for priority handling.</p>
    </>
  ),

  "/admin/change-management": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Change Management governs amendments to quota, credit, rate table, and other entities after a preview calculation has run. Each amendment captures a before/after state and must be approved by a second comp admin before committing.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Amendment lifecycle: Open → Pending Approval → Approved → Committed. Only the initiator can cancel or withdraw. Any comp admin can submit for approval (four-eyes on approval only).</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">After committing, a Reapprove Stage step is required for any pipeline stage impacted by the amendment before the period can advance to certification.</p>
    </>
  ),

  "/admin/governance": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Governance provides audit log access, operation registry status, and system health dashboards for comp admins with oversight responsibilities.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">The audit log records every state-changing operation with actor, timestamp, and payload. Use it to trace who approved a plan, who committed an amendment, or who released a statement batch.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Operation registry entries enforce DR-005 — any unregistered POST/PATCH/DELETE returns 503. New operations must be registered before testing in any environment.</p>
    </>
  ),

  "/admin/trust-center": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Agent Trust Center manages the permissions and scope boundaries for AI-powered Copilot actions. Each agent operation requires an explicit trust grant before it can modify data on behalf of a user.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Trust grants are scoped to operation type and fiscal period — a grant for quota corrections does not extend to credit modifications. Grants expire after 24 hours or on period close, whichever comes first.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">All agent actions are recorded in the audit log under the original user's identity with an agent_assisted flag for traceability.</p>
    </>
  ),

  "/admin/finance-view": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Finance View provides a read-only summary of compensation costs by period, segment, and business unit, intended for FP&A and finance stakeholders who do not need full comp admin access.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Data reflects the latest final calculation run. Accrual forecasts and period-over-period variance analysis are available for the current fiscal year.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Finance users have read-only access to this screen and to Payroll View. Write operations (amendments, exceptions, plan approvals) are restricted to comp_admin role.</p>
    </>
  ),

  "/admin/settings": (
    <>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Settings manages tenant-level configuration: reporting currency, fiscal year start month, subscription tier, and feature flags. Changes here affect all users in the tenant.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Feature flags control access to advanced capabilities such as AI query, supplemental runs, and multi-currency reporting. Flags are set by Anthropic — contact support to change tier entitlements.</p>
      <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">Tenant slug and tenant ID are read-only and used for API authentication. Reporting currency can only be changed before the first final calculation run in a fiscal year.</p>
    </>
  ),
};

// ---------------------------------------------------------------------------
// SIShellPanel — shell-level System Intelligence panel, mirrors CopilotPanel layout.
// open/onClose controlled by shell state (copilotOpen / setCopilotOpen).
// Content keyed by active route — static text, no API calls.
// ---------------------------------------------------------------------------
function SIShellPanel({ open, onClose, activeNavItem }) {
  const location = useLocation();
  const content = SI_CONTENT[location.pathname] ?? null;

  if (!open) return null;

  return (
    <div className="flex h-full w-80 flex-col border-l border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      {/* Header — matches CopilotPanel header exactly */}
      <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3 shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[rgb(var(--color-primary))]" />
          <span className="text-tok-sm font-semibold">System Intelligence</span>
        </div>
        {activeNavItem?.label && (
          <span className="max-w-[120px] truncate text-tok-xs text-[rgb(var(--color-text-muted))]">
            {activeNavItem.label}
          </span>
        )}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {content ?? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No guidance available for this screen.</p>
        )}
      </div>
    </div>
  );
}

// S615 — CommercialRoleLibraryStub removed. Real component imported above.

// ============================================================================
// S579 — OI-UI-TABLE-CONSISTENCY
// Shared sortable/searchable/scrollable table sub-components.
// Pattern: PayoutResultsViewer S578.
// RULE-04 compliant: read-only, no write-path changes.
// ============================================================================

// SortIcon — matches PayoutResultsViewer pattern exactly
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />;
  return sortDir === "asc"
    ? <ChevronUp   className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
    : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />;
}

function thCls() {
  return "px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]";
}
function tdCls() {
  return "px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap";
}

// ── PositionTable ────────────────────────────────────────────────────────────
function PositionTable({ positions, loading, error }) {
  const [search, setSearch]     = useState("");
  const [sortCol, setSortCol]   = useState("full_name");
  const [sortDir, setSortDir]   = useState("asc");
  const [selected, setSelected] = useState(null);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (positions ?? []).filter((p) =>
      !q ||
      p.full_name?.toLowerCase().includes(q) ||
      p.employee_id?.toLowerCase().includes(q) ||
      (p.role_name ?? p.role_code ?? "").toLowerCase().includes(q) ||
      (p.segment_name ?? p.segment_code ?? "").toLowerCase().includes(q)
    );
  }, [positions, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "full_name")   { av = a.full_name ?? ""; bv = b.full_name ?? ""; }
      else if (sortCol === "employee_id") { av = a.employee_id ?? ""; bv = b.employee_id ?? ""; }
      else if (sortCol === "role")   { av = a.role_name ?? a.role_code ?? ""; bv = b.role_name ?? b.role_code ?? ""; }
      else if (sortCol === "segment") { av = a.segment_name ?? a.segment_code ?? ""; bv = b.segment_name ?? b.segment_code ?? ""; }
      else if (sortCol === "hire_date") { av = a.hire_date ?? ""; bv = b.hire_date ?? ""; }
      else if (sortCol === "status") { av = a.is_active ? "Active" : "Inactive"; bv = b.is_active ? "Active" : "Inactive"; }
      else { av = ""; bv = ""; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  return (
    <Card title="Position Detail" action={
      positions?.length > 0 ? <Pill tone="Ready">{positions.length} positions</Pill> : null
    }>
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load positions." />}
      {!loading && !error && (positions ?? []).length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No positions found. Upload a position CSV above to populate.</p>
      )}
      {!loading && !error && (positions ?? []).length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search positions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {positions.length}</span>}
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                <tr>
                  {[["full_name","Name"],["employee_id","Employee ID"],["role","Role"],["segment","Segment"],["hire_date","Hire Date"],["status","Status"]].map(([col, label]) => (
                    <th key={col} className={thCls()} onClick={() => toggleSort(col)}>
                      {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr
                    key={p.position_id}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selected?.position_id === p.position_id ? "bg-[rgb(var(--color-surface-muted))]" : ""}`}
                    onClick={() => setSelected(selected?.position_id === p.position_id ? null : p)}
                  >
                    <td className={tdCls()}>{p.full_name ?? "—"}</td>
                    <td className={`${tdCls()} font-mono text-tok-xs`}>{p.employee_id ?? "—"}</td>
                    <td className={tdCls()}>{p.role_name ?? p.role_code ?? "—"}</td>
                    <td className={tdCls()}>{p.segment_name ?? p.segment_code ?? "—"}</td>
                    <td className={tdCls()}>{p.hire_date ?? "—"}</td>
                    <td className={tdCls()}>
                      <Pill tone={p.is_active ? "Complete" : "Draft"}>{p.is_active ? "Active" : "Inactive"}</Pill>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No positions match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Side panel */}
          {selected && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSelected(null)} />
              <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
                <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
                  <div>
                    <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Position Detail</p>
                    <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">{selected.full_name}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="ml-2 rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {[["Employee ID", selected.employee_id],["Role", selected.role_name ?? selected.role_code],["Segment", selected.segment_name ?? selected.segment_code],["Hire Date", selected.hire_date],["Status", selected.is_active ? "Active" : "Inactive"],["Territory", selected.territory_code],["Country", selected.country_code],["Currency", selected.local_currency_code],["Ramp Start", selected.ramp_start_date],["Email", selected.email]].map(([label, val]) => val != null && (
                    <div key={label}>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                      <p className="text-tok-sm text-[rgb(var(--color-text))]">{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ── AssignmentTable ──────────────────────────────────────────────────────────
function AssignmentTable({ assignments, loading, error, periodId, statusTone }) {
  const [search, setSearch]     = useState("");
  const [sortCol, setSortCol]   = useState("plan_code");
  const [sortDir, setSortDir]   = useState("asc");
  const [selected, setSelected] = useState(null);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (assignments ?? []).filter((a) =>
      !q ||
      (a.plan_code ?? a.plan_name ?? "").toLowerCase().includes(q) ||
      (a.role_code ?? a.role_name ?? "").toLowerCase().includes(q) ||
      (a.assignment_status ?? "").toLowerCase().includes(q)
    );
  }, [assignments, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "plan_code")      { av = a.plan_code ?? a.plan_name ?? ""; bv = b.plan_code ?? b.plan_name ?? ""; }
      else if (sortCol === "role")      { av = a.role_code ?? a.role_name ?? ""; bv = b.role_code ?? b.role_name ?? ""; }
      else if (sortCol === "effective_date") { av = a.effective_date ?? ""; bv = b.effective_date ?? ""; }
      else if (sortCol === "status")    { av = a.assignment_status ?? ""; bv = b.assignment_status ?? ""; }
      else { av = ""; bv = ""; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  return (
    <Card title="Current Assignments" action={assignments?.length > 0 ? <Pill tone="Ready">{assignments.length} assignments</Pill> : null}>
      {!periodId && <ErrorNote message="No open fiscal period — cannot load assignments." />}
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load plan assignments." />}
      {!loading && !error && periodId && (assignments ?? []).length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No plan assignments for this period. Upload a CSV above to assign positions to plans.</p>
      )}
      {!loading && !error && (assignments ?? []).length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search assignments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {assignments.length}</span>}
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                <tr>
                  {[["plan_code","Plan"],["role","Role"],["effective_date","Effective Date"],["status","Status"]].map(([col, label]) => (
                    <th key={col} className={thCls()} onClick={() => toggleSort(col)}>
                      {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => (
                  <tr
                    key={a.plan_assignment_id}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selected?.plan_assignment_id === a.plan_assignment_id ? "bg-[rgb(var(--color-surface-muted))]" : ""}`}
                    onClick={() => setSelected(selected?.plan_assignment_id === a.plan_assignment_id ? null : a)}
                  >
                    <td className={tdCls()}>{a.plan_code ?? a.plan_name ?? "—"}</td>
                    <td className={tdCls()}>{a.role_code ?? a.role_name ?? "—"}</td>
                    <td className={tdCls()}>{a.effective_date ?? "—"}</td>
                    <td className={tdCls()}><Pill tone={statusTone[a.assignment_status] ?? "Draft"}>{a.assignment_status}</Pill></td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No assignments match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {selected && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSelected(null)} />
              <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
                <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
                  <div>
                    <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Assignment Detail</p>
                    <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">{selected.plan_code ?? selected.plan_name}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="ml-2 rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {[["Plan", selected.plan_code ?? selected.plan_name],["Role", selected.role_code ?? selected.role_name],["Effective Date", selected.effective_date],["End Date", selected.end_date],["Status", selected.assignment_status],["Business Unit", selected.business_unit_code],["Period", selected.fiscal_period_code]].map(([label, val]) => val != null && (
                    <div key={label}>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                      <p className="text-tok-sm text-[rgb(var(--color-text))]">{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ── ImportLogTable — shared by Quota and Credit ──────────────────────────────
function ImportLogTable({ title, logs, loading, error, statusTone, emptyText, period }) {
  const [search, setSearch]   = useState("");
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(null);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (logs ?? []).filter((l) =>
      !q ||
      (l.source_filename ?? "").toLowerCase().includes(q) ||
      (l.load_status ?? "").toLowerCase().includes(q)
    );
  }, [logs, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "source_filename") { av = a.source_filename ?? ""; bv = b.source_filename ?? ""; }
      else if (sortCol === "rows_loaded") { av = a.rows_loaded ?? 0; bv = b.rows_loaded ?? 0; }
      else if (sortCol === "created_at")  { av = a.created_at ?? ""; bv = b.created_at ?? ""; }
      else if (sortCol === "load_status") { av = a.load_status ?? ""; bv = b.load_status ?? ""; }
      else { av = ""; bv = ""; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  return (
    <Card title={title}>
      {period && <div className="mb-2 rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]">Active period: <strong>{period.period_label ?? period.period_code}</strong></div>}
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load import history." />}
      {!loading && !error && (logs ?? []).length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">{emptyText}</p>}
      {!loading && !error && (logs ?? []).length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {logs.length}</span>}
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                <tr>
                  {[["source_filename","File"],["rows_loaded","Rows"],["created_at","Loaded"],["load_status","Status"]].map(([col, label]) => (
                    <th key={col} className={thCls()} onClick={() => toggleSort(col)}>
                      {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((log) => (
                  <tr
                    key={log.import_log_id}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selected?.import_log_id === log.import_log_id ? "bg-[rgb(var(--color-surface-muted))]" : ""}`}
                    onClick={() => setSelected(selected?.import_log_id === log.import_log_id ? null : log)}
                  >
                    <td className={`${tdCls()} max-w-[180px] truncate font-mono text-tok-xs`} title={log.source_filename}>{log.source_filename ?? "—"}</td>
                    <td className={tdCls()}>{log.rows_loaded ?? "—"}</td>
                    <td className={tdCls()}>{log.created_at ? new Date(log.created_at).toLocaleDateString() : "—"}</td>
                    <td className={tdCls()}><Pill tone={statusTone[log.load_status] ?? "Draft"}>{log.load_status}</Pill></td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No records match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {selected && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSelected(null)} />
              <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
                <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
                  <div>
                    <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Load Detail</p>
                    <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">{selected.source_filename ?? "—"}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="ml-2 rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {[["File", selected.source_filename],["Rows Loaded", selected.rows_loaded],["Rows Unchanged", selected.rows_unchanged],["Rows Rejected", selected.rows_rejected],["Status", selected.load_status],["Load ID", selected.import_log_id],["Loaded At", selected.created_at ? new Date(selected.created_at).toLocaleString() : null]].map(([label, val]) => val != null && (
                    <div key={label}>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                      <p className="text-tok-sm text-[rgb(var(--color-text))] font-mono break-all">{String(val)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ── CalcRunTable ─────────────────────────────────────────────────────────────
function CalcRunTable({ runs, loading, error, statusTone }) {
  const [search, setSearch]     = useState("");
  const [sortCol, setSortCol]   = useState("run_started_at");
  const [sortDir, setSortDir]   = useState("desc");
  const [selected, setSelected] = useState(null);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (runs ?? []).filter((r) =>
      !q ||
      (r.run_type ?? "").toLowerCase().includes(q) ||
      (r.run_status ?? "").toLowerCase().includes(q) ||
      (r.run_label ?? "").toLowerCase().includes(q)
    );
  }, [runs, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "run_type")         { av = a.run_type ?? ""; bv = b.run_type ?? ""; }
      else if (sortCol === "run_status")  { av = a.run_status ?? ""; bv = b.run_status ?? ""; }
      else if (sortCol === "run_started_at") { av = a.run_started_at ?? ""; bv = b.run_started_at ?? ""; }
      else if (sortCol === "positions_calculated") { av = a.positions_calculated ?? 0; bv = b.positions_calculated ?? 0; }
      else if (sortCol === "run_label")        { av = a.run_label ?? ""; bv = b.run_label ?? ""; }
      else if (sortCol === "fiscal_period_id") { av = a.fiscal_period_id ?? 0; bv = b.fiscal_period_id ?? 0; }
      else { av = ""; bv = ""; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  return (
    <>
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load calculation runs." />}
      {!loading && !error && (runs ?? []).length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No calculation runs yet for this tenant.</p>
      )}
      {!loading && !error && (runs ?? []).length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search runs…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {runs.length}</span>}
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                <tr>
                  {[["run_type","Run Type"],["fiscal_period_id","Period"],["run_label","Label"],["run_status","Status"],["run_started_at","Started"],["positions_calculated","Calculated"]].map(([col, label]) => (
                    <th key={col} className={thCls()} onClick={() => toggleSort(col)}>
                      {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((run) => (
                  <tr
                    key={run.calculation_run_id}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selected?.calculation_run_id === run.calculation_run_id ? "bg-[rgb(var(--color-surface-muted))]" : ""}`}
                    onClick={() => setSelected(selected?.calculation_run_id === run.calculation_run_id ? null : run)}
                  >
                    <td className={tdCls()}>{run.run_type ?? "—"}</td>
                    <td className={tdCls()}>{run.fiscal_period_id ?? "—"}</td>
                    <td className={tdCls()}><span className="text-[rgb(var(--color-text-muted))] italic">{run.run_label || run.display_name || "—"}</span></td>
                    <td className={tdCls()}><Pill tone={statusTone[run.run_status] ?? "Draft"}>{run.run_status?.replace(/_/g, " ")}</Pill></td>
                    <td className={tdCls()}>{run.run_started_at ? new Date(run.run_started_at).toLocaleString() : "—"}</td>
                    <td className={tdCls()}>{run.positions_calculated ?? "—"}</td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No runs match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {selected && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSelected(null)} />
              <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
                <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
                  <div>
                    <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Run Detail</p>
                    <p className="text-tok-base font-semibold text-[rgb(var(--color-text))]">{selected.run_type} — {selected.run_status?.replace(/_/g, " ")}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="ml-2 rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {[["Run ID", selected.calculation_run_id],["Label", selected.run_label || selected.display_name],["Fiscal Period", selected.fiscal_period_id],["Run Type", selected.run_type],["Status", selected.run_status?.replace(/_/g, " ")],["Started", selected.run_started_at ? new Date(selected.run_started_at).toLocaleString() : null],["Completed", selected.run_completed_at ? new Date(selected.run_completed_at).toLocaleString() : null],["Positions Calculated", selected.positions_calculated],["Positions Excluded", selected.positions_excluded],["Scenario Set", selected.scenario_set_id]].map(([label, val]) => val != null && (
                    <div key={label}>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                      <p className="text-tok-sm text-[rgb(var(--color-text))] font-mono break-all">{String(val)}</p>
                    </div>
                  ))}
                  {selected.positions_excluded > 0 && (
                    <p className="text-tok-xs text-[rgb(var(--color-warning))]">{selected.positions_excluded} position{selected.positions_excluded !== 1 ? "s" : ""} excluded from this run</p>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

// ── StatementBatchTable ───────────────────────────────────────────────────────
function StatementBatchTable({ batches, loading, error, statusTone, onSubmit, onRelease }) {
  const [search, setSearch]     = useState("");
  const [sortCol, setSortCol]   = useState("created_at");
  const [sortDir, setSortDir]   = useState("desc");
  const [selected, setSelected] = useState(null);

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (batches ?? []).filter((b) =>
      !q ||
      (b.statement_batch_id ?? "").toLowerCase().includes(q) ||
      (b.batch_status ?? "").toLowerCase().includes(q)
    );
  }, [batches, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortCol === "statement_batch_id") { av = a.statement_batch_id ?? ""; bv = b.statement_batch_id ?? ""; }
      else if (sortCol === "created_at")    { av = a.created_at ?? ""; bv = b.created_at ?? ""; }
      else if (sortCol === "statement_count") { av = a.statement_count ?? 0; bv = b.statement_count ?? 0; }
      else if (sortCol === "batch_status")  { av = a.batch_status ?? ""; bv = b.batch_status ?? ""; }
      else { av = ""; bv = ""; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  return (
    <>
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load statement batches." />}
      {!loading && !error && (batches ?? []).length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No statement batches found. Create a batch after a final calculation run completes.</p>
      )}
      {!loading && !error && (batches ?? []).length > 0 && (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search batches…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {batches.length}</span>}
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
                <tr>
                  {[["statement_batch_id","Batch ID"],["created_at","Created"],["statement_count","Count"],["batch_status","Status"]].map(([col, label]) => (
                    <th key={col} className={thCls()} onClick={() => toggleSort(col)}>
                      {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((batch) => (
                  <tr
                    key={batch.statement_batch_id}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selected?.statement_batch_id === batch.statement_batch_id ? "bg-[rgb(var(--color-surface-muted))]" : ""}`}
                    onClick={() => setSelected(selected?.statement_batch_id === batch.statement_batch_id ? null : batch)}
                  >
                    <td className={`${tdCls()} font-mono text-tok-xs`}>{String(batch.statement_batch_id).slice(0, 8)}…</td>
                    <td className={tdCls()}>{batch.created_at ? new Date(batch.created_at).toLocaleDateString() : "—"}</td>
                    <td className={tdCls()}>{batch.statement_count ?? "—"}</td>
                    <td className={tdCls()}><Pill tone={statusTone[batch.batch_status] ?? "Draft"}>{batch.batch_status?.replace(/_/g, " ")}</Pill></td>
                    <td className={`${tdCls()}`} onClick={(e) => e.stopPropagation()}>
                      {batch.batch_status === "draft" && (
                        <Button onClick={() => onSubmit(batch.statement_batch_id)}>Submit</Button>
                      )}
                      {batch.batch_status === "pending_approval" && (
                        <Button onClick={() => onRelease(batch.statement_batch_id)}>Release</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No batches match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {selected && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setSelected(null)} />
              <div className="fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
                <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
                  <div>
                    <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Batch Detail</p>
                    <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] font-mono">{String(selected.statement_batch_id).slice(0, 8)}…</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="ml-2 rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {[["Batch ID", selected.statement_batch_id],["Status", selected.batch_status?.replace(/_/g, " ")],["Created", selected.created_at ? new Date(selected.created_at).toLocaleString() : null],["Statement Count", selected.statement_count],["Fiscal Period", selected.fiscal_period_id]].map(([label, val]) => val != null && (
                    <div key={label}>
                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                      <p className="text-tok-sm text-[rgb(var(--color-text))] font-mono break-all">{String(val)}</p>
                    </div>
                  ))}
                  <div className="pt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {selected.batch_status === "draft" && <Button onClick={() => { onSubmit(selected.statement_batch_id); setSelected(null); }}>Submit</Button>}
                    {selected.batch_status === "pending_approval" && <Button onClick={() => { onRelease(selected.statement_batch_id); setSelected(null); }}>Release</Button>}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

// ============================================================================
// S402 — Surface 2 sub-components (Stage 15 Readiness)
// R-UI-01: position scope section + staleness warning + re-assess
// R-UI-02: hold management inline (place / approve / release)
// R-UI-03: population snapshot creation + approval
// ============================================================================

// Reason code → human label (derived from confirmed live chk_phr_reason_code values)
// Reason code → human label — complete set from live chk_phr_reason_code constraint.
// Verified by: SELECT pg_get_constraintdef(oid) FROM pg_constraint WHERE conname = 'chk_phr_reason_code'
// 12 values confirmed (ddl_summary truncated at 8 — S402 Step 8 audit).
const HOLD_REASON_LABELS = {
  PLAN_ASSIGNMENT_MISSING:      "Plan assignment missing",
  PLAN_ASSIGNMENT_NOT_APPROVED: "Plan assignment not approved",
  QUOTA_MISSING:                "Quota missing",
  CREDIT_NOT_LOADED:            "Credit not loaded",
  CALC_MODE_NOT_CONFIGURED:     "Calc mode not configured",
  TRANSACTION_DATA_MISSING:     "Transaction data missing",
  DRAW_PLAN_NOT_APPROVED:       "Draw plan not approved",
  SPLIT_PLAN_NOT_APPROVED:      "Split plan not approved",
  ACKNOWLEDGMENT_MISSING:       "Acknowledgment missing",
  MODIFIER_EXCEPTION_PENDING:   "Modifier exception pending",
  AMENDMENT_REAPPROVAL_PENDING: "Amendment reapproval pending",
  INTEGRATION_EVENT_PENDING:    "Integration event pending",
};

// All 12 confirmed live reason codes — complete, no truncation.
const HOLD_REASON_CODES = Object.keys(HOLD_REASON_LABELS);

function holdReasonLabel(code) {
  return HOLD_REASON_LABELS[code] ?? code?.replace(/_/g, " ").toLowerCase() ?? "—";
}

function eligibilityTone(status) {
  return {
    eligible:      "Complete",
    ineligible:    "Blocked",
    hold_pending:  "Warning",
    hold_approved: "Warning",
    excluded:      "Blocked",
  }[status] ?? "Draft";
}

// Staleness threshold default — reads from parameter_config but uses 120-min default
const DEFAULT_STALENESS_MINUTES = 120;

// ── PositionScopeSection ──────────────────────────────────────────────────
// Shows per-position readiness list, staleness warning, hold management inline
// Does NOT re-trigger assessment — reads persisted position_period_readiness

function PositionScopeSection({ periodId, userId, onHoldsChanged }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedPositionId, setExpandedPositionId] = useState(null);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("full_name");
  const [sortDir, setSortDir] = useState("asc");

  // Persisted readiness list (does NOT re-run assessment)
  const { data: readinessData, loading: readinessLoading, error: readinessError } = useFetch(
    periodId ? `${API_BASE}/readiness/positions?fiscal_period_id=${periodId}&limit=200&_k=${refreshKey}` : null
  );
  const positions       = readinessData?.positions ?? [];
  const totalEligible   = readinessData?.total_eligible   ?? 0;
  const totalIneligible = readinessData?.total_ineligible ?? 0;
  const totalHoldPending  = readinessData?.total_hold_pending  ?? 0;
  const totalHoldApproved = readinessData?.total_hold_approved ?? 0;

  // Staleness: find oldest assessed_at across positions
  const oldestAssessedAt = positions.reduce((oldest, p) => {
    if (!p.assessed_at) return oldest;
    return !oldest || p.assessed_at < oldest ? p.assessed_at : oldest;
  }, null);
  const assessedAgeMinutes = oldestAssessedAt
    ? Math.floor((Date.now() - new Date(oldestAssessedAt).getTime()) / 60000)
    : null;
  const isStale = assessedAgeMinutes !== null && assessedAgeMinutes > DEFAULT_STALENESS_MINUTES;

  // Hold list for this period
  const { data: holdsData, loading: holdsLoading } = useFetch(
    periodId ? `${API_BASE}/position-holds?fiscal_period_id=${periodId}&_k=${refreshKey}` : null
  );
  const holds = holdsData?.data ?? holdsData?.position_holds ?? [];

  // Hold action state
  const [holdModal, setHoldModal]       = useState(null); // 'place' | 'release'
  const [holdTargetId, setHoldTargetId] = useState(null); // position_id for placing
  const [holdTargetHoldId, setHoldTargetHoldId] = useState(null); // hold_id for approve/release
  const [holdReasonCode, setHoldReasonCode]   = useState(HOLD_REASON_CODES[0]);
  const [holdJustification, setHoldJustification] = useState("");
  const [holdReleaseReason, setHoldReleaseReason] = useState("");
  const [holdSubmitting, setHoldSubmitting]   = useState(false);
  const [holdError, setHoldError]             = useState(null);

  function holdForPosition(positionId) {
    return holds.find((h) =>
      h.position_id === positionId &&
      ["pending", "approved"].includes(h.hold_status)
    ) ?? null;
  }

  function refresh() {
    setRefreshKey((k) => k + 1);
    if (onHoldsChanged) onHoldsChanged();
  }

  async function handlePlaceHold() {
    if (!holdTargetId || !holdReasonCode || !holdJustification.trim()) return;
    setHoldSubmitting(true);
    setHoldError(null);
    try {
      const res = await fetch(`${API_BASE}/position-holds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          position_id:           holdTargetId,
          fiscal_period_id:      periodId,
          reason_code:           holdReasonCode,
          business_justification: holdJustification.trim(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      setHoldModal(null);
      setHoldJustification("");
      refresh();
    } catch (e) {
      setHoldError(e.message);
    } finally {
      setHoldSubmitting(false);
    }
  }

  async function handleApproveHold(holdId) {
    setHoldSubmitting(true);
    setHoldError(null);
    try {
      const res = await fetch(`${API_BASE}/position-holds/${holdId}/approve`, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      refresh();
    } catch (e) {
      setHoldError(e.message);
    } finally {
      setHoldSubmitting(false);
    }
  }

  async function handleReleaseHold() {
    if (!holdTargetHoldId) return;
    setHoldSubmitting(true);
    setHoldError(null);
    try {
      const res = await fetch(`${API_BASE}/position-holds/${holdTargetHoldId}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ release_reason: holdReleaseReason.trim() || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      setHoldModal(null);
      setHoldReleaseReason("");
      setHoldTargetHoldId(null);
      refresh();
    } catch (e) {
      setHoldError(e.message);
    } finally {
      setHoldSubmitting(false);
    }
  }

  // Sort + filter derived list
  function sortValue(pos, col) {
    if (col === "full_name")         return (pos.full_name ?? "").toLowerCase();
    if (col === "role_code")         return (pos.role_code ?? "").toLowerCase();
    if (col === "segment_code")      return (pos.segment_code ?? "").toLowerCase();
    if (col === "eligibility_status") return pos.eligibility_status ?? "";
    if (col === "assessed_at")       return pos.assessed_at ?? "";
    return "";
  }

  const filteredPositions = positions
    .filter((pos) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (pos.full_name ?? "").toLowerCase().includes(q) ||
        (pos.role_code ?? "").toLowerCase().includes(q) ||
        (pos.segment_code ?? "").toLowerCase().includes(q) ||
        (pos.eligibility_status ?? "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const av = sortValue(a, sortCol);
      const bv = sortValue(b, sortCol);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });

  function handleSort(col) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  function SortIcon({ col }) {
    if (sortCol !== col) return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
    return sortDir === "asc"
      ? <ChevronUp className="ml-1 inline h-3 w-3 text-[rgb(var(--color-accent))]" />
      : <ChevronDown className="ml-1 inline h-3 w-3 text-[rgb(var(--color-accent))]" />;
  }

  return (
    <>
      {/* Staleness warning */}
      {isStale && (
        <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <div className="flex-1">
            <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
              Assessment is {assessedAgeMinutes} minutes old — data may have changed
            </p>
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Re-assess before creating a snapshot. The snapshot can still be created from stale results,
              but this will be flagged in the audit record.
            </p>
          </div>
        </div>
      )}

      {/* Summary counts */}
      {(totalEligible > 0 || totalIneligible > 0 || totalHoldPending > 0 || totalHoldApproved > 0) && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { label: "Eligible",       value: totalEligible,     tone: "text-[rgb(var(--color-success))]"     },
            { label: "Ineligible",     value: totalIneligible,   tone: "text-[rgb(var(--color-danger))]"      },
            { label: "Hold pending",   value: totalHoldPending,  tone: "text-[rgb(var(--color-warning))]"     },
            { label: "Hold approved",  value: totalHoldApproved, tone: "text-[rgb(var(--color-text-muted))]"  },
          ].map(({ label, value, tone }) => (
            <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
              <p className={`text-tok-base font-bold ${tone}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Assessed-at timestamp */}
      {oldestAssessedAt && (
        <p className={`text-tok-xs ${isStale ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text-muted))]"}`}>
          {isStale ? <Clock className="mb-0.5 mr-1 inline h-3 w-3" /> : null}
          Last assessed {new Date(oldestAssessedAt).toLocaleString()}
          {assessedAgeMinutes !== null && ` (${assessedAgeMinutes} min ago)`}
        </p>
      )}

      {readinessLoading && <LoadingRow />}
      {readinessError && <ErrorNote message="Unable to load position readiness data." />}
      {!readinessLoading && !readinessError && positions.length === 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-5 text-center">
          <ListChecks className="mx-auto mb-2 h-6 w-6 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">No position assessment on record</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Run "Assess Positions" from the Positions screen (Data Setup › Positions) first.
          </p>
        </div>
      )}

      {/* Hold error (global, not per-row) */}
      {holdError && <ErrorNote message={holdError} />}

      {/* Search bar */}
      {positions.length > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, role, segment, or status…"
            className="flex-1 bg-transparent text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none"
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Position table */}
      {positions.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-[rgb(var(--color-border))]">
          <div className="overflow-x-auto">
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full border-collapse text-tok-sm">
                <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface-muted))]">
                  <tr>
                    <th className="w-6 px-3 py-2.5" />
                    {[
                      { col: "full_name",          label: "Name" },
                      { col: "role_code",           label: "Role" },
                      { col: "segment_code",        label: "Segment" },
                      { col: "eligibility_status",  label: "Status" },
                      { col: "assessed_at",         label: "Assessed" },
                    ].map(({ col, label }) => (
                      <th
                        key={col}
                        onClick={() => handleSort(col)}
                        className="cursor-pointer select-none px-3 py-2.5 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] whitespace-nowrap"
                      >
                        {label}<SortIcon col={col} />
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
                  {filteredPositions.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                        No positions match "{search}"
                      </td>
                    </tr>
                  )}
                  {filteredPositions.map((pos) => {
                    const hold            = holdForPosition(pos.position_id);
                    const isExpanded      = expandedPositionId === pos.position_id;
                    const reasons         = Array.isArray(pos.incompleteness_reasons) ? pos.incompleteness_reasons : [];
                    const isCreatorOfHold = hold && String(hold.created_by) === String(userId);

                    return (
                      <>
                        <tr
                          key={pos.position_id}
                          className={`transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${isExpanded ? "bg-[rgb(var(--color-surface-muted))]" : ""}`}
                        >
                          {/* Expand toggle */}
                          <td className="px-3 py-2.5">
                            <button
                              onClick={() => setExpandedPositionId(isExpanded ? null : pos.position_id)}
                              className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                              title={isExpanded ? "Collapse" : "Expand details"}
                            >
                              {isExpanded
                                ? <ChevronUp className="h-3.5 w-3.5" />
                                : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                          {/* Name */}
                          <td className="px-3 py-2.5 font-medium text-[rgb(var(--color-text))] whitespace-nowrap">
                            {pos.full_name ?? pos.position_id}
                          </td>
                          {/* Role */}
                          <td className="px-3 py-2.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                            {pos.role_code ?? "—"}
                          </td>
                          {/* Segment */}
                          <td className="px-3 py-2.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                            {pos.segment_code ?? "—"}
                          </td>
                          {/* Status */}
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <Pill tone={eligibilityTone(pos.eligibility_status)}>
                              {pos.eligibility_status?.replace(/_/g, " ")}
                            </Pill>
                          </td>
                          {/* Assessed at */}
                          <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                            {pos.assessed_at ? new Date(pos.assessed_at).toLocaleString() : "—"}
                          </td>
                          {/* Hold actions — NN-36 */}
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {pos.eligibility_status !== "eligible" && !hold && (
                                <button
                                  onClick={() => { setHoldTargetId(pos.position_id); setHoldReasonCode(HOLD_REASON_CODES[0]); setHoldJustification(""); setHoldError(null); setHoldModal("place"); }}
                                  className="flex items-center gap-1 rounded-lg border border-[rgb(var(--color-border))] px-2 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                                  title="Place hold on this position"
                                >
                                  <Lock className="h-3 w-3" /> Hold
                                </button>
                              )}
                              {hold?.hold_status === "pending" && !isCreatorOfHold && (
                                <button
                                  onClick={() => { setHoldError(null); handleApproveHold(hold.hold_id ?? hold.position_hold_id); }}
                                  disabled={holdSubmitting}
                                  className="flex items-center gap-1 rounded-lg border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/5 px-2 py-1 text-tok-xs font-medium text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/10 disabled:opacity-50"
                                  title="Approve this hold (NN-36)"
                                >
                                  <ThumbsUp className="h-3 w-3" /> Approve
                                </button>
                              )}
                              {hold?.hold_status === "pending" && isCreatorOfHold && (
                                <span className="rounded-lg border border-[rgb(var(--color-border))] px-2 py-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                                  Awaiting approval
                                </span>
                              )}
                              {hold?.hold_status === "approved" && (
                                <button
                                  onClick={() => { setHoldTargetHoldId(hold.hold_id ?? hold.position_hold_id); setHoldReleaseReason(""); setHoldError(null); setHoldModal("release"); }}
                                  className="flex items-center gap-1 rounded-lg border border-[rgb(var(--color-border))] px-2 py-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                                  title="Release this hold"
                                >
                                  <LockOpen className="h-3 w-3" /> Release
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded detail row */}
                        {isExpanded && (
                          <tr key={`${pos.position_id}-detail`} className="bg-[rgb(var(--color-surface-muted))]">
                            <td colSpan={7} className="px-6 py-3">
                              <div className="space-y-2">
                                {reasons.length === 0 && (
                                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No incompleteness dimensions flagged.</p>
                                )}
                                {reasons.map((r, i) => (
                                  <div key={i} className="flex items-start gap-2 rounded-lg bg-[rgb(var(--color-surface))] px-3 py-2">
                                    <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
                                    <div className="min-w-0">
                                      <p className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
                                        {holdReasonLabel(r.code)}
                                        <span className="ml-1.5 font-mono text-[rgb(var(--color-text-muted))]">{r.gate}</span>
                                      </p>
                                      {r.detail && (
                                        <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">{r.detail}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {hold && (
                                  <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs">
                                    <p className="font-medium text-[rgb(var(--color-text))]">
                                      Hold: <span className="font-normal">{hold.hold_status?.replace(/_/g, " ")}</span>
                                      {hold.hold_source === "system" && (
                                        <span className="ml-1.5 text-[rgb(var(--color-text-muted))]">(system-created)</span>
                                      )}
                                    </p>
                                    {hold.business_justification && (
                                      <p className="mt-0.5 text-[rgb(var(--color-text-muted))]">{hold.business_justification}</p>
                                    )}
                                    {hold.hold_status === "approved" && (
                                      <p className="mt-1.5 text-[rgb(var(--color-warning))]">
                                        <Info className="mb-0.5 mr-1 inline h-3 w-3" />
                                        Fixing the underlying data issue does <strong>not</strong> automatically release this hold.
                                        A comp admin must explicitly release it once the data is corrected.
                                      </p>
                                    )}
                                  </div>
                                )}
                                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                                  v{pos.assessment_version} · assessed {pos.assessed_at ? new Date(pos.assessed_at).toLocaleString() : "—"}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* Table footer: count */}
          <div className="border-t border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {search
                ? `${filteredPositions.length} of ${positions.length} positions`
                : `${positions.length} positions`}
            </p>
          </div>
        </div>
      )}

      {/* Place Hold modal */}
      {holdModal === "place" && (
        <InlineModal title="Place Position Hold" onClose={() => { setHoldModal(null); setHoldError(null); }}>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            A second comp admin must approve this hold before the position can be excluded
            from a population snapshot (NN-36).
          </p>
          <FormField label="Reason Code" required>
            <SelectInput
              value={holdReasonCode}
              onChange={setHoldReasonCode}
              options={HOLD_REASON_CODES.map((c) => ({ value: c, label: holdReasonLabel(c) }))}
            />
          </FormField>
          <FormField label="Business Justification" required
            hint="Explain why this position is being held. This text appears in the audit record.">
            <textarea
              value={holdJustification}
              onChange={(e) => setHoldJustification(e.target.value)}
              rows={3}
              placeholder="e.g. Sales rep transferred mid-quarter — plan assignment update pending HR approval."
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none resize-none"
            />
          </FormField>
          {holdError && <ErrorNote message={holdError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handlePlaceHold} disabled={holdSubmitting || !holdJustification.trim()}>
              {holdSubmitting ? "Placing hold…" : "Place Hold"}
            </Button>
            <Button variant="secondary" onClick={() => { setHoldModal(null); setHoldError(null); }}>Cancel</Button>
          </div>
        </InlineModal>
      )}

      {/* Release Hold modal */}
      {holdModal === "release" && (
        <InlineModal title="Release Position Hold" onClose={() => { setHoldModal(null); setHoldError(null); }}>
          <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
            <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
              Releasing this hold does not fix the underlying data issue.
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Only release a hold once you have confirmed the data issue has been corrected.
              The position will be re-evaluated on the next assessment.
            </p>
          </div>
          <FormField label="Release Reason" hint="Optional — explain why the hold is being released.">
            <TextInput
              value={holdReleaseReason}
              onChange={setHoldReleaseReason}
              placeholder="e.g. Plan assignment now approved in HR system."
            />
          </FormField>
          {holdError && <ErrorNote message={holdError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleReleaseHold} disabled={holdSubmitting}>
              {holdSubmitting ? "Releasing…" : "Release Hold"}
            </Button>
            <Button variant="secondary" onClick={() => { setHoldModal(null); setHoldError(null); }}>Cancel</Button>
          </div>
        </InlineModal>
      )}
    </>
  );
}

// ── PopulationSnapshotSection ─────────────────────────────────────────────
// R-UI-03: snapshot creation, submit, approve workflow
// NN-36: approve ≠ creator AND ≠ submitter

function PopulationSnapshotSection({ periodId, userId, onSnapshotApproved }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: snapshotsData, loading: snapshotsLoading } = useFetch(
    periodId ? `${API_BASE}/population-snapshots?fiscal_period_id=${periodId}&_k=${refreshKey}` : null
  );
  const snapshots = snapshotsData?.data ?? snapshotsData?.snapshots ?? [];
  const activeSnapshot = snapshots.find(
    (s) => ["draft", "pending_approval", "approved"].includes(s.snapshot_status)
  ) ?? null;

  const [creating, setCreating]         = useState(false);
  const [createError, setCreateError]   = useState(null);
  const [submitError, setSubmitError]   = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [actioning, setActioning]       = useState(false);
  const [createResult, setCreateResult] = useState(null); // response from POST

  function refresh() {
    setRefreshKey((k) => k + 1);
  }

  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      const res = await fetch(`${API_BASE}/population-snapshots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fiscal_period_id: periodId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      setCreateResult(body);
      refresh();
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleSubmit(snapshotId) {
    setActioning(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/population-snapshots/${snapshotId}/submit`, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      refresh();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setActioning(false);
    }
  }

  async function handleApprove(snapshotId) {
    setActioning(true);
    setApproveError(null);
    try {
      const res = await fetch(`${API_BASE}/population-snapshots/${snapshotId}/approve`, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      refresh();
      if (onSnapshotApproved) onSnapshotApproved(snapshotId);
    } catch (e) {
      setApproveError(e.message);
    } finally {
      setActioning(false);
    }
  }

  const statusTone = {
    draft:            "Draft",
    pending_approval: "Warning",
    approved:         "Complete",
    superseded:       "Draft",
  };

  // NN-36: approver must not be creator or submitter
  function canApprove(snapshot) {
    const uid = String(userId ?? "");
    if (!uid) return true; // can't check without user ID — let server enforce
    const isCreator   = String(snapshot.created_by ?? "")   === uid;
    const isSubmitter = String(snapshot.submitted_for_approval_by ?? "") === uid;
    return !isCreator && !isSubmitter;
  }

  return (
    <div className="space-y-3">
      {snapshotsLoading && <LoadingRow />}

      {/* Stale warning from create result */}
      {createResult?.assessment_is_stale && (
        <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Snapshot created from a stale assessment ({createResult.assessment_age_minutes} min old).
            This is recorded in the audit trail.
          </p>
        </div>
      )}

      {/* No active snapshot */}
      {!snapshotsLoading && !activeSnapshot && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No population snapshot for this period</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Creating a snapshot derives the included and excluded position sets from current hold state.
            Positions with approved holds are excluded. System holds are auto-created for any ineligible
            position with no comp admin hold.
          </p>
          <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Requires minimum 2 active comp admins (governance requirement).
          </p>
        </div>
      )}

      {createError && <ErrorNote message={createError} />}
      {submitError && <ErrorNote message={submitError} />}
      {approveError && <ErrorNote message={approveError} />}

      {/* Active snapshot */}
      {activeSnapshot && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
              <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">Population Snapshot</span>
            </div>
            <Pill tone={statusTone[activeSnapshot.snapshot_status] ?? "Draft"}>
              {activeSnapshot.snapshot_status?.replace(/_/g, " ")}
            </Pill>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Field label="Included"  value={activeSnapshot.positions_included ?? "—"} />
            <Field label="Excluded"  value={activeSnapshot.positions_excluded ?? "—"} />
            <Field label="Hash"      value={activeSnapshot.population_hash ? `${activeSnapshot.population_hash.slice(0, 12)}…` : "—"} />
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Created {activeSnapshot.created_at ? new Date(activeSnapshot.created_at).toLocaleString() : "—"}
          </p>

          {/* Workflow actions */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {activeSnapshot.snapshot_status === "draft" && (
              <Button onClick={() => handleSubmit(activeSnapshot.snapshot_id)} disabled={actioning}>
                <Send className="mr-1.5 h-3.5 w-3.5" />
                {actioning ? "Submitting…" : "Submit for approval"}
              </Button>
            )}
            {activeSnapshot.snapshot_status === "pending_approval" && canApprove(activeSnapshot) && (
              <Button onClick={() => handleApprove(activeSnapshot.snapshot_id)} disabled={actioning}>
                <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
                {actioning ? "Approving…" : "Approve snapshot"}
              </Button>
            )}
            {activeSnapshot.snapshot_status === "pending_approval" && !canApprove(activeSnapshot) && (
              <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5">
                <Info className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Awaiting approval from a different comp admin (NN-36)
                </span>
              </div>
            )}
            {activeSnapshot.snapshot_status === "approved" && (
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))]" />
                <span className="text-tok-sm text-[rgb(var(--color-success))]">
                  Snapshot approved — engine will use this population for the next run
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create snapshot button */}
      {!activeSnapshot && (
        <Button onClick={handleCreate} disabled={creating || !periodId}>
          <Camera className="mr-1.5 h-4 w-4" />
          {creating ? "Creating snapshot…" : "Create population snapshot"}
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// AcknowledgmentCoverageCard — S616 OI-ACKNOWLEDGMENT-GOVERNANCE-INTEGRATION
// Fetches GET /api/v1/fiscal-periods/{id}/acknowledgment-coverage
// Renders green/amber/red indicator + rep count + coverage %.
// Mounted inside ReadinessPanel — receives periodId prop.
function AcknowledgmentCoverageCard({ periodId }) {
  const { data, loading, error } = useFetch(
    periodId
      ? `${API_BASE}/fiscal-periods/${periodId}/acknowledgment-coverage`
      : null,
  );

  const indicator      = data?.indicator      ?? null;
  const acknowledged   = data?.acknowledged   ?? null;
  const totalAssigned  = data?.total_assigned ?? null;
  const coveragePct    = data?.coverage_pct   ?? null;
  const thresholdGreen = data?.threshold_green ?? 85;
  const thresholdAmber = data?.threshold_amber ?? 50;

  const indicatorColor =
    indicator === "green"
      ? "text-[rgb(var(--color-success))] bg-[rgb(var(--color-success))]/10 border-[rgb(var(--color-success))]/30"
      : indicator === "amber"
      ? "text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/10 border-[rgb(var(--color-warning))]/30"
      : indicator === "red"
      ? "text-[rgb(var(--color-danger))] bg-[rgb(var(--color-danger))]/10 border-[rgb(var(--color-danger))]/30"
      : "text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-[rgb(var(--color-border))]";

  const dotColor =
    indicator === "green"
      ? "bg-[rgb(var(--color-success))]"
      : indicator === "amber"
      ? "bg-[rgb(var(--color-warning))]"
      : indicator === "red"
      ? "bg-[rgb(var(--color-danger))]"
      : "bg-[rgb(var(--color-text-muted))]";

  const indicatorLabel =
    indicator === "green" ? "On track"
    : indicator === "amber" ? "Needs attention"
    : indicator === "red"   ? "At risk"
    : "—";

  if (!periodId) return null;

  return (
    <Card title="Plan Acknowledgment Coverage">
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load acknowledgment coverage." />}
      {!loading && !error && data && (
        <div className="flex flex-wrap items-center gap-4">
          <div className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-tok-sm font-semibold ${indicatorColor}`}>
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotColor}`} />
            {indicatorLabel}
          </div>
          <div className="text-tok-sm text-[rgb(var(--color-text))]">
            <span className="font-semibold">{acknowledged ?? "—"}</span>
            {" of "}
            <span className="font-semibold">{totalAssigned ?? "—"}</span>
            {" reps acknowledged"}
          </div>
          {coveragePct !== null && (
            <div className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {coveragePct}%
            </div>
          )}
          <div className="ml-auto flex items-center gap-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-[rgb(var(--color-success))]" />
              ≥{thresholdGreen}%
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-[rgb(var(--color-warning))]" />
              ≥{thresholdAmber}%
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-[rgb(var(--color-danger))]" />
              &lt;{thresholdAmber}%
            </span>
          </div>
        </div>
      )}
      {!loading && !error && !data && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          No acknowledgment data for this period.
        </p>
      )}
    </Card>
  );
}

// ReadinessPanel — S402 Surface 2 (Stage 15, /admin/readiness)
// Reads persisted position_period_readiness — does NOT re-run assessment
// Shows: period-level gate summary (existing) + position scope + hold mgmt +
//        snapshot workflow + staleness warning + snapshot-aware Run Calculation
// ============================================================================

function ReadinessPanel({ mode, period }) {
  const { user }  = useAuth();
  const periodId  = period?.fiscal_period_id;
  const userId    = user?.user_id ?? null;

  // Existing period-level gate data
  const [fetchKey, setFetchKey] = useState(0);
  const { data, loading, error } = useFetch(
    periodId ? `${API_BASE}/readiness-status?fiscal_period_id=${periodId}&_k=${fetchKey}` : null
  );
  const gates         = data?.gates ?? null;
  const domains       = Array.isArray(data?.domains) ? data.domains : [];
  const entityCount   = data?.entity_count ?? null;
  const overallStatus = data?.data_readiness_status ?? null;
  const blockingCount = data?.blocking_issue_count ?? 0;
  const previewEligible = gates?.preview_eligible === true;

  // S402 snapshot state (for snapshot-aware Run Calculation button)
  const [snapshotRefreshKey, setSnapshotRefreshKey]   = useState(0);
  const [approvedSnapshotId, setApprovedSnapshotId]   = useState(null);
  const { data: snapshotsData } = useFetch(
    periodId ? `${API_BASE}/population-snapshots?fiscal_period_id=${periodId}&_k=${snapshotRefreshKey}` : null
  );
  const activeApprovedSnapshot = (snapshotsData?.data ?? snapshotsData?.snapshots ?? []).find(
    (s) => s.snapshot_status === "approved"
  ) ?? null;
  const resolvedSnapshotId = approvedSnapshotId ?? activeApprovedSnapshot?.snapshot_id ?? null;

  // Re-assess positions (elevated when stale)
  const [reassessing, setReassessing] = useState(false);
  const [reassessError, setReassessError] = useState(null);
  const [reassessResult, setReassessResult] = useState(null);

  async function handleReassess() {
    if (!periodId) return;
    setReassessing(true);
    setReassessError(null);
    setReassessResult(null);
    try {
      const res = await fetch(`${API_BASE}/readiness/positions/assess`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fiscal_period_id: periodId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      setReassessResult(body);
      setFetchKey((k) => k + 1);
    } catch (e) {
      setReassessError(e.message);
    } finally {
      setReassessing(false);
    }
  }

  // Run Calculation (snapshot-aware)
  const [selectedScenarioSetId, setSelectedScenarioSetId] = useState("");
  const [runType, setRunType]         = useState("preview");
  const [runLabel, setRunLabel]       = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [runError, setRunError]       = useState(null);
  const [pollingRunId, setPollingRunId]   = useState(null);   // S580: in-flight run polling
  const [pollingStatus, setPollingStatus] = useState(null);   // run_status of in-flight run
  const { data: scenarioData } = useFetch(periodId ? `${API_BASE}/scenario-sets?fiscal_period_id=${periodId}` : null);
  const scenarioSets = scenarioData?.scenario_sets ?? [];

  useEffect(() => {
    if (scenarioSets.length > 0 && !selectedScenarioSetId) {
      const active = scenarioSets.find((s) => s.is_active) ?? scenarioSets[0];
      setSelectedScenarioSetId(String(active.scenario_set_id));
    }
  }, [scenarioSets]);

  // S580: poll in-flight run until terminal status
  useEffect(() => {
    if (!pollingRunId) return;
    const TERMINAL = new Set(["completed", "completed_with_exclusions", "failed"]);
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/calculation-runs/${pollingRunId}`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        const status = d?.run_status ?? d?.calculation_run?.run_status;
        if (!status) return;
        setPollingStatus(status);
        if (TERMINAL.has(status)) {
          clearInterval(interval);
          setPollingRunId(null);
          if (status === "failed") {
            setRunError(`Run ${pollingRunId} failed.`);
          } else {
            window.location.href = "/admin/calculation";
          }
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [pollingRunId]);

  async function handleRunCalculation() {
    if (!periodId) return;  // S572: scenario_set_id optional
    setSubmitting(true);
    setRunError(null);
    try {
      const trimmedLabel = runLabel.trim();
      const body = {
        fiscal_period_id: periodId,
        run_type:         runType,
        // S572: scenario_set_id optional
        ...(selectedScenarioSetId ? { scenario_set_id: selectedScenarioSetId } : {}),
        // S402 snapshot path
        ...(resolvedSnapshotId ? { population_snapshot_id: resolvedSnapshotId } : {}),
        // S573: Final run requires explicit confirmation flag
        ...(runType === "final" ? { confirm_final: true } : {}),
        // S587: optional user-supplied run label
        ...(trimmedLabel ? { run_label: trimmedLabel } : {}),
      };
      const res = await fetch(`${API_BASE}/calculation-runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const b = await res.json().catch(() => ({}));
      if (res.status === 409 && b?.error?.code === "RUN_ALREADY_RUNNING") {
        // S580: in-flight run detected — start polling instead of showing error
        const inFlightId = b?.error?.detail?.calculation_run_id;
        if (inFlightId) {
          setPollingRunId(inFlightId);
          setPollingStatus("running");
        } else {
          setRunError("A run is already in progress for this period.");
        }
      } else if (!res.ok) {
        setRunError(b?.error?.message ?? `Error ${res.status}`);
      } else {
        window.location.href = "/admin/calculation";
      }
    } catch (e) {
      setRunError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const recheck = () => setFetchKey((k) => k + 1);

  return (
    <div className="w-full space-y-4">

      {/* ── S616: Acknowledgment coverage indicator ── */}
      <AcknowledgmentCoverageCard periodId={periodId} />

      {/* ── Row 1: Existing period-level gate summary (unchanged) + System Intelligence ── */}
      <div className="w-full space-y-4">
        <Card
          title="Readiness"

          action={
            gates
              ? <Pill tone={gates.final_eligible ? "Complete" : gates.preview_eligible ? "Ready" : "Blocked"}>
                  {gates.final_eligible ? "Final Eligible" : gates.preview_eligible ? "Preview Eligible" : "Not Eligible"}
                </Pill>
              : null
          }
        >
          {loading && <LoadingRow />}
          {error && <ErrorNote message="Unable to load readiness status." />}
          {!loading && !error && !gates && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No readiness data for this period.</p>
          )}
          {gates && (
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <ValidationItem ok={gates.preview_eligible} warn={!gates.preview_eligible} text={`Preview eligible: ${gates.preview_eligible ? "Yes" : "No"}`} />
                <ValidationItem ok={gates.final_eligible} warn={!gates.final_eligible} text={`Final eligible: ${gates.final_eligible ? "Yes" : `Requires: ${gates.final_requires?.replace(/_/g, " ") ?? "—"}`}`} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Readiness records", value: entityCount ?? "—" },
                  { label: "Overall status",  value: overallStatus?.replace(/_/g, " ") ?? "—" },
                  { label: "Blocking issues", value: blockingCount },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 flex flex-col gap-1">
                    <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{label}</span>
                    <strong className={["text-tok-base font-semibold", label === "Blocking issues" && value > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-text))]"].join(" ")}>{value}</strong>
                  </div>
                ))}
              </div>
              {domains.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        {["Entity Type", "Count", "Min Status", "Blocking"].map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgb(var(--color-border))]">
                      {domains.map((d) => (
                        <tr key={d.entity_type} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className="px-3 py-2 text-[rgb(var(--color-text))] font-medium">{d.entity_type}</td>
                          <td className="px-3 py-2 text-[rgb(var(--color-text))]">{d.count}</td>
                          <td className="px-3 py-2 text-[rgb(var(--color-text))]">{d.min_status?.replace(/_/g, " ") ?? "—"}</td>
                          <td className="px-3 py-2">
                            <span className={d.blocking_issue_count > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}>{d.blocking_issue_count}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" onClick={recheck} disabled={loading}>{loading ? "Checking…" : "Re-check"}</Button>
          </div>
        </Card>
</div>

      {/* ── Row 2: S402 Position Scope ── */}
      <Card
        title="Position Scope"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant={reassessResult ? "secondary" : "secondary"}
              className={reassessing ? "opacity-70" : ""}
              onClick={handleReassess}
              disabled={reassessing || !periodId || period?.period_status !== "open"}
              title={period?.period_status !== "open" ? `Period is ${period?.period_status} — re-assessment not available` : "Re-assess — reads latest DB state for all positions"}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${reassessing ? "animate-spin" : ""}`} />
              {reassessing ? "Re-assessing…" : "Re-assess positions"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {reassessError && <ErrorNote message={reassessError} />}
          {reassessResult && (
            <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
              Re-assessment complete — {reassessResult.assessed} positions scanned ·{" "}
              {reassessResult.eligible} eligible · {reassessResult.ineligible} ineligible
            </div>
          )}
          <PositionScopeSection
            periodId={periodId}
            userId={userId}
            onHoldsChanged={() => setSnapshotRefreshKey((k) => k + 1)}
          />
        </div>
      </Card>

      {/* ── Row 3: S402 Population Snapshot ── */}
      <Card
        title="Population Snapshot"
        action={
          activeApprovedSnapshot
            ? <Pill tone="Complete">Snapshot approved</Pill>
            : resolvedSnapshotId
            ? <Pill tone="Warning">Snapshot pending</Pill>
            : <Pill tone="Draft">No snapshot</Pill>
        }
      >
        <div className="space-y-2">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            A population snapshot locks the included and excluded position sets for the run.
            It must be approved by a second comp admin (NN-36) before the engine will use it.
            Without a snapshot the engine runs the full population (legacy path).
          </p>
          <PopulationSnapshotSection
            periodId={periodId}
            userId={userId}
            onSnapshotApproved={(id) => {
              setApprovedSnapshotId(id);
              setSnapshotRefreshKey((k) => k + 1);
            }}
          />
        </div>
      </Card>

      {/* ── Row 4: Ready to Calculate panel removed — S602
           Reason: navigated to active period regardless of selected period (confusing).
           Calculate is accessible directly from left nav.
           Population Snapshot placement under review — OI-POPULATION-SNAPSHOT-PLACEMENT.
           Unused state vars (selectedScenarioSetId, runType, runLabel, submitting,
           runError, pollingRunId, pollingStatus, handleRunCalculation) retained in place
           to avoid refactor scope creep this session. Clean up post-GA:
           OI-READINESS-STATE-CLEANUP. ── */}

    </div>
  );
}

// ============================================================================
// SHARED MODAL PRIMITIVES
// ============================================================================

function InlineModal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, required, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
        {label}{required && <span className="ml-0.5 text-[rgb(var(--color-danger))]">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, className = "" }) {
  return (
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className={`w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none ${className}`}
    />
  );
}

function DateInput({ value, onChange }) {
  return (
    <input type="date" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ============================================================================
// FiscalCalendarPanel
// ============================================================================

function FiscalCalendarPanel({ mode }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: periodsData, loading: periodsLoading, error: periodsError } = useFetch(`${API_BASE}/fiscal-periods?_k=${refreshKey}`);
  const { data: yearsData } = useFetch(`${API_BASE}/fiscal-years?_k=${refreshKey}`);
  const allPeriods     = periodsData?.fiscal_periods ?? [];
  const allFiscalYears = yearsData?.data ?? [];

  const [modal, setModal]         = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting]   = useState(false);

  const [fyLabel, setFyLabel] = useState("");
  const [fyStart, setFyStart] = useState("");
  const [fyEnd,   setFyEnd]   = useState("");
  const [fpYearId, setFpYearId] = useState("");
  const [fpCode,   setFpCode]   = useState("");
  const [fpLabel,  setFpLabel]  = useState("");
  const [fpStart,  setFpStart]  = useState("");
  const [fpEnd,    setFpEnd]    = useState("");
  const [fpType,   setFpType]   = useState("monthly");
  const [generateYearId, setGenerateYearId] = useState(null); // fiscal_year_id for generate modal

  function resetFy() { setFyLabel(""); setFyStart(""); setFyEnd(""); setSubmitError(null); }
  function resetFp() { setFpYearId(allFiscalYears[0]?.fiscal_year_id ?? ""); setFpCode(""); setFpLabel(""); setFpStart(""); setFpEnd(""); setSubmitError(null); }

  async function handleCreateFiscalYear() {
    if (!fyLabel.trim() || !fyStart || !fyEnd) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/fiscal-years`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ fiscal_year_label: fyLabel.trim(), year_start_date: fyStart, year_end_date: fyEnd }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      setModal(null); resetFy(); setRefreshKey((k) => k + 1);
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  async function handleGeneratePeriods(fiscalYearId, periodType) {
    // S573: generate all monthly or quarterly periods for a fiscal year in one shot.
    // Idempotent — skips existing periods. Displays result counts to user.
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/fiscal-years/${fiscalYearId}/generate-periods`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ period_type: periodType }),
      });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) { throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      setModal(null); setRefreshKey((k) => k + 1);
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  async function handleCreatePeriod() {
    if (!fpCode.trim() || !fpStart || !fpEnd) return;
    setSubmitting(true); setSubmitError(null);
    try {
      // S573 fix: DDL CHECK allows only 'open','closed','locked' — no 'draft' status.
      // period_status defaults to 'open' server-side; do not send it from client.
      const body = { period_code: fpCode.trim(), period_label: fpLabel.trim() || undefined, period_start_date: fpStart, period_end_date: fpEnd, period_type: fpType || "monthly" };
      if (fpYearId) body.fiscal_year_id = Number(fpYearId);
      const res = await fetch(`${API_BASE}/fiscal-periods`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      setModal(null); resetFp(); setRefreshKey((k) => k + 1);
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  const STATUS_TONE = { open: "Ready", closed: "Complete", locked: "Complete", draft: "Draft" };

  // OI-FISCAL-ROLLFORWARD-BUTTON-GUARD
  // Roll Forward is only valid on the chronologically last locked period that has
  // no open or locked successor. Compute a Set of period IDs that have an active
  // successor so we can disable the button on all other locked periods.
  const lockedPeriodIdsWithActiveSuccessor = new Set(
    allPeriods
      .filter((p) => p.period_status === "locked")
      .filter((p) =>
        allPeriods.some(
          (other) =>
            other.period_start_date > p.period_start_date &&
            (other.period_status === "open" || other.period_status === "locked")
        )
      )
      .map((p) => p.fiscal_period_id)
  );

  async function handleRollForward(periodId) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/fiscal-periods/${periodId}/roll-forward`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="w-full space-y-4">
        <Card title="Fiscal Calendar" action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => { resetFp(); setModal("fiscal-period"); }}>New Period</Button>
            <Button onClick={() => { resetFy(); setModal("fiscal-year"); }}>New Fiscal Year</Button>
          </div>
        }>
          {periodsLoading && <LoadingRow />}
          {periodsError && <ErrorNote message="Unable to load fiscal periods." />}
          {submitError && <ErrorNote message={submitError} />}
          {!periodsLoading && !periodsError && allPeriods.length === 0 && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No fiscal periods found. Create a fiscal year and period to begin.</p>
          )}
          <div className="mt-2 space-y-2">
            {allPeriods.map((p) => {
              const isLocked = p.period_status === "locked";
              const canRollForward = isLocked && !lockedPeriodIdsWithActiveSuccessor.has(p.fiscal_period_id);
              return (
              <div key={p.fiscal_period_id} className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                <div className="grid gap-1 sm:grid-cols-3 flex-1">
                  <Field label="Period" value={p.period_label ?? p.period_code} />
                  <Field label="Start"  value={p.period_start_date} />
                  <Field label="End"    value={p.period_end_date} />
                </div>
                <div className="ml-3 flex shrink-0 items-center gap-2">
                  <Pill tone={STATUS_TONE[p.period_status] ?? "Draft"}>{p.period_status}</Pill>
                  {isLocked && (
                    <Button
                      variant="secondary"
                      onClick={() => handleRollForward(p.fiscal_period_id)}
                      disabled={!canRollForward || submitting}
                      title={canRollForward ? "Roll forward to next period" : "Roll Forward only available on the last locked period with no active successor"}
                    >
                      Roll Forward
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          {allFiscalYears.length > 0 && (
            <div className="mt-4 border-t border-[rgb(var(--color-border))] pt-4">
              <p className="mb-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Fiscal Years</p>
              <div className="space-y-1.5">
                {allFiscalYears.map((fy) => (
                  <div key={fy.fiscal_year_id} className="flex items-center justify-between text-tok-sm">
                    <span className="text-[rgb(var(--color-text))]">{fy.fiscal_year_label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{fy.year_start_date} → {fy.year_end_date}</span>
                      <Button variant="secondary" onClick={() => { setGenerateYearId(fy.fiscal_year_id); setFpType("monthly"); setSubmitError(null); setModal("generate-periods"); }}>
                        Generate Periods
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
        </div>

      {modal === "fiscal-year" && (
        <InlineModal title="Create Fiscal Year" onClose={() => setModal(null)}>
          <FormField label="Fiscal Year Label" required hint="e.g. FY2026"><TextInput value={fyLabel} onChange={setFyLabel} placeholder="FY2026" /></FormField>
          <FormField label="Start Date" required><DateInput value={fyStart} onChange={setFyStart} /></FormField>
          <FormField label="End Date" required hint="Must be after start date"><DateInput value={fyEnd} onChange={setFyEnd} /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreateFiscalYear} disabled={submitting || !fyLabel.trim() || !fyStart || !fyEnd}>{submitting ? "Creating…" : "Create Fiscal Year"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}

      {modal === "fiscal-period" && (
        <InlineModal title="Create Fiscal Period" onClose={() => setModal(null)}>
          {allFiscalYears.length > 0 && (
            <FormField label="Fiscal Year">
              <SelectInput value={fpYearId} onChange={setFpYearId} options={allFiscalYears.map((y) => ({ value: y.fiscal_year_id, label: y.fiscal_year_label }))} />
            </FormField>
          )}
          {allFiscalYears.length === 0 && (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-warning))]">No fiscal years found — create a fiscal year first.</div>
          )}
          <FormField label="Period Type" required>
            <SelectInput value={fpType} onChange={setFpType} options={[{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "annual", label: "Annual" }]} />
          </FormField>
          <FormField label="Period Code" required hint="e.g. FY2026-M02"><TextInput value={fpCode} onChange={setFpCode} placeholder="FY2026-M02" /></FormField>
          <FormField label="Period Label" hint="Display name (optional)"><TextInput value={fpLabel} onChange={setFpLabel} placeholder="Feb 2026" /></FormField>
          <FormField label="Start Date" required><DateInput value={fpStart} onChange={setFpStart} /></FormField>
          <FormField label="End Date" required><DateInput value={fpEnd} onChange={setFpEnd} /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreatePeriod} disabled={submitting || !fpCode.trim() || !fpStart || !fpEnd}>{submitting ? "Creating…" : "Create Period"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}

      {modal === "generate-periods" && (
        <InlineModal title="Generate Periods" onClose={() => setModal(null)}>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Generates all periods for the selected fiscal year with correct date ranges.
            Existing periods are skipped (idempotent).
          </p>
          <FormField label="Period Type" required>
            <SelectInput value={fpType} onChange={setFpType} options={[{ value: "monthly", label: "Monthly (12 periods)" }, { value: "quarterly", label: "Quarterly (4 periods)" }]} />
          </FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={() => handleGeneratePeriods(generateYearId, fpType)} disabled={submitting}>{submitting ? "Generating…" : "Generate"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
    </>
  );
}

// ============================================================================
// OrgCsvImportPanel
// CSV import mode for OrganizationPanel — shown when user toggles to "CSV Import".
// POSTs to POST /api/v1/import/org-setup (OI-ORG-REFERENCE-CSV-UPLOAD, S639).
// No validate/confirm step — single-shot idempotent upsert.
// Not period-gated — org reference data is tenant-level.
// ============================================================================

const ORG_CSV_CONTRACTS = {
  businessUnits: {
    fileType:  "business_unit",
    required:  ["bu_code", "bu_name", "is_active"],
    optional:  ["bu_weight", "quota_currency_code", "sort_order"],
    notes: [
      "bu_code: uppercase letters, numbers, underscores (e.g. ARR_NEW)",
      "is_active: true / false",
      "bu_weight: decimal 0–1 (default 1.0000 if omitted)",
      "quota_currency_code: ISO code, e.g. USD (default USD if omitted)",
    ],
  },
  segments: {
    fileType:  "segment",
    required:  ["segment_code", "segment_name", "is_active"],
    optional:  ["sort_order"],
    notes: [
      "segment_code: uppercase letters, numbers, underscores (e.g. ENT)",
      "is_active: true / false",
    ],
  },
  roles: {
    fileType:  "role",
    required:  ["role_code", "role_name", "calculation_mode", "quota_bearing", "is_active"],
    optional:  ["sort_order"],
    notes: [
      "calculation_mode maps to role category in the database",
      "Valid calculation_mode values: AE · CSM · SA · SPECIALIST · ic · hybrid · rollup · MANAGER · OTHER",
      "quota_bearing: true / false",
      "role_code: uppercase letters, numbers, hyphens, underscores (e.g. AE_ENT)",
    ],
  },
  sellingMotions: {
    fileType:  "selling_motion",
    required:  ["motion_code", "motion_name", "is_active"],
    optional:  ["sort_order"],
    notes: [
      "motion_code: uppercase letters, numbers, underscores only — enforced by database",
      "is_active: true / false",
    ],
  },
};

function OrgCsvImportPanel({ activeTab, onImportSuccess }) {
  const contract    = ORG_CSV_CONTRACTS[activeTab];
  const [file, setFile]         = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]     = useState(null);  // { ok, message }
  const [motions, setMotions]   = useState([]);

  // Load valid motion codes live for Selling Motions tab guidance
  useEffect(() => {
    if (activeTab !== "sellingMotions") return;
    fetch(`${API_BASE}/selling-motions`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setMotions(Array.isArray(d) ? d : (d?.data ?? [])); })
      .catch(() => {});
  }, [activeTab]);

  // Reset on tab change
  useEffect(() => { setFile(null); setResult(null); }, [activeTab]);

  async function handleUpload() {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file_type", contract.fileType);
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/import/org-setup`, { method: "POST", credentials: "include", body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = body?.detail?.error?.message ?? body?.error?.message ?? `Error ${res.status}`;
        setResult({ ok: false, message: msg });
      } else {
        setResult({ ok: true, message: `${body.rows_written} row(s) upserted successfully.` });
        setFile(null);
        onImportSuccess?.();
      }
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally { setUploading(false); }
  }

  return (
    <div className="space-y-4 px-4 py-4">
      {/* Field contract */}
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-2">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Required columns</p>
          <p className="mt-0.5 font-mono text-tok-xs text-[rgb(var(--color-text))]">{contract.required.join(", ")}</p>
        </div>
        {contract.optional.length > 0 && (
          <div>
            <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Optional columns</p>
            <p className="mt-0.5 font-mono text-tok-xs text-[rgb(var(--color-text))]">{contract.optional.join(", ")}</p>
          </div>
        )}
        <ul className="space-y-0.5">
          {contract.notes.map((n, i) => (
            <li key={i} className="text-tok-xs text-[rgb(var(--color-text-muted))] before:content-['·'] before:mr-1.5">{n}</li>
          ))}
        </ul>
      </div>

      {/* Valid motion codes — live from DB, shown only on Selling Motions tab */}
      {activeTab === "sellingMotions" && motions.length > 0 && (
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-1.5">
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Motion codes already in this tenant</p>
          <div className="flex flex-wrap gap-1.5">
            {motions.map((m) => (
              <span key={m.selling_motion_id} className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-0.5 font-mono text-tok-xs text-[rgb(var(--color-text))]">
                {m.motion_code}
              </span>
            ))}
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Re-uploading an existing code updates the name and is_active — it does not create a duplicate.</p>
        </div>
      )}

      {/* File picker + upload */}
      <div className="flex items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors">
          <input type="file" accept=".csv" className="sr-only" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }} />
          Choose CSV file
        </label>
        {file && <span className="text-tok-xs text-[rgb(var(--color-text-muted))] truncate max-w-xs">{file.name}</span>}
      </div>

      {file && (
        <button type="button" onClick={handleUpload} disabled={uploading}
          className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity">
          {uploading ? "Uploading…" : `Import ${ORG_ENTITY_CONFIG[activeTab].label}`}
        </button>
      )}

      {result && (
        <div className={`rounded-lg border px-4 py-3 ${result.ok ? "border-[rgb(var(--color-success,34,197,94))] bg-[rgb(var(--color-success,34,197,94))]/5" : "border-[rgb(var(--color-danger))] bg-[rgb(var(--color-danger))]/5"}`}>
          <p className={`text-tok-sm font-medium ${result.ok ? "text-[rgb(var(--color-success,34,197,94))]" : "text-[rgb(var(--color-danger))]"}`}>{result.message}</p>
        </div>
      )}
    </div>
  );
}

// OrganizationPanel
// Full tabbed editable-table view — mirrors ReferenceDataReview pattern.
// Four tabs: Business Units · Segments · Roles · Selling Motions
// Each tab: inline PATCH per row + POST modal for new rows.
// ============================================================================

const ORG_ENTITY_CONFIG = {
  businessUnits: {
    key: "businessUnits", label: "Business Units", endpoint: "/business-units",
    idField: "business_unit_id",
    columns: [
      { field: "bu_code",            label: "BU Code" },
      { field: "bu_name",            label: "BU Name" },
      { field: "bu_description",     label: "Description" },
      { field: "bu_weight",          label: "Weight" },
      { field: "quota_currency_code",label: "Currency" },
      { field: "is_active",          label: "Active" },
      { field: "sort_order",         label: "Sort" },
    ],
  },
  segments: {
    key: "segments", label: "Segments", endpoint: "/segments",
    idField: "segment_id",
    columns: [
      { field: "segment_code", label: "Segment Code" },
      { field: "segment_name", label: "Segment Name" },
      { field: "description",  label: "Description" },
      { field: "is_active",    label: "Active" },
      { field: "sort_order",   label: "Sort" },
    ],
  },
  roles: {
    key: "roles", label: "Roles", endpoint: "/roles",
    idField: "role_id",
    columns: [
      { field: "role_code",        label: "Role Code" },
      { field: "role_name",        label: "Role Name" },
      { field: "role_category",    label: "Category", readOnly: true },
      { field: "role_description", label: "Description" },
      { field: "is_active",        label: "Active" },
      { field: "sort_order",       label: "Sort" },
    ],
  },
  sellingMotions: {
    key: "sellingMotions", label: "Selling Motions", endpoint: "/selling-motions",
    idField: "selling_motion_id",
    columns: [
      { field: "motion_code",        label: "Motion Code" },
      { field: "motion_name",        label: "Motion Name" },
      { field: "motion_description", label: "Description" },
      { field: "allows_overlay",     label: "Overlay" },
      { field: "credit_split_pct",   label: "Credit Split %" },
      { field: "is_active",          label: "Active" },
      { field: "sort_order",         label: "Sort" },
    ],
  },
  // S700 — AD-94: Ramp Schedules as 5th tab in Organization.
  // Role-scoped artifact — logically follows Roles tab in definition sequence.
  // Custom UI rendered by RampSchedulesTab (not the generic OrgEditableCell table).
  // API: POST /ramp-schedules (manual upsert) + /ramp-schedules/import/validate+confirm (CSV).
  rampSchedules: {
    key: "rampSchedules", label: "Ramp Schedules", endpoint: "/ramp-schedules",
    idField: "ramp_schedule_id",
    columns: [], // not used — RampSchedulesTab renders its own UI
  },
};

const ORG_TAB_ORDER = ["businessUnits", "segments", "roles", "sellingMotions", "rampSchedules"];

const ROLE_CATEGORIES_ORG = [
  { value: "AE",         label: "Account Executive (AE)" },
  { value: "CSM",        label: "Customer Success Manager (CSM)" },
  { value: "SA",         label: "Solutions Architect (SA)" },
  { value: "SPECIALIST", label: "Specialist" },
];

function OrgEditableCell({ value, readOnly, onChange }) {
  if (readOnly) return <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">{String(value ?? "")}</span>;
  if (typeof value === "boolean") {
    return (
      <select value={value ? "true" : "false"} onChange={(e) => onChange(e.target.value === "true")}
        className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm">
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  return (
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm" />
  );
}

// ============================================================================
// RampSchedulesTab — S700 AD-94
// 5th tab in OrganizationPanel. Role-scoped ramp schedule definition.
// Manual path: role + effective_date + month/factor grid → POST /ramp-schedules
// CSV path:    validate → preview → confirm (same file format as ImportDataPanel + Excel add-in)
// API: POST /ramp-schedules (upsert, Gate 5 exempt per S268 DR-007)
//      GET  /ramp-schedules
//      POST /ramp-schedules/import/validate (multipart)
//      POST /ramp-schedules/import/confirm?session_id=...
//      GET  /ramp-schedules/import/template
// CSV columns: role_code, ramp_month, ramp_factor, effective_date
// ramp_factor: 0.0000–1.0000 | ramp_month: 1–36
// No amendment gate. No wizard props. No feature flag gate.
// ============================================================================

const RAMP_REQUIRED_CSV_COLS = ["role_code", "ramp_month", "ramp_factor", "effective_date"];

function formatRampFactor(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : String(v);
}

function RampSchedulesTab() {
  // Reference data
  const [roles,          setRoles]          = useState([]);
  const [rolesLoading,   setRolesLoading]   = useState(false);
  const [rolesError,     setRolesError]     = useState(null);

  // Schedule rows
  const [rows,           setRows]           = useState([]);
  const [rowsLoading,    setRowsLoading]    = useState(false);
  const [rowsError,      setRowsError]      = useState(null);

  // Manual create form
  const [manualRoleId,   setManualRoleId]   = useState("");
  const [manualEffDate,  setManualEffDate]  = useState("");
  // Grid: array of { ramp_month, ramp_factor } — start with 3 blank rows
  const [gridRows,       setGridRows]       = useState([
    { ramp_month: "1", ramp_factor: "" },
    { ramp_month: "2", ramp_factor: "" },
    { ramp_month: "3", ramp_factor: "" },
  ]);
  const [manualSaving,   setManualSaving]   = useState(false);
  const [manualError,    setManualError]    = useState(null);
  const [manualSuccess,  setManualSuccess]  = useState(null);

  // CSV import
  const [importMode,     setImportMode]     = useState("manual"); // "manual" | "csv"
  const [csvFile,        setCsvFile]        = useState(null);
  const [csvSession,     setCsvSession]     = useState(null);
  const [csvImporting,   setCsvImporting]   = useState(false);
  const [csvError,       setCsvError]       = useState(null);
  const [csvCommitted,   setCsvCommitted]   = useState(false);

  // Filter
  const [filterRoleId,   setFilterRoleId]   = useState("");

  const roleNameById = useMemo(() =>
    Object.fromEntries(roles.map((r) => [String(r.role_id), r.role_name ?? r.role_code])),
    [roles]
  );

  // Load roles
  useEffect(() => {
    setRolesLoading(true);
    fetch(`${API_BASE}/roles`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setRoles(Array.isArray(d) ? d : d?.roles ?? d?.data ?? []))
      .catch((e) => setRolesError(e.message))
      .finally(() => setRolesLoading(false));
  }, []);

  // Load ramp schedules
  const loadRows = useCallback(() => {
    setRowsLoading(true);
    setRowsError(null);
    const url = filterRoleId
      ? `${API_BASE}/ramp-schedules?role_id=${filterRoleId}`
      : `${API_BASE}/ramp-schedules`;
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setRows(d?.data ?? d?.ramp_schedules ?? (Array.isArray(d) ? d : [])))
      .catch((e) => setRowsError(e.message))
      .finally(() => setRowsLoading(false));
  }, [filterRoleId]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Group rows by role + effective_date for display
  const grouped = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const k = `${r.role_id}__${r.effective_date}`;
      if (!map[k]) map[k] = { role_id: r.role_id, effective_date: r.effective_date, months: [] };
      map[k].months.push(r);
    });
    Object.values(map).forEach((g) =>
      g.months.sort((a, b) => Number(a.ramp_month) - Number(b.ramp_month))
    );
    return Object.values(map).sort((a, b) => {
      const rc = String(a.role_id).localeCompare(String(b.role_id));
      return rc !== 0 ? rc : b.effective_date.localeCompare(a.effective_date);
    });
  }, [rows]);

  // Grid helpers
  function updateGridRow(idx, field, value) {
    setGridRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }
  function addGridRow() {
    setGridRows((prev) => {
      const nextMonth = prev.length > 0
        ? String(Number(prev[prev.length - 1].ramp_month) + 1)
        : "1";
      return [...prev, { ramp_month: nextMonth, ramp_factor: "" }];
    });
  }
  function removeGridRow(idx) {
    setGridRows((prev) => prev.filter((_, i) => i !== idx));
  }

  // Manual save — POST /ramp-schedules
  async function handleManualSave(e) {
    e.preventDefault();
    setManualError(null);
    setManualSuccess(null);

    if (!manualRoleId)  { setManualError("Role is required."); return; }
    if (!manualEffDate) { setManualError("Effective date is required."); return; }

    const validRows = gridRows.filter((r) => r.ramp_month !== "" && r.ramp_factor !== "");
    if (validRows.length === 0) { setManualError("At least one ramp month row is required."); return; }

    // Client-side validations
    for (const r of validRows) {
      const m = Number(r.ramp_month);
      const f = Number(r.ramp_factor);
      if (!Number.isInteger(m) || m < 1 || m > 36) {
        setManualError(`Ramp month must be a positive integer 1–36 (got ${r.ramp_month}).`); return;
      }
      if (!Number.isFinite(f) || f < 0 || f > 1) {
        setManualError(`Ramp factor must be between 0.0000 and 1.0000 (got ${r.ramp_factor}).`); return;
      }
    }

    const months = validRows.map((r) => Number(r.ramp_month));
    if (new Set(months).size !== months.length) {
      setManualError("Duplicate ramp months are not allowed."); return;
    }

    setManualSaving(true);
    try {
      const res = await fetch(`${API_BASE}/ramp-schedules`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role_id:        Number(manualRoleId),
          effective_date: manualEffDate,
          rows: validRows.map((r) => ({
            ramp_month:  Number(r.ramp_month),
            ramp_factor: Number(r.ramp_factor),
          })),
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setManualSuccess(`${data.rows_written ?? validRows.length} row(s) saved for ${roleNameById[manualRoleId] ?? manualRoleId} effective ${manualEffDate}.`);
      setGridRows([
        { ramp_month: "1", ramp_factor: "" },
        { ramp_month: "2", ramp_factor: "" },
        { ramp_month: "3", ramp_factor: "" },
      ]);
      loadRows();
    } catch (e) {
      setManualError(e.message ?? "Unable to save ramp schedule.");
    } finally {
      setManualSaving(false);
    }
  }

  // CSV validate
  async function handleCsvValidate() {
    if (!csvFile) return;
    setCsvError(null);
    setCsvSession(null);
    setCsvCommitted(false);
    setCsvImporting(true);
    try {
      // Client-side header check
      const text = await csvFile.text();
      const firstLine = text.trim().split("\n")[0] ?? "";
      const headers = firstLine.split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
      const missing = RAMP_REQUIRED_CSV_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) {
        setCsvError(`Missing required columns: ${missing.join(", ")}`);
        return;
      }
      const fd = new FormData();
      fd.append("file", csvFile);
      const res = await fetch(`${API_BASE}/ramp-schedules/import/validate`, {
        method: "POST", credentials: "include", body: fd,
      });
      const json = await res.json();
      if (!res.ok) {
        const errs = json?.detail?.error?.detail?.errors;
        setCsvError(Array.isArray(errs) ? errs.join("\n") : (json?.detail?.error?.message ?? `Validation failed (${res.status})`));
        return;
      }
      setCsvSession(json);
    } catch (e) {
      setCsvError(e.message ?? "Validation request failed.");
    } finally {
      setCsvImporting(false);
    }
  }

  // CSV confirm
  async function handleCsvConfirm() {
    if (!csvSession?.session_id) return;
    setCsvImporting(true);
    setCsvError(null);
    try {
      const res = await fetch(
        `${API_BASE}/ramp-schedules/import/confirm?session_id=${encodeURIComponent(csvSession.session_id)}`,
        { method: "POST", credentials: "include" }
      );
      const json = await res.json();
      if (!res.ok) {
        setCsvError(json?.detail?.error?.message ?? `Confirm failed (${res.status})`);
        return;
      }
      setCsvCommitted(true);
      setCsvSession(null);
      setCsvFile(null);
      loadRows();
    } catch (e) {
      setCsvError(e.message ?? "Confirm request failed.");
    } finally {
      setCsvImporting(false);
    }
  }

  const inputCls = "w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1.5 text-tok-sm focus:outline-none focus:border-[rgb(var(--color-primary))]";
  const thCls    = "px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide";
  const tdCls    = "px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]";

  return (
    <div className="space-y-5">

      {/* ── Description ── */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Define ramp factors by role and month. Ramp month 1 = rep's first month in role, regardless of calendar.
        Ramp factor 0.5000 = 50% quota attainment credit. The engine applies the version active at the rep's ramp start date.
        This file format is identical to the Import Data panel and Excel add-in (role_code, ramp_month, ramp_factor, effective_date).
      </div>

      {/* ── Manual / CSV toggle — mirrors other OrganizationPanel tabs ── */}
      <div className="flex items-center justify-end">
        <div className="flex items-center rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-0.5 gap-0.5">
          {["manual", "csv"].map((m) => (
            <button key={m} type="button"
              onClick={() => { setImportMode(m); setManualError(null); setManualSuccess(null); setCsvError(null); setCsvSession(null); setCsvCommitted(false); }}
              className={`rounded-md px-3 py-1 text-tok-xs font-medium transition-colors ${importMode === m ? "bg-[rgb(var(--color-primary))] text-white" : "text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"}`}>
              {m === "manual" ? "Manual" : "CSV Import"}
            </button>
          ))}
        </div>
      </div>

      {/* ── MANUAL PATH ── */}
      {importMode === "manual" && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 space-y-4">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Add / update ramp schedule</h3>

          {manualError   && <div className="rounded-lg border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{manualError}</div>}
          {manualSuccess && <div className="rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-2 text-tok-sm text-[rgb(var(--color-success))]">{manualSuccess}</div>}

          <form onSubmit={handleManualSave} className="space-y-4">
            {/* Role + effective date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Role</label>
                {rolesLoading ? <div className="h-8 rounded-lg skeleton" /> : (
                  <select value={manualRoleId} onChange={(e) => setManualRoleId(e.target.value)} className={inputCls} required>
                    <option value="">Select role…</option>
                    {roles.map((r) => (
                      <option key={r.role_id} value={r.role_id}>{r.role_name ?? r.role_code}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Effective Date</label>
                <input type="date" value={manualEffDate} onChange={(e) => setManualEffDate(e.target.value)} className={inputCls} required />
              </div>
            </div>

            {/* Month / factor grid */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Ramp Months</p>
                <button type="button" onClick={addGridRow}
                  className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline">
                  + Add month
                </button>
              </div>
              <div className="overflow-hidden rounded-lg border border-[rgb(var(--color-border))]">
                <table className="w-full">
                  <thead className="bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
                    <tr>
                      <th className={thCls}>Month #</th>
                      <th className={thCls}>Ramp Factor (0–1)</th>
                      <th className={thCls}>Display</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {gridRows.map((row, idx) => (
                      <tr key={idx} className="border-b border-[rgb(var(--color-border))] last:border-0">
                        <td className="px-3 py-2 w-32">
                          <input type="number" min="1" max="36" step="1"
                            value={row.ramp_month}
                            onChange={(e) => updateGridRow(idx, "ramp_month", e.target.value)}
                            className={inputCls} />
                        </td>
                        <td className="px-3 py-2 w-48">
                          <input type="number" min="0" max="1" step="0.0001"
                            value={row.ramp_factor}
                            onChange={(e) => updateGridRow(idx, "ramp_factor", e.target.value)}
                            placeholder="e.g. 0.5000"
                            className={inputCls} />
                        </td>
                        <td className="px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
                          {formatRampFactor(row.ramp_factor)}
                        </td>
                        <td className="px-3 py-2">
                          {gridRows.length > 1 && (
                            <button type="button" onClick={() => removeGridRow(idx)}
                              className="text-tok-xs text-[rgb(var(--color-danger))] hover:underline">
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Ramp factor 0.5000 = 50% · 1.0000 = fully ramped · must be between 0 and 1.
              </p>
            </div>

            <button type="submit" disabled={manualSaving}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
              {manualSaving ? "Saving…" : "Save Ramp Schedule"}
            </button>
          </form>
        </div>
      )}

      {/* ── CSV PATH ── */}
      {importMode === "csv" && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 space-y-4">
          <div>
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Same file format as Import Data panel and Excel add-in.
              Required columns: <code className="text-tok-xs font-mono">role_code, ramp_month, ramp_factor, effective_date</code>.
              Existing rows for the same role + month + effective date are updated in place.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a href={`${API_BASE}/ramp-schedules/import/template`}
              className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline">
              Download template
            </a>
          </div>

          {csvError && (
            <div className="rounded-lg border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))] whitespace-pre-wrap">{csvError}</div>
          )}
          {csvCommitted && (
            <div className="rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">✓ Import committed — ramp schedules updated.</div>
          )}

          {/* Step 1 — file + validate */}
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">1</span>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Select file and validate</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept=".csv"
                onChange={(e) => {
                  setCsvFile(e.target.files?.[0] ?? null);
                  setCsvSession(null); setCsvCommitted(false); setCsvError(null);
                }}
                className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              />
              <button type="button"
                disabled={!csvFile || csvImporting}
                onClick={handleCsvValidate}
                className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">
                {csvImporting && !csvSession ? "Validating…" : "Validate"}
              </button>
            </div>

            {/* Validation result */}
            {csvSession && (
              <div className="space-y-3">
                <div className="flex items-center gap-4 text-tok-sm">
                  <span className="text-[rgb(var(--color-success))]">
                    ✓ <strong>{csvSession.rows_valid}</strong> row(s) valid
                  </span>
                  {csvSession.rows_rejected > 0 && (
                    <span className="text-[rgb(var(--color-danger))]">
                      <strong>{csvSession.rows_rejected}</strong> rejected — fix CSV and re-upload.
                    </span>
                  )}
                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                    Session expires {new Date(csvSession.expires_at).toLocaleTimeString()}
                  </span>
                </div>

                {/* Preview */}
                {csvSession.preview?.length > 0 && (
                  <div className="overflow-x-auto rounded-lg border border-[rgb(var(--color-border))]">
                    <table className="w-full text-tok-xs">
                      <thead className="bg-[rgb(var(--color-surface-muted))]">
                        <tr>
                          {["role_code","ramp_month","ramp_factor","effective_date"].map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[rgb(var(--color-border))]">
                        {csvSession.preview.map((r, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-text))]">{r.role_code}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{r.ramp_month}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{formatRampFactor(r.ramp_factor)}</td>
                            <td className="px-3 py-1.5 text-[rgb(var(--color-text))]">{r.effective_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 2 — confirm */}
          {csvSession && csvSession.rows_rejected === 0 && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">2</span>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Confirm and load</p>
              </div>
              <p className="text-tok-sm text-[rgb(var(--color-success))]">
                ✓ Validation passed — {csvSession.rows_valid} row(s) ready to load.
              </p>
              <button type="button"
                disabled={csvImporting}
                onClick={handleCsvConfirm}
                className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
                {csvImporting ? "Loading…" : "Confirm and load"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Existing Ramp Schedules Table ── */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--color-border))] px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Ramp Schedules</h3>
            {rows.length > 0 && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">({rows.length} rows)</span>}
          </div>
          <div className="flex items-center gap-2">
            {/* Role filter */}
            <select value={filterRoleId} onChange={(e) => setFilterRoleId(e.target.value)}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none">
              <option value="">All roles</option>
              {roles.map((r) => (
                <option key={r.role_id} value={r.role_id}>{r.role_name ?? r.role_code}</option>
              ))}
            </select>
            <button type="button" onClick={loadRows} disabled={rowsLoading}
              className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-40">
              {rowsLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {rowsLoading && <div className="px-4 py-6"><LoadingRow /></div>}
        {rowsError   && <div className="px-4 py-4"><ErrorNote message="Unable to load ramp schedules." /></div>}

        {!rowsLoading && !rowsError && grouped.length === 0 && (
          <div className="px-4 py-8 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
            No ramp schedules configured. Use the form above to add rows.
          </div>
        )}

        {!rowsLoading && !rowsError && grouped.length > 0 && (
          <div className="divide-y divide-[rgb(var(--color-border))]">
            {grouped.map((g) => (
              <div key={`${g.role_id}__${g.effective_date}`} className="px-4 py-3">
                <div className="mb-2 flex items-center gap-3">
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                    {roleNameById[String(g.role_id)] ?? `Role ${g.role_id}`}
                  </p>
                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">effective {g.effective_date}</span>
                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{g.months.length} month{g.months.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.months.map((m) => (
                    <div key={m.ramp_schedule_id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2.5 py-1 text-tok-xs">
                      <span className="font-medium text-[rgb(var(--color-text-muted))]">M{m.ramp_month}</span>
                      <span className="font-semibold text-[rgb(var(--color-text))]">{formatRampFactor(m.ramp_factor)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrganizationPanel({ mode }) {
  const [activeTab, setActiveTab]       = useState("businessUnits");
  const [importMode, setImportMode]     = useState("manual"); // "manual" | "csv"
  const [rows, setRows]                 = useState({ businessUnits: [], segments: [], roles: [], sellingMotions: [], rampSchedules: [] });
  const [loading, setLoading]           = useState({});
  const [loadError, setLoadError]       = useState({});
  const [editMap, setEditMap]           = useState({});
  const [selected, setSelected]         = useState(new Set());
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [deleteError, setDeleteError]   = useState(null);
  const [modal, setModal]               = useState(null);
  const [submitError, setSubmitError]   = useState(null);
  const [submitting, setSubmitting]     = useState(false);
  const [buName, setBuName]             = useState("");
  const [buCode, setBuCode]             = useState("");
  const [buWeight, setBuWeight]         = useState("");
  const [buCurrency, setBuCurrency]     = useState("USD");
  const [segName, setSegName]           = useState("");
  const [segCode, setSegCode]           = useState("");
  const [segDesc, setSegDesc]           = useState("");
  const [roleName, setRoleName]         = useState("");
  const [roleCode, setRoleCode]         = useState("");
  const [roleCategory, setRoleCategory] = useState("AE");
  const [motionName, setMotionName]     = useState("");
  const [motionCode, setMotionCode]     = useState("");
  const [creditSplitPct, setCreditSplitPct] = useState("");

  const config  = ORG_ENTITY_CONFIG[activeTab];
  const tabRows = rows[activeTab] ?? [];

  function switchTab(key) {
    setActiveTab(key);
    setImportMode("manual");
    setSelected(new Set());
    setDeleteConfirm(false);
    setDeleteError(null);
    setSaveError(null);
  }

  async function fetchTab(key) {
    const cfg = ORG_ENTITY_CONFIG[key];
    setLoading((c) => ({ ...c, [key]: true }));
    setLoadError((c) => ({ ...c, [key]: null }));
    try {
      const res = await fetch(`${API_BASE}${cfg.endpoint}`, { credentials: "include" });
      if (!res.ok) {
        if (res.status < 500) { setRows((c) => ({ ...c, [key]: [] })); return; }
        throw new Error(`Server error (${res.status}) — please try again.`);
      }
      const payload = await res.json();
      const data = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      setRows((c) => ({ ...c, [key]: data }));
    } catch (e) { setLoadError((c) => ({ ...c, [key]: e.message })); }
    finally { setLoading((c) => ({ ...c, [key]: false })); }
  }

  // rampSchedules excluded — RampSchedulesTab manages its own data fetching internally (AD-94)
  useEffect(() => { ORG_TAB_ORDER.filter((k) => k !== "rampSchedules").forEach(fetchTab); }, []);

  function getVal(row, field) {
    const k = `${activeTab}:${row[config.idField]}`;
    return Object.prototype.hasOwnProperty.call(editMap[k] ?? {}, field) ? editMap[k][field] : row[field];
  }

  function setVal(row, field, value) {
    const k = `${activeTab}:${row[config.idField]}`;
    setEditMap((c) => ({ ...c, [k]: { ...(c[k] ?? {}), [field]: value } }));
  }

  // Dirty rows on current tab
  const dirtyKeys = Object.keys(editMap).filter((k) => k.startsWith(`${activeTab}:`));
  const hasDirty  = dirtyKeys.length > 0;

  // BU weight sum — recalculated live as user edits weight cells
  const buWeightSum = activeTab === "businessUnits"
    ? (rows.businessUnits ?? []).reduce((sum, row) => {
        const k = `businessUnits:${row.business_unit_id}`;
        const w = editMap[k]?.bu_weight !== undefined
          ? parseFloat(editMap[k].bu_weight)
          : parseFloat(row.bu_weight ?? 0);
        return sum + (isNaN(w) ? 0 : w);
      }, 0)
    : null;
  const buWeightOk  = buWeightSum === null || Math.abs(buWeightSum - 1.0) < 0.00011;
  const saveBlocked = !hasDirty || saving || (activeTab === "businessUnits" && !buWeightOk);

  // Save All — PATCH every dirty row on current tab
  async function handleSaveAll() {
    setSaving(true); setSaveError(null);
    const errors = [];
    for (const k of dirtyKeys) {
      const rowId   = parseInt(k.split(":")[1], 10);
      const row     = tabRows.find((r) => r[config.idField] === rowId);
      if (!row) continue;
      const changes = editMap[k];
      try {
        const res = await fetch(`${API_BASE}${config.endpoint}/${rowId}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          credentials: "include", body: JSON.stringify(changes),
        });
        if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
        const saved   = await res.json().catch(() => null);
        const updated = saved?.data ?? saved ?? { ...row, ...changes };
        setRows((c) => ({ ...c, [activeTab]: c[activeTab].map((r) => r[config.idField] === rowId ? { ...r, ...updated } : r) }));
        setEditMap((c) => { const n = { ...c }; delete n[k]; return n; });
      } catch (e) {
        errors.push(`${row[config.columns[0].field]}: ${e.message}`);
      }
    }
    if (errors.length) setSaveError(errors.join(" · "));
    setSaving(false);
  }

  // Delete selected rows
  async function handleDelete() {
    setDeleting(true); setDeleteError(null);
    const errors = [];
    for (const rowId of selected) {
      try {
        const res = await fetch(`${API_BASE}${config.endpoint}/${rowId}`, { method: "DELETE", credentials: "include" });
        if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? b?.detail?.error?.message ?? `Error ${res.status}`); }
        setRows((c) => ({ ...c, [activeTab]: c[activeTab].filter((r) => r[config.idField] !== rowId) }));
        setEditMap((c) => { const n = { ...c }; delete n[`${activeTab}:${rowId}`]; return n; });
      } catch (e) {
        const row = tabRows.find((r) => r[config.idField] === rowId);
        errors.push(`${row?.[config.columns[0].field] ?? rowId}: ${e.message}`);
      }
    }
    setSelected(new Set());
    setDeleteConfirm(false);
    if (errors.length) setDeleteError(errors.join(" · "));
    setDeleting(false);
  }

  // Checkbox helpers
  function toggleRow(rowId) {
    setSelected((s) => { const n = new Set(s); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  }
  function toggleAll() {
    setSelected((s) => s.size === tabRows.length ? new Set() : new Set(tabRows.map((r) => r[config.idField])));
  }
  const allChecked  = tabRows.length > 0 && selected.size === tabRows.length;
  const someChecked = selected.size > 0 && !allChecked;

  function resetCreate() {
    setBuName(""); setBuCode(""); setBuWeight(""); setBuCurrency("USD");
    setSegName(""); setSegCode(""); setSegDesc("");
    setRoleName(""); setRoleCode(""); setRoleCategory("AE");
    setMotionName(""); setMotionCode(""); setCreditSplitPct("");
    setSubmitError(null);
  }

  async function handleCreate() {
    setSubmitting(true); setSubmitError(null);
    try {
      let body = {};
      if (modal === "bu")           body = { bu_name: buName.trim(), bu_code: buCode.trim().toUpperCase(), quota_currency_code: buCurrency.trim().toUpperCase() || "USD", ...(buWeight ? { bu_weight: parseFloat(buWeight) } : {}) };
      else if (modal === "segment") body = { segment_name: segName.trim(), segment_code: segCode.trim().toUpperCase(), ...(segDesc.trim() ? { description: segDesc.trim() } : {}) };
      else if (modal === "role")    body = { role_name: roleName.trim(), role_code: roleCode.trim().toUpperCase(), role_category: roleCategory };
      else if (modal === "motion")  body = { motion_name: motionName.trim(), motion_code: motionCode.trim().toUpperCase(), ...(creditSplitPct ? { credit_split_pct: parseFloat(creditSplitPct) } : {}) };
      const endpointMap = { bu: "/business-units", segment: "/segments", role: "/roles", motion: "/selling-motions" };
      const tabMap      = { bu: "businessUnits",   segment: "segments",  role: "roles",  motion: "sellingMotions" };
      const res = await fetch(`${API_BASE}${endpointMap[modal]}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(body),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? b?.detail?.error?.message ?? `Error ${res.status}`); }
      const created = await res.json().catch(() => null);
      const newRow  = created?.data ?? created;
      if (newRow) setRows((c) => ({ ...c, [tabMap[modal]]: [...c[tabMap[modal]], newRow] }));
      else fetchTab(tabMap[modal]);
      setModal(null); resetCreate();
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  const CREATE_DISABLED = {
    bu:      submitting || !buName.trim() || !buCode.trim(),
    segment: submitting || !segName.trim() || !segCode.trim(),
    role:    submitting || !roleName.trim() || !roleCode.trim(),
    motion:  submitting || !motionName.trim() || !motionCode.trim(),
  };

  const newModalKey = { businessUnits: "bu", segments: "segment", roles: "role", sellingMotions: "motion" }[activeTab];
  const newLabel    = { bu: "New Business Unit", segment: "New Segment", role: "New Role", motion: "New Selling Motion" }[newModalKey];

  return (
    <>
      <div className="w-full space-y-4">
        <div className="space-y-0">

          {/* Tab bar */}
          <div className="flex items-center justify-between rounded-t-2xl border border-b-0 border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 pt-3">
            <div className="flex gap-1">
              {ORG_TAB_ORDER.map((key) => (
                <button key={key} type="button" onClick={() => switchTab(key)}
                  className={`rounded-t-xl px-4 py-2 text-tok-sm font-medium transition-colors ${activeTab === key ? "bg-[rgb(var(--color-primary))] text-white" : "text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"}`}>
                  {ORG_ENTITY_CONFIG[key].label}
                  {key !== "rampSchedules" && rows[key]?.length > 0 && <span className="ml-1.5 text-tok-xs opacity-70">({rows[key].length})</span>}
                </button>
              ))}
            </div>
            {/* Manual / CSV Import toggle — hidden for rampSchedules (handled internally by RampSchedulesTab) */}
            {activeTab !== "rampSchedules" && (
              <div className="flex items-center mb-1 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-0.5 gap-0.5">
                {["manual", "csv"].map((m) => (
                  <button key={m} type="button" onClick={() => setImportMode(m)}
                    className={`rounded-md px-3 py-1 text-tok-xs font-medium transition-colors ${importMode === m ? "bg-[rgb(var(--color-primary))] text-white" : "text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"}`}>
                    {m === "manual" ? "Manual" : "CSV Import"}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Ramp Schedules tab — custom UI, bypasses generic table entirely */}
          {activeTab === "rampSchedules" && (
            <div className="border-x border-b rounded-b-2xl border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4">
              <RampSchedulesTab />
            </div>
          )}

          {/* CSV Import mode — replaces toolbar + table (non-rampSchedules tabs only) */}
          {activeTab !== "rampSchedules" && importMode === "csv" && (
            <div className="border-x border-b rounded-b-2xl border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              <OrgCsvImportPanel activeTab={activeTab} onImportSuccess={() => fetchTab(activeTab)} />
            </div>
          )}

          {/* Toolbar + table — Manual mode only, non-rampSchedules tabs only */}
          {activeTab !== "rampSchedules" && importMode === "manual" && (
            <>
            <div className="flex items-center gap-2 border-x border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2">
            <Button onClick={() => { resetCreate(); setModal(newModalKey); }}>{newLabel}</Button>
            <button type="button"
              disabled={selected.size === 0 || deleting}
              onClick={() => { setDeleteConfirm(true); setDeleteError(null); }}
              className="rounded-lg border border-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-danger))] disabled:cursor-not-allowed disabled:opacity-40 hover:bg-[rgb(var(--color-danger))]/10">
              Delete{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
            <button type="button"
              disabled={saveBlocked}
              onClick={handleSaveAll}
              title={activeTab === "businessUnits" && !buWeightOk ? `Weights sum to ${buWeightSum?.toFixed(4)} — must equal 1.0` : undefined}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40">
              {saving ? "Saving…" : "Save"}
            </button>
            {activeTab === "businessUnits" && tabRows.length > 0 && (
              <span className={`ml-auto text-tok-xs font-medium ${buWeightOk ? "text-[rgb(var(--color-success,34,197,94))]" : "text-[rgb(var(--color-warning))]"}`}>
                Weights: {buWeightSum?.toFixed(4)} / 1.0{buWeightOk ? " ✓" : " ⚠ must equal 1.0 to save"}
              </span>
            )}
            {saveError && !activeTab === "businessUnits" && (
              <span className="ml-auto text-tok-xs text-[rgb(var(--color-danger))]">{saveError}</span>
            )}
          </div>

          {/* Save error (non-BU tabs) */}
          {saveError && (
            <div className="border-x border-[rgb(var(--color-border))] bg-[rgb(var(--color-danger))]/5 px-4 py-2">
              <p className="text-tok-xs text-[rgb(var(--color-danger))]">{saveError}</p>
            </div>
          )}

          {/* Delete confirm banner */}
          {deleteConfirm && (
            <div className="flex items-center gap-3 border-x border-[rgb(var(--color-border))] bg-[rgb(var(--color-danger))]/10 px-4 py-2.5">
              <span className="text-tok-sm text-[rgb(var(--color-danger))]">
                Delete {selected.size} {selected.size === 1 ? "item" : "items"}? This cannot be undone.
              </span>
              <button type="button" onClick={handleDelete} disabled={deleting}
                className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1 text-tok-xs font-medium text-white disabled:opacity-40">
                {deleting ? "Deleting…" : "Confirm"}
              </button>
              <button type="button" onClick={() => { setDeleteConfirm(false); setDeleteError(null); }}
                className="text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]">
                Cancel
              </button>
              {deleteError && <span className="text-tok-xs text-[rgb(var(--color-danger))]">{deleteError}</span>}
            </div>
          )}

          {/* Table */}
          <div className="rounded-b-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
            {loadError[activeTab] && (
              <div className="px-6 py-8 text-center">
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">Something went wrong</p>
                <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">{loadError[activeTab]}</p>
                <button type="button" onClick={() => fetchTab(activeTab)} className="mt-3 rounded-lg border border-[rgb(var(--color-border))] px-4 py-1.5 text-tok-sm text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">Try again</button>
              </div>
            )}
            <div className="overflow-x-auto">
              {loading[activeTab] ? (
                <div className="px-4 py-10 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</div>
              ) : tabRows.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                    {activeTab === "businessUnits"  && "Create your business unit structure"}
                    {activeTab === "segments"       && "Define your sales segments"}
                    {activeTab === "roles"          && "Set up your sales roles"}
                    {activeTab === "sellingMotions" && "Configure your selling motions"}
                  </p>
                  <p className="mt-1.5 max-w-md mx-auto text-tok-sm text-[rgb(var(--color-text-muted))]">
                    {activeTab === "businessUnits"  && "Business units group your sales organization for quota allocation and reporting. Add at least one before importing positions."}
                    {activeTab === "segments"       && "Segments define how your market is divided — e.g. Enterprise, SMB, Federal. Positions are assigned to a segment at import."}
                    {activeTab === "roles"          && "Roles classify each rep's function — AE, CSM, SE, Manager. Role category controls eligibility for plan components."}
                    {activeTab === "sellingMotions" && "Selling motions define how credit is split on shared deals — Direct, Renewal, Overlay, Management. Required before loading credits."}
                  </p>
                </div>
              ) : (
                <table className="w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
                  <thead className="bg-[rgb(var(--color-surface-muted))]">
                    <tr>
                      <th className="w-10 px-3 py-3">
                        <input type="checkbox" checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = someChecked; }}
                          onChange={toggleAll}
                          className="rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]" />
                      </th>
                      {config.columns.map((col) => (
                        <th key={col.field} className="px-3 py-3 text-left font-semibold text-[rgb(var(--color-text))] whitespace-nowrap">{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
                    {tabRows.map((row) => {
                      const rowId     = row[config.idField];
                      const editKey   = `${activeTab}:${rowId}`;
                      const isDirty   = Boolean(editMap[editKey] && Object.keys(editMap[editKey]).length);
                      const isSelected = selected.has(rowId);
                      return (
                        <tr key={rowId}
                          className={`${isSelected ? "bg-[rgb(var(--color-primary))]/5" : ""} ${isDirty ? "ring-1 ring-inset ring-[rgb(var(--color-primary))]/30" : ""}`}>
                          <td className="w-10 px-3 py-3">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleRow(rowId)}
                              className="rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]" />
                          </td>
                          {config.columns.map((col) => (
                            <td key={col.field} className="px-3 py-3 align-top text-[rgb(var(--color-text))]">
                              <OrgEditableCell value={getVal(row, col.field)} readOnly={col.readOnly} onChange={(v) => setVal(row, col.field, v)} />
                              {col.field === "role_category" && <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Engine-constrained</p>}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
            </>
          )}
        </div>
</div>

      {/* Create modals */}
      {modal === "bu" && (
        <InlineModal title="Create Business Unit" onClose={() => setModal(null)}>
          <FormField label="BU Name" required><TextInput value={buName} onChange={setBuName} placeholder="Enterprise" /></FormField>
          <FormField label="BU Code" required hint="Uppercase letters, numbers, underscores"><TextInput value={buCode} onChange={(v) => setBuCode(v.toUpperCase())} placeholder="ENT" /></FormField>
          <FormField label="Weight" hint="Proportion of total quota (0 to 1)"><TextInput value={buWeight} onChange={setBuWeight} placeholder="1.0" /></FormField>
          <FormField label="Currency" hint="Reporting currency code"><TextInput value={buCurrency} onChange={(v) => setBuCurrency(v.toUpperCase())} placeholder="USD" /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreate} disabled={CREATE_DISABLED.bu}>{submitting ? "Creating…" : "Create Business Unit"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
      {modal === "segment" && (
        <InlineModal title="Create Segment" onClose={() => setModal(null)}>
          <FormField label="Segment Name" required><TextInput value={segName} onChange={setSegName} placeholder="Enterprise" /></FormField>
          <FormField label="Segment Code" required hint="Uppercase letters, numbers, hyphens, underscores"><TextInput value={segCode} onChange={(v) => setSegCode(v.toUpperCase())} placeholder="ENT" /></FormField>
          <FormField label="Description" hint="Optional"><TextInput value={segDesc} onChange={setSegDesc} placeholder="Enterprise segment" /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreate} disabled={CREATE_DISABLED.segment}>{submitting ? "Creating…" : "Create Segment"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
      {modal === "role" && (
        <InlineModal title="Create Sales Role" onClose={() => setModal(null)}>
          <FormField label="Role Name" required><TextInput value={roleName} onChange={setRoleName} placeholder="Account Executive ENT" /></FormField>
          <FormField label="Role Code" required hint="Uppercase letters, numbers, hyphens, underscores"><TextInput value={roleCode} onChange={(v) => setRoleCode(v.toUpperCase())} placeholder="AE_ENT" /></FormField>
          <FormField label="Role Category" required><SelectInput value={roleCategory} onChange={setRoleCategory} options={ROLE_CATEGORIES_ORG} /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreate} disabled={CREATE_DISABLED.role}>{submitting ? "Creating…" : "Create Role"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
      {modal === "motion" && (
        <InlineModal title="Create Selling Motion" onClose={() => setModal(null)}>
          <FormField label="Motion Name" required><TextInput value={motionName} onChange={setMotionName} placeholder="Direct Sales" /></FormField>
          <FormField label="Motion Code" required hint="Uppercase letters and numbers only"><TextInput value={motionCode} onChange={(v) => setMotionCode(v.toUpperCase())} placeholder="DIRECT" /></FormField>
          <FormField label="Credit Split %" hint="Optional. Proportion of credit (0 to 1)"><TextInput value={creditSplitPct} onChange={setCreditSplitPct} placeholder="0.5" /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreate} disabled={CREATE_DISABLED.motion}>{submitting ? "Creating…" : "Create Selling Motion"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PlanWizardPanel
// ---------------------------------------------------------------------------

function PlanWizardPanel({ mode, period, wizardSteps }) {
  const { data: plansData, loading: plansLoading, error: plansError } = useFetch(`${API_BASE}/plans`);
  const plans = plansData?.data ?? [];
  const STATUS_TONE = { draft: "Draft", pending_approval: "Ready", approved: "Complete", superseded: "Draft", archived: "Draft" };

  return (
    <div className="w-full space-y-4">
      <Card title="Plan Wizard" action={
        <Button onClick={() => fetch(`${API_BASE}/plans`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ fiscal_period_id: period?.fiscal_period_id, plan_code: `PLAN_${Date.now()}`, plan_name: "New Plan", source_type: "wizard" }) }).then(() => window.location.reload())}>
          New Plan
        </Button>
      }>
        {plansLoading && <LoadingRow />}
        {plansError && <ErrorNote message="Unable to load plans." />}
        {!plansLoading && !plansError && plans.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No plans found. Create a plan to begin the wizard.</p>}
        <div className="mt-2 space-y-2">
          {plans.map((plan) => (
            <div key={plan.plan_id} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="grid gap-1 sm:grid-cols-3 flex-1">
                  <Field label="Plan" value={plan.plan_name} /><Field label="Code" value={plan.plan_code} /><Field label="Version" value={plan.version ?? plan.plan_version} />
                </div>
                <Pill tone={STATUS_TONE[plan.plan_status] ?? "Draft"}>{plan.plan_status?.replace(/_/g, " ")}</Pill>
              </div>
            </div>
          ))}
        </div>
      </Card>
      </div>
  );
}

// ---------------------------------------------------------------------------
// CalculationPanel — S402 Stage 16 (R-UI-04)
// Extends existing UI with completed_with_exclusions terminal state:
//   - Excluded positions panel (position, reason, hold reference)
//   - "Proceed to Statements with exclusions" — requires second comp admin (NN-36)
//   - "Initiate supplemental run" — with Scenario A/B/C classification
// Existing completed / failed / running states: unchanged.
//
// Audit Finding G (RESOLVED): statement_batches_router.py §15.2 populates
//   statement_output by iterating payout_summary scoped to calculation_run_id —
//   not active positions. Excluded positions have no payout_summary row for this
//   run, so they are excluded from the batch automatically. No UI guard needed.
//
// Backend fix required (tracked separately):
//   statement_batches_router.py §15.1 create_statement_batch queries
//   run_status = 'completed' only. Must add 'completed_with_exclusions' to that
//   IN clause before "Proceed to Statements" can successfully create the batch.
// ---------------------------------------------------------------------------

// Supplemental run scenario labels (DR-022 §8)
const SUPPLEMENTAL_SCENARIO_LABELS = {
  scenario_a: {
    label:       "Scenario A — First-time calculation",
    description: "No prior payout_summary exists for this position. This is a first-time run.",
    tone:        "Complete",
  },
  scenario_b: {
    label:       "Scenario B — Pre-certification amendment (DR-007)",
    description: "A prior payout_summary exists and Stage 19 (certification) has not completed. Requires DR-007 pre-cert amendment path.",
    tone:        "Warning",
  },
  scenario_c: {
    label:       "Scenario C — Post-certification amendment (DR-006)",
    description: "A prior payout_summary exists and Stage 19 is complete. Requires DR-006 post-cert amendment path.",
    tone:        "Blocked",
  },
};

// ExcludedPositionsPanel — shown when run_status = completed_with_exclusions
function ExcludedPositionsPanel({ runId, userId, onProceedToStatements }) {
  const { data: runDetail, loading, error } = useFetch(
    runId ? `${API_BASE}/calculation-runs/${runId}` : null
  );
  const excludedPositions = runDetail?.excluded_positions ?? [];
  const positionsExcluded = runDetail?.positions_excluded ?? excludedPositions.length;
  const positionsCalculated = runDetail?.positions_calculated ?? null;

  // NN-36: "Proceed to Statements with exclusions" requires a DIFFERENT comp admin
  // than whoever last interacted with this run (enforced server-side, surfaced here)
  const runCreatedBy = runDetail?.created_by ?? null;
  const isSelfApprovalBlocked = runCreatedBy && String(runCreatedBy) === String(userId ?? "");

  // Audit log write for exclusions authorized (audit_log: calculation.exclusions.authorized)
  const [authorizing, setAuthorizing]     = useState(false);
  const [authorizeError, setAuthorizeError] = useState(null);

  async function handleProceedToStatements() {
    if (!runId) return;
    setAuthorizing(true);
    setAuthorizeError(null);
    try {
      // Write audit event before navigating to Statements
      await fetch(`${API_BASE}/audit-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          event_name:  "calculation.exclusions.authorized",
          entity_type: "calculation_run",
          entity_id:   runId,
        }),
      });
      // Audit Finding G resolved: router iterates payout_summary (correct).
      // Navigate to Statements — batch creation there will fail with
      // NO_COMPLETED_FINAL_RUN (422) until statement_batches_router.py §15.1
      // adds 'completed_with_exclusions' to its run_status filter.
      // That backend fix is tracked separately; the UI will surface the 422 error.
      if (onProceedToStatements) onProceedToStatements();
      else window.location.href = "/admin/statements";
    } catch (e) {
      setAuthorizeError(e.message ?? "Failed to record authorization.");
    } finally {
      setAuthorizing(false);
    }
  }

  // Supplemental run initiation
  const [supplementalModal, setSupplementalModal] = useState(false);
  const [scenarioMap, setScenarioMap]             = useState({}); // position_id → scenario_x
  const [scenarioLoading, setScenarioLoading]     = useState(false);
  const [supplementalError, setSupplementalError] = useState(null);
  const [supplementalConfirmed, setSupplementalConfirmed] = useState(false);

  async function loadScenarios() {
    if (!runId) return;
    setScenarioLoading(true);
    setSupplementalError(null);
    try {
      // Classification is returned by the run detail endpoint (DR-022 §8 — synchronous)
      // Fall back to run detail excluded positions if classification not yet present
      const map = {};
      (runDetail?.excluded_positions ?? []).forEach((pos) => {
        map[pos.position_id] = pos.supplemental_scenario ?? "scenario_a";
      });
      setScenarioMap(map);
    } catch (e) {
      setSupplementalError(e.message);
    } finally {
      setScenarioLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Post-run exclusions summary strip */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
        <CircleSlash className="h-5 w-5 shrink-0 text-[rgb(var(--color-warning))]" />
        <div className="flex-1">
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Run completed with {positionsExcluded} excluded position{positionsExcluded !== 1 ? "s" : ""}
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {positionsCalculated !== null && `${positionsCalculated} calculated · `}
            {positionsExcluded} excluded — review the list below before proceeding to statements.
          </p>
        </div>
      </div>

      {/* Excluded positions table */}
      {loading && <LoadingRow />}
      {error && <ErrorNote message="Unable to load run details." />}
      {!loading && excludedPositions.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full text-tok-sm border-collapse">
            <thead>
              <tr>
                {["Position", "Role", "Reason", "Hold reference"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))]">
              {excludedPositions.map((pos) => (
                <tr key={pos.position_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                  <td className="px-3 py-2 font-medium text-[rgb(var(--color-text))]">
                    {pos.full_name ?? pos.position_id}
                  </td>
                  <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">
                    {pos.role_code ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">
                    {holdReasonLabel(pos.exclusion_reason_code ?? pos.reason_code)}
                  </td>
                  <td className="px-3 py-2 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                    {pos.position_hold_id
                      ? String(pos.position_hold_id).slice(0, 8) + "…"
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !error && excludedPositions.length === 0 && positionsExcluded > 0 && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Excluded position detail not available in this response — check calculation run detail endpoint.
        </p>
      )}

      {authorizeError && <ErrorNote message={authorizeError} />}

      {/* Action row: Proceed to Statements + Supplemental Run */}
      <div className="flex flex-wrap items-start gap-3">
        {/* Proceed to Statements — NN-36: blocked if self-approval */}
        <div className="flex flex-col gap-1">
          {isSelfApprovalBlocked ? (
            <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <Info className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
              <div>
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Second comp admin required to proceed
                </p>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Actor separation (NN-36): the comp admin who initiated this run
                  cannot also authorize proceeding to statements with exclusions.
                  A different comp admin must click "Proceed to Statements."
                </p>
              </div>
            </div>
          ) : (
            <>
              <Button
                onClick={handleProceedToStatements}
                disabled={authorizing}
              >
                <ArrowRight className="mr-1.5 h-4 w-4" />
                {authorizing ? "Recording authorization…" : "Proceed to Statements with exclusions"}
              </Button>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Authorizes statement generation for the calculated population. Records
                <code className="mx-0.5 font-mono">calculation.exclusions.authorized</code>
                in the audit log.
              </p>
            </>
          )}
        </div>

        {/* Initiate supplemental run */}
        <Button
          variant="secondary"
          onClick={() => { loadScenarios(); setSupplementalModal(true); setSupplementalConfirmed(false); setSupplementalError(null); }}
        >
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Initiate supplemental run
        </Button>
      </div>

      {/* Supplemental run modal — shows scenario classification per DR-022 §8 */}
      {supplementalModal && (
        <InlineModal
          title="Initiate Supplemental Run"
          onClose={() => setSupplementalModal(false)}
        >
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            A supplemental run calculates excluded positions separately. Each position
            is classified by scenario before you confirm.
          </p>

          {scenarioLoading && <LoadingRow />}
          {supplementalError && <ErrorNote message={supplementalError} />}

          {!scenarioLoading && excludedPositions.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {excludedPositions.map((pos) => {
                const scenario = scenarioMap[pos.position_id] ?? "scenario_a";
                const meta     = SUPPLEMENTAL_SCENARIO_LABELS[scenario] ?? SUPPLEMENTAL_SCENARIO_LABELS.scenario_a;
                return (
                  <div key={pos.position_id} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                          {pos.full_name ?? pos.position_id}
                        </p>
                        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {pos.role_code ?? ""}
                        </p>
                      </div>
                      <Pill tone={meta.tone}>{scenario.replace("_", " ").toUpperCase()}</Pill>
                    </div>
                    <p className="mt-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {meta.description}
                    </p>
                    {(scenario === "scenario_b" || scenario === "scenario_c") && (
                      <p className="mt-1 text-tok-xs font-medium text-[rgb(var(--color-warning))]">
                        Amendment path required — the supplemental run will route through
                        {scenario === "scenario_b" ? " DR-007 (pre-cert)" : " DR-006 (post-cert)"}.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!scenarioLoading && excludedPositions.length === 0 && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              No excluded positions with detail available. A supplemental run can still be initiated
              if there are positions in NOT_CALCULATED state for this period.
            </p>
          )}

          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2">
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={supplementalConfirmed}
                onChange={(e) => setSupplementalConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                I have reviewed the scenario classification above and confirm that any required
                amendment paths (DR-006, DR-007) are understood before proceeding.
              </span>
            </label>
          </div>

          {supplementalError && <ErrorNote message={supplementalError} />}

          <div className="flex gap-2 pt-1">
            <Button
              disabled={!supplementalConfirmed || scenarioLoading}
              onClick={() => {
                // Supplemental run initiation navigates to Readiness to create a new snapshot
                // for the excluded population (scope governance — not implemented as a single click)
                setSupplementalModal(false);
                window.location.href = "/admin/readiness";
              }}
            >
              Confirm and go to Readiness
            </Button>
            <Button variant="secondary" onClick={() => setSupplementalModal(false)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
    </div>
  );
}

function CalculationPanel({ mode, period }) {
  const { user }  = useAuth();
  const userId    = user?.user_id ?? null;
  const periodId  = period?.fiscal_period_id;

  const [runsRefreshKey, setRunsRefreshKey] = useState(0);
  const { data: runsData, loading: runsLoading, error: runsError } = useFetch(
    `${API_BASE}/calculation-runs?_k=${runsRefreshKey}`
  );
  const { data: scenarioData } = useFetch(periodId ? `${API_BASE}/scenario-sets?fiscal_period_id=${periodId}` : null);
  const runs         = runsData?.calculation_runs ?? [];
  const scenarioSets = scenarioData?.scenario_sets ?? [];

  const [selectedScenarioSetId, setSelectedScenarioSetId] = useState("");
  const [runType, setRunType]         = useState("preview");
  const [runLabel, setRunLabel]       = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [runError, setRunError]       = useState(null);
  const [pollingRunId, setPollingRunId]   = useState(null);   // S580: in-flight run polling
  const [pollingStatus, setPollingStatus] = useState(null);

  useEffect(() => {
    if (scenarioSets.length > 0 && !selectedScenarioSetId) {
      const active = scenarioSets.find((s) => s.is_active) ?? scenarioSets[0];
      setSelectedScenarioSetId(String(active.scenario_set_id));
    }
  }, [scenarioSets]);

  // S580: poll in-flight run until terminal status
  useEffect(() => {
    if (!pollingRunId) return;
    const TERMINAL = new Set(["completed", "completed_with_exclusions", "failed"]);
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/calculation-runs/${pollingRunId}`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        const status = d?.run_status ?? d?.calculation_run?.run_status;
        if (!status) return;
        setPollingStatus(status);
        if (TERMINAL.has(status)) {
          clearInterval(interval);
          setPollingRunId(null);
          if (status === "failed") {
            setRunError(`Run ${pollingRunId} failed.`);
          } else {
            setRunsRefreshKey((k) => k + 1);
          }
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [pollingRunId]);

  async function handleRunCalculation() {
    if (!periodId) return;  // S572: scenario_set_id optional
    setSubmitting(true);
    setRunError(null);
    try {
      const trimmedLabel = runLabel.trim();
      const res = await fetch(`${API_BASE}/calculation-runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fiscal_period_id: periodId, run_type: runType, ...(selectedScenarioSetId ? { scenario_set_id: selectedScenarioSetId } : {}), ...(runType === "final" ? { confirm_final: true } : {}), ...(trimmedLabel ? { run_label: trimmedLabel } : {}) }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body?.error?.code === "RUN_ALREADY_RUNNING") {
        const inFlightId = body?.error?.detail?.calculation_run_id;
        if (inFlightId) { setPollingRunId(inFlightId); setPollingStatus("running"); }
        else { setRunError("A run is already in progress for this period."); }
      } else if (!res.ok) {
        setRunError(body?.error?.message ?? `Error ${res.status}`);
      } else {
        setRunsRefreshKey((k) => k + 1);
      }
    } catch (e) { setRunError(e.message); }
    finally { setSubmitting(false); }
  }

  // S402: extended status tone (adds completed_with_exclusions)
  const RUN_STATUS_TONE = {
    completed:                "Complete",
    completed_with_exclusions: "Warning",
    running:                  "Ready",
    failed:                   "Blocked",
    queued:                   "Draft",
    pending:                  "Draft",
  };

  // Identify most recent run — may be completed_with_exclusions
  const latestRun = runs[0] ?? null;
  const latestIsExclusions = latestRun?.run_status === "completed_with_exclusions";

  return (
    <div className="w-full space-y-4">
        {/* Standard run trigger */}
        <Card title="Calculation">
          <div className="mb-4 space-y-3 rounded-xl border border-[rgb(var(--color-border))] p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Scenario Set</label>
                <select value={selectedScenarioSetId} onChange={(e) => setSelectedScenarioSetId(e.target.value)} className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none">
                  {scenarioSets.length === 0 && <option value="">No scenario sets — Finance must create one in Finance View</option>}
                  {scenarioSets.map((s) => <option key={s.scenario_set_id} value={String(s.scenario_set_id)}>{s.scenario_set_name ?? s.scenario_set_code}</option>)}
                </select>
                {scenarioSets.length === 0 && (
                  <p className="mt-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    Base calculation proceeds without a scenario set. To model P50/P75/P90 exposure, Finance must create and submit a scenario set — comp admin approves it in Finance View before it appears here.
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Run Type</label>
                <select value={runType} onChange={(e) => setRunType(e.target.value)} className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none">
                  <option value="preview">Preview</option><option value="final">Final</option>
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Run Label <span className="font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={runLabel}
                onChange={(e) => setRunLabel(e.target.value)}
                maxLength={120}
                placeholder="e.g. Pre-close sanity check, with retro adj applied"
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
              <p className="mt-0.5 text-right text-tok-xs text-[rgb(var(--color-text-muted))]">{runLabel.length}/120</p>
            </div>

            {runError && <ErrorNote message={runError} />}

            {/* S580: in-flight run polling banner */}
            {pollingRunId && (
              <div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-accent))]/30 bg-[rgb(var(--color-accent))]/6 px-4 py-3">
                <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-[rgb(var(--color-accent))]" />
                <div className="flex-1 min-w-0">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-accent))]">
                    Run in progress — {pollingStatus ?? "running"}
                  </p>
                  <p className="mt-0.5 text-tok-xs font-mono text-[rgb(var(--color-text-muted))] truncate">{pollingRunId}</p>
                  <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">Checking every 5 s — will redirect on completion.</p>
                </div>
              </div>
            )}

            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              To run with a population snapshot (excluding specific positions), use the
              <strong> Readiness</strong> screen (Stage 15) — create and approve a snapshot there first,
              then Run Calculation from that screen.
            </p>

            {/* S602 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — gate Run Calculation on period status */}
            {period?.period_status !== "open" ? (
              <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
                <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
                <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                  Period is <strong>{period?.period_status ?? "not open"}</strong> — calculation runs are not available for this period.
                  Switch to an open period to run calculations.
                </p>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button onClick={handleRunCalculation} disabled={submitting || !periodId || !!pollingRunId}>
                  {submitting ? "Submitting…" : pollingRunId ? "Run in progress…" : "Run Calculation"}
                </Button>
              </div>
            )}
          </div>

          {/* Run history — S579 OI-UI-TABLE-CONSISTENCY */}
          <CalcRunTable runs={runs} loading={runsLoading} error={runsError} statusTone={RUN_STATUS_TONE} />
        </Card>

        {/* S402 R-UI-04: completed_with_exclusions post-run panel */}
        {latestIsExclusions && (
          <Card
            title="Post-Run Exclusion Review"
            action={<Pill tone="Warning">Action required</Pill>}
          >
            <ExcludedPositionsPanel
              runId={latestRun.calculation_run_id}
              userId={userId}
              onProceedToStatements={() => { window.location.href = "/admin/statements"; }}
            />
          </Card>
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanApprovalPanel
// S540: Extracted to CompensationPoliciesWorkspace.jsx (Approvals tab).
// Route /admin/plan-approval now redirects to /admin/compensation?tab=approvals.
// Function body retained below for parse integrity — it is no longer rendered
// from AdminPanel. Safe to delete in a follow-up cleanup pass.
// Original: OI-S387-01 — plan component approval queue + AmendmentBeginModal wire.
// ---------------------------------------------------------------------------

function PlanApprovalPanel({ mode, period, wizardSessionId }) {
  const fiscalPeriodId = period?.fiscal_period_id ?? null;

  // ── Plan components pending approval ──────────────────────────────────────
  const { data: compData, loading: compLoading, error: compError } = useFetch(`${API_BASE}/plan-components`);
  const allComponents = compData?.plan_components ?? compData?.data ?? [];
  const pendingComponents = allComponents.filter((c) => !c.approved_by && !c.approved_at && planMap[String(c.plan_id)]?.plan_status !== 'approved');

  // Plan + role/segment lookup for display
  const { data: plansData }    = useFetch(`${API_BASE}/plans`);
  const { data: rolesData }    = useFetch(`${API_BASE}/roles`);
  const { data: segmentsData } = useFetch(`${API_BASE}/segments`);
  const allPlans    = plansData?.plans    ?? plansData?.data    ?? [];
  const allRoles    = rolesData?.roles    ?? rolesData?.data    ?? [];
  const allSegments = segmentsData?.segments ?? segmentsData?.data ?? [];

  const planMap    = Object.fromEntries(allPlans.map((p) => [String(p.plan_id), p]));
  const roleMap    = Object.fromEntries(allRoles.map((r) => [String(r.role_id), r]));
  const segmentMap = Object.fromEntries(allSegments.map((s) => [String(s.segment_id), s]));

  // Amendment modal state — one component approved at a time
  const [amendOpen,        setAmendOpen]        = useState(false);
  const [amendingCompId,   setAmendingCompId]   = useState(null);
  const [approvingCompId,  setApprovingCompId]  = useState(null); // spinner per row
  const [compError2,       setCompError2]       = useState('');
  const [compSuccess,      setCompSuccess]       = useState('');

  // Refresh key so component list re-fetches after approve
  const [compRefreshKey, setCompRefreshKey] = useState(0);
  const { data: compDataFresh } = useFetch(
    compRefreshKey > 0 ? `${API_BASE}/plan-components?_k=${compRefreshKey}` : null
  );

  // Merge fresh data after approve
  const [localComponents, setLocalComponents] = useState(null);
  useEffect(() => {
    if (compDataFresh) {
      const fresh = compDataFresh?.plan_components ?? compDataFresh?.data ?? [];
      setLocalComponents(fresh);
    }
  }, [compDataFresh]);

  const displayComponents = (localComponents ?? allComponents).filter(
    (c) => !c.approved_by && !c.approved_at
  );

  function handleApproveComponentClick(compId) {
    if (!fiscalPeriodId) { setCompError2('No active fiscal period found. Cannot open amendment.'); return; }
    setCompError2('');
    setCompSuccess('');
    setAmendingCompId(compId);
    setAmendOpen(true);
  }

  async function handleAmendmentBegin(amendmentId) {
    setAmendOpen(false);
    const compId = amendingCompId;
    setAmendingCompId(null);
    setApprovingCompId(compId);
    setCompError2('');
    try {
      const body = { amendment_id: amendmentId, fiscal_period_id: fiscalPeriodId };
      const res = await fetch(`${API_BASE}/plan-components/${compId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.error?.message ?? b?.detail?.error?.message ?? `Error ${res.status}`);
      }
      // Commit amendment
      await fetch(`${API_BASE}/amendments/${amendmentId}/commit`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      }).catch(() => {});
      setCompSuccess(`Plan component approved successfully. It is now engine-eligible.`);
      setCompRefreshKey((k) => k + 1);
    } catch (e) {
      setCompError2(e.message ?? 'Unable to approve plan component.');
    } finally {
      setApprovingCompId(null);
    }
  }

  // ── Plan-level approval ───────────────────────────────────────────────────
  const { data: planApprovalData, loading: planLoading, error: planError } = useFetch(
    `${API_BASE}/plans?plan_status=pending_approval`
  );
  const pendingPlans = planApprovalData?.data ?? planApprovalData?.plans ?? [];

  const [rejectingPlanId,  setRejectingPlanId]  = useState(null);
  const [rejectionReason,  setRejectionReason]  = useState('');
  const [planActionError,  setPlanActionError]  = useState('');
  const [planActioning,    setPlanActioning]    = useState(false);

  async function handleApprovePlan(planId) {
    setPlanActioning(true); setPlanActionError('');
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/approve`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      window.location.reload();
    } catch (e) { setPlanActionError(e.message); } finally { setPlanActioning(false); }
  }

  async function handleRejectPlan(planId) {
    if (!rejectionReason.trim()) { setPlanActionError('Rejection reason is required.'); return; }
    setPlanActioning(true); setPlanActionError('');
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/reject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejection_reason: rejectionReason.trim() }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      window.location.reload();
    } catch (e) { setPlanActionError(e.message); } finally { setPlanActioning(false); }
  }

  return (
    <>
      {/* Amendment Begin Modal — for component approval */}
      <AmendmentBeginModal
        open={amendOpen}
        entityType="plan_component"
        entityId={amendingCompId ? String(amendingCompId) : null}
        fiscalPeriodId={fiscalPeriodId}
        wizardSessionId={wizardSessionId ?? null}
        onBegin={handleAmendmentBegin}
        onCancel={() => { setAmendOpen(false); setAmendingCompId(null); }}
      />

      <div className="w-full space-y-4">

        {/* ── Section 1: Plan Components Pending Approval ── */}
        <div className="w-full space-y-4">
          <Card
            title="Plan Components Pending Approval"

            action={
              pendingComponents.length > 0
                ? <Pill tone="Warning">{displayComponents.length} pending</Pill>
                : null
            }
          >
            {compLoading && <LoadingRow />}
            {compError && <ErrorNote message="Unable to load plan components." />}
            {compSuccess && (
              <div className="mb-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
                {compSuccess}
              </div>
            )}
            {compError2 && <ErrorNote message={compError2} />}
            {!compLoading && !compError && displayComponents.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No plan components pending approval.
              </p>
            )}
            <div className="mt-2 space-y-3">
              {displayComponents.map((comp) => {
                const plan    = planMap[String(comp.plan_id)];
                const role    = roleMap[String(comp.role_id)];
                const segment = segmentMap[String(comp.segment_id)];
                const isActioning = approvingCompId === comp.plan_component_id;
                return (
                  <div
                    key={comp.plan_component_id}
                    className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4"
                  >
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      <Field label="Plan"    value={plan?.plan_name ?? plan?.plan_code ?? `Plan ${comp.plan_id}`} />
                      <Field label="Role"    value={role?.role_name ?? role?.role_code ?? `Role ${comp.role_id}`} />
                      <Field label="Segment" value={segment?.segment_name ?? segment?.segment_code ?? `Segment ${comp.segment_id}`} />
                      <Field label="Effective" value={comp.effective_date ?? '—'} />
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      <Field label="OTE Range"   value={`${comp.ote_min?.toLocaleString() ?? '—'} – ${comp.ote_max?.toLocaleString() ?? '—'}`} />
                      <Field label="Base / Var"  value={`${Math.round((comp.base_pct ?? 0) * 100)}% / ${Math.round((comp.variable_pct ?? 0) * 100)}%`} />
                      <Field label="Weights"     value={`ARR ${comp.arr_weight ?? 0} · MBO ${comp.mbo_weight ?? 0} · NPS ${comp.nps_weight ?? 0}`} />
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Button
                        onClick={() => handleApproveComponentClick(comp.plan_component_id)}
                        disabled={isActioning || !fiscalPeriodId}
                      >
                        {isActioning ? 'Approving…' : 'Approve component'}
                      </Button>
                      {!fiscalPeriodId && (
                        <span className="text-tok-xs text-[rgb(var(--color-warning))]">
                          No active fiscal period — cannot approve
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
</div>

        {/* ── Section 2: Plans Pending Approval ── */}
        <div className="w-full space-y-4">
          <Card
            title="Plans Pending Approval"

            action={pendingPlans.length > 0 ? <Pill tone="Warning">{pendingPlans.length} pending</Pill> : null}
          >
            {planLoading && <LoadingRow />}
            {planError && <ErrorNote message="Unable to load plans." />}
            {planActionError && <ErrorNote message={planActionError} />}
            {!planLoading && !planError && pendingPlans.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No plans pending approval. Once a plan is submitted it will appear here.
              </p>
            )}
            <div className="mt-2 space-y-3">
              {pendingPlans.map((plan) => (
                <div
                  key={plan.plan_id}
                  className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4"
                >
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Field label="Plan"    value={plan.plan_name} />
                    <Field label="Code"    value={plan.plan_code} />
                    <Field label="Version" value={plan.version ?? plan.plan_version ?? '—'} />
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={() => handleApprovePlan(plan.plan_id)}
                      disabled={planActioning}
                    >
                      Approve plan
                    </Button>
                    {rejectingPlanId === plan.plan_id ? (
                      <>
                        <input
                          type="text"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Rejection reason"
                          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
                        />
                        <Button variant="danger" onClick={() => handleRejectPlan(plan.plan_id)} disabled={planActioning}>
                          Confirm reject
                        </Button>
                        <Button variant="secondary" onClick={() => { setRejectingPlanId(null); setRejectionReason(''); }}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button variant="secondary" onClick={() => { setRejectingPlanId(plan.plan_id); setRejectionReason(''); setPlanActionError(''); }}>
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
</div>

      </div>
    </>
  );
}

// ============================================================================
// SalesRolesPanel
// ============================================================================

function SalesRolesPanel({ mode }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, loading, error } = useFetch(`${API_BASE}/positions?_k=${refreshKey}`);
  const { data: rolesData }    = useFetch(`${API_BASE}/roles?_k=${refreshKey}`);
  const { data: motionsData }  = useFetch(`${API_BASE}/selling-motions?_k=${refreshKey}`);
  const positions      = data?.data       ?? [];
  const roles          = rolesData?.data  ?? [];
  const sellingMotions = motionsData?.data ?? [];

  const roleMap = {};
  positions.forEach((p) => {
    const key = p.role_code ?? String(p.role_id ?? "unknown");
    if (!roleMap[key]) roleMap[key] = { role_name: p.role_name ?? key, role_category: p.role_category ?? "—", count: 0, segments: new Set() };
    roleMap[key].count += 1;
    if (p.segment_code) roleMap[key].segments.add(p.segment_code);
  });
  const roleSummary = Object.values(roleMap);

  const [modal, setModal]             = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [roleName,     setRoleName]     = useState("");
  const [roleCode,     setRoleCode]     = useState("");
  const [roleCategory, setRoleCategory] = useState("AE");
  const [motionName,     setMotionName]     = useState("");
  const [motionCode,     setMotionCode]     = useState("");
  const [creditSplitPct, setCreditSplitPct] = useState("");

  const ROLE_CATEGORIES = [
    { value: "AE", label: "Account Executive (AE)" }, { value: "CSM", label: "Customer Success Manager (CSM)" },
    { value: "SA", label: "Solutions Architect (SA)" }, { value: "SPECIALIST", label: "Specialist" },
  ];

  function resetRole()   { setRoleName(""); setRoleCode(""); setRoleCategory("AE"); setSubmitError(null); }
  function resetMotion() { setMotionName(""); setMotionCode(""); setCreditSplitPct(""); setSubmitError(null); }

  async function handleCreateRole() {
    if (!roleName.trim() || !roleCode.trim()) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/roles`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ role_name: roleName.trim(), role_code: roleCode.trim().toUpperCase(), role_category: roleCategory }) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      setModal(null); resetRole(); setRefreshKey((k) => k + 1);
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  async function handleCreateSellingMotion() {
    if (!motionName.trim() || !motionCode.trim()) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const body = { motion_name: motionName.trim(), motion_code: motionCode.trim().toUpperCase() };
      if (creditSplitPct) body.credit_split_pct = parseFloat(creditSplitPct);
      const res = await fetch(`${API_BASE}/selling-motions`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b?.error?.message ?? `Error ${res.status}`); }
      setModal(null); resetMotion(); setRefreshKey((k) => k + 1);
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  return (
    <>
      <div className="w-full space-y-4">
        <Card title="Sales Roles" action={
          <div className="flex items-center gap-2">
            {roleSummary.length > 0 && <Pill tone="Ready">{roleSummary.length} roles</Pill>}
            <Button variant="secondary" onClick={() => { resetMotion(); setModal("selling-motion"); }}>New Selling Motion</Button>
            <Button onClick={() => { resetRole(); setModal("role"); }}>New Role</Button>
          </div>
        }>
          {loading && <LoadingRow />}
          {error && <ErrorNote message="Unable to load role data." />}
          {!loading && !error && roleSummary.length === 0 && roles.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No roles found. Create roles or load a position CSV to populate.</p>}
          {roleSummary.length > 0 && (
            <div className="space-y-2">
              <p className="mb-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Active Roles (from positions)</p>
              {roleSummary.map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                  <div className="grid flex-1 gap-1 sm:grid-cols-3">
                    <Field label="Role" value={r.role_name} /><Field label="Category" value={r.role_category} /><Field label="Segments" value={r.segments.size > 0 ? [...r.segments].join(", ") : "—"} />
                  </div>
                  <Pill tone="Draft">{r.count} {r.count === 1 ? "rep" : "reps"}</Pill>
                </div>
              ))}
            </div>
          )}
          {roles.length > 0 && (
            <div className="mt-4 border-t border-[rgb(var(--color-border))] pt-4">
              <p className="mb-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Configured Roles ({roles.length})</p>
              <div className="space-y-1.5">
                {roles.map((r) => (
                  <div key={r.role_id} className="flex items-center justify-between rounded-lg border border-[rgb(var(--color-border))] px-3 py-2">
                    <span className="text-tok-sm text-[rgb(var(--color-text))]">{r.role_name}</span>
                    <div className="flex items-center gap-2"><span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">{r.role_code}</span><Pill tone="Draft">{r.role_category}</Pill></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sellingMotions.length > 0 && (
            <div className="mt-4 border-t border-[rgb(var(--color-border))] pt-4">
              <p className="mb-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Selling Motions ({sellingMotions.length})</p>
              <div className="space-y-1.5">
                {sellingMotions.map((sm) => (
                  <div key={sm.selling_motion_id} className="flex items-center justify-between rounded-lg border border-[rgb(var(--color-border))] px-3 py-2">
                    <span className="text-tok-sm text-[rgb(var(--color-text))]">{sm.motion_name}</span>
                    <span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">{sm.motion_code}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
        </div>

      {modal === "role" && (
        <InlineModal title="Create Sales Role" onClose={() => setModal(null)}>
          <FormField label="Role Name" required><TextInput value={roleName} onChange={setRoleName} placeholder="Enterprise Account Executive" /></FormField>
          <FormField label="Role Code" required hint="Uppercase letters, numbers, hyphens, underscores"><TextInput value={roleCode} onChange={(v) => setRoleCode(v.toUpperCase())} placeholder="ENT-AE" /></FormField>
          <FormField label="Role Category" required><SelectInput value={roleCategory} onChange={setRoleCategory} options={ROLE_CATEGORIES} /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreateRole} disabled={submitting || !roleName.trim() || !roleCode.trim()}>{submitting ? "Creating…" : "Create Role"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}

      {modal === "selling-motion" && (
        <InlineModal title="Create Selling Motion" onClose={() => setModal(null)}>
          <FormField label="Motion Name" required><TextInput value={motionName} onChange={setMotionName} placeholder="Direct Sales" /></FormField>
          <FormField label="Motion Code" required hint="Uppercase letters and numbers only"><TextInput value={motionCode} onChange={(v) => setMotionCode(v.toUpperCase())} placeholder="DIRECT" /></FormField>
          <FormField label="Credit Split %" hint="Optional. Proportion of credit (0 to 1)"><TextInput value={creditSplitPct} onChange={setCreditSplitPct} placeholder="0.5" /></FormField>
          {submitError && <ErrorNote message={submitError} />}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleCreateSellingMotion} disabled={submitting || !motionName.trim() || !motionCode.trim()}>{submitting ? "Creating…" : "Create Selling Motion"}</Button>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
          </div>
        </InlineModal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PositionLoadPanel — S402 Surface 1 (SETUP-07, R-UI-01)
// Adds: "Assess Positions" button (explicit trigger only — NEVER fires on visit)
//       eligible / ineligible count summary after assessment
//       Gate verdict: Proceed enabled when eligible count > 0
// Does NOT show: holds, snapshot creation, approval workflow (those are Surface 2)
// ---------------------------------------------------------------------------

function PositionLoadPanel({ mode, period }) {
  const periodId = period?.fiscal_period_id;

  // Import history — refreshKey triggers refetch after a successful upload
  const [logsKey, setLogsKey] = useState(0);
  const { data, loading, error } = useFetch(`${API_BASE}/import-logs?file_type=positions&_k=${logsKey}`);
  const logs = data?.data ?? [];
  const STATUS_TONE = { loaded: "Complete", failed: "Blocked", pending: "Draft" };

  // CA-01: CSV upload state — matches PositionDataReview pattern exactly
  // file_type = 'position' (singular) — matches onboarding surface and import_service.py
  const POSITION_REQUIRED_COLS = ['employee_id', 'full_name', 'role_code', 'segment_code', 'hire_date', 'is_active'];

  const [csvFile, setCsvFile]                         = useState(null);
  const [csvDryResult, setCsvDryResult]               = useState(null);
  const [csvCommitted, setCsvCommitted]               = useState(false);
  const [csvImporting, setCsvImporting]               = useState(false);
  const [csvError, setCsvError]                       = useState('');

  // Column-mapping pre-flight — idempotent upsert, same mappings as PositionDataReview
  async function ensurePositionColumnMapping() {
    const mappings = [
      { source_column_name: 'employee_id',        target_column_name: 'employee_id',        is_required: true  },
      { source_column_name: 'full_name',           target_column_name: 'full_name',          is_required: true  },
      { source_column_name: 'role_code',           target_column_name: 'role_code',          is_required: true  },
      { source_column_name: 'segment_code',        target_column_name: 'segment_code',       is_required: true  },
      { source_column_name: 'hire_date',           target_column_name: 'hire_date',          is_required: true  },
      { source_column_name: 'is_active',           target_column_name: 'is_active',          is_required: true  },
      { source_column_name: 'ramp_start_date',     target_column_name: 'ramp_start_date',    is_required: false },
      { source_column_name: 'territory_code',      target_column_name: 'territory_code',     is_required: false },
      { source_column_name: 'manager_employee_id', target_column_name: 'manager_employee_id',is_required: false },
      { source_column_name: 'email',               target_column_name: 'email',              is_required: false },
      { source_column_name: 'country_code',        target_column_name: 'country_code',       is_required: false },
      { source_column_name: 'business_unit_code',  target_column_name: 'business_unit_code', is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'position', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed position column mapping.');
  }

  async function runCsvValidate(file) {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = POSITION_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const warnings = [];
      if (!headers.includes('ramp_start_date'))     warnings.push('ramp_start_date absent — ramp will not be set.');
      if (!headers.includes('territory_code'))      warnings.push('territory_code absent — territory will not be set.');
      if (!headers.includes('manager_employee_id')) warnings.push('manager_employee_id absent — manager will not be linked.');
      if (!headers.includes('email'))               warnings.push('email absent — email will not be set.');
      setCsvDryResult({ rows: lines.length - 1, warnings, errors: [] });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvImporting(false); }
  }

  async function runCsvCommit(file) {
    if (!file || !csvDryResult) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      await ensurePositionColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'position');
      // S598: use position-specific endpoint for lifecycle-aware responses
      const res = await fetch(`${API_BASE}/positions/import`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) {
        // 3d — period post-final or all-rejected
        const detail = json?.error?.detail ?? '';
        if (detail.includes('PERIOD_POST_FINAL_LOCKED') || json?.error?.code === 'IMPORT_ALL_ROWS_REJECTED') {
          setCsvError('This period has been finalised. Classification and financial changes are locked. Use the amendment process to make corrections.');
        } else {
          setCsvError(json?.error?.message ?? 'All rows rejected.');
        }
        return;
      }
      if (!res.ok) { setCsvError(json?.error?.message ?? json?.message ?? json?.detail ?? `Error ${res.status}`); return; }
      if (res.status === 207) {
        // Partial accept — show summary with lifecycle error codes
        const rejected = json.rows_rejected ?? 0;
        const rowResults = json.row_results ?? [];
        const hasImmutable = rowResults.some(r => r.error_code === 'HIRE_DATE_IMMUTABLE' || r.error_code === 'RAMP_DATE_IMMUTABLE');
        const hasPostFinal = rowResults.some(r => r.error_code === 'PERIOD_POST_FINAL_LOCKED');
        let msg = `Partial load — ${rejected} row(s) rejected.`;
        if (hasImmutable) msg += ' Some rows were rejected because hire date or ramp start date cannot be changed via re-import.';
        if (hasPostFinal) msg += ' Some rows were rejected because this period has been finalised — use the amendment process.';
        setCsvError(msg);
        setCsvCommitted(true);
        setLogsKey((k) => k + 1);
        return;
      }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      setLogsKey((k) => k + 1);
    } catch (e) { setCsvError(e.message || 'Commit failed.'); }
    finally { setCsvImporting(false); }
  }

  // S402: Position readiness summary — reads persisted records (does NOT trigger assessment)
  // Only fetched after user explicitly assesses or when data already exists
  const [readinessKey, setReadinessKey] = useState(0);
  const [readinessFetched, setReadinessFetched] = useState(false);
  const { data: readinessData, loading: readinessLoading } = useFetch(
    periodId && readinessFetched
      ? `${API_BASE}/readiness/positions?fiscal_period_id=${periodId}&limit=1&_k=${readinessKey}`
      : null
  );

  // Assessment trigger state
  const [assessing, setAssessing]       = useState(false);
  const [assessResult, setAssessResult] = useState(null); // { assessed, eligible, ineligible }
  const [assessError, setAssessError]   = useState(null);

  const totalEligible   = assessResult?.eligible   ?? readinessData?.total_eligible   ?? null;
  const totalIneligible = assessResult?.ineligible ?? readinessData?.total_ineligible ?? null;
  const hasResults = totalEligible !== null;

  // SPEC: R-UI-01 — explicit trigger ONLY. Never fires on mount or route visit.
  async function handleAssess() {
    if (!periodId) return;
    setAssessing(true);
    setAssessError(null);
    setAssessResult(null);
    try {
      const res = await fetch(`${API_BASE}/readiness/positions/assess`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fiscal_period_id: periodId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? body?.detail ?? `Error ${res.status}`);
      setAssessResult({
        assessed:   body.assessed   ?? 0,
        eligible:   body.eligible   ?? 0,
        ineligible: body.ineligible ?? 0,
      });
      setReadinessFetched(true);
      setReadinessKey((k) => k + 1);
    } catch (e) {
      setAssessError(e.message ?? "Assessment failed.");
    } finally {
      setAssessing(false);
    }
  }

  const eligibleCount   = totalEligible   ?? 0;
  const ineligibleCount = totalIneligible ?? 0;
  const proceedEnabled  = hasResults && eligibleCount > 0 && !assessing;

  return (
    <div className="w-full space-y-4">
      <div className="space-y-4">

        {/* S402 Surface 1 — Position Eligibility Assessment */}
        <Card
          title="Position Eligibility"
          action={
            <div className="flex items-center gap-2">
              {hasResults && (
                <Pill tone={eligibleCount > 0 ? "Ready" : "Blocked"}>
                  {eligibleCount} eligible · {ineligibleCount} ineligible
                </Pill>
              )}
            </div>
          }
        >
          <p className="mb-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Run a per-position eligibility assessment to identify which positions can be included
            in a calculation run. Assessment checks 12 data-readiness dimensions for each active position.
          </p>

          {!periodId && (
            <div className="mb-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-warning))]">
              No open fiscal period — assessment cannot be triggered.
            </div>
          )}

          {assessError && <ErrorNote message={assessError} />}

          {/* Assess trigger — NEVER automatic */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleAssess}
              disabled={assessing || !periodId}
            >
              {assessing ? (
                <><RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />Assessing…</>
              ) : (
                <><ListChecks className="mr-1.5 h-4 w-4" />Assess Positions</>
              )}
            </Button>
            {hasResults && !assessing && (
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {assessResult
                  ? `Assessment complete — ${assessResult.assessed} positions scanned`
                  : "Showing persisted results"}
              </span>
            )}
          </div>

          {/* Results summary */}
          {hasResults && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
                  <div>
                    <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Eligible</p>
                    <p className="text-tok-lg font-bold text-[rgb(var(--color-text))]">{eligibleCount}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                  ineligibleCount > 0
                    ? "border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5"
                    : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]"
                }`}>
                  {ineligibleCount > 0
                    ? <TriangleAlert className="h-5 w-5 shrink-0 text-[rgb(var(--color-warning))]" />
                    : <CheckCircle2 className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
                  }
                  <div>
                    <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Ineligible</p>
                    <p className="text-tok-lg font-bold text-[rgb(var(--color-text))]">{ineligibleCount}</p>
                  </div>
                </div>
              </div>

              {/* Gate verdict */}
              <div className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-tok-sm ${
                proceedEnabled
                  ? "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 text-[rgb(var(--color-success))]"
                  : "border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 text-[rgb(var(--color-danger))]"
              }`}>
                {proceedEnabled
                  ? <><CheckCircle2 className="h-4 w-4 shrink-0" /> Ready to proceed — {eligibleCount} position{eligibleCount !== 1 ? "s" : ""} will be included in the next calculation run.</>
                  : <><XCircle className="h-4 w-4 shrink-0" /> Cannot proceed — no eligible positions found. Resolve data issues and re-assess.</>
                }
              </div>

              {ineligibleCount > 0 && (
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  To manage holds and create a population snapshot, go to <strong>Readiness</strong> (Stage 15).
                </p>
              )}
            </div>
          )}
        </Card>

        {/* CA-01: CSV Bulk Import — matches PositionDataReview pattern */}
        <Card title="Position Load">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">
            Upload <strong>Positions.csv</strong> to load or amend rep positions. Validates before committing.
          </p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-3">
            Required: <span className="text-[rgb(var(--color-primary))]">employee_id, full_name, role_code, segment_code, hire_date, is_active</span>
          </p>
          {csvError && (
            <div role="alert" className="mb-3 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{csvError}</div>
          )}
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                accept=".csv"
                className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setCsvFile(f);
                  setCsvDryResult(null);
                  setCsvCommitted(false);
                  setCsvError('');
                }}
              />
              <button
                type="button"
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40"
                disabled={!csvFile || csvImporting}
                onClick={() => runCsvValidate(csvFile)}
              >
                {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
              </button>
              {csvCommitted && <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Loaded</span>}
            </div>
            {csvDryResult && (
              <div className="flex flex-col gap-2">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Rows parsed: <strong>{csvDryResult.rows}</strong> · Positions to load: <strong>{csvDryResult.rows}</strong>
                </p>
                {csvDryResult.warnings?.map((w, i) => (
                  <p key={i} className="text-tok-xs text-[rgb(var(--color-warning))]">⚠ {w}</p>
                ))}
                {csvDryResult.errors?.length > 0 ? (
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-tok-sm text-[rgb(var(--color-success))]">
                      ✓ Validation passed — {csvDryResult.rows} position(s) ready to load.
                    </span>
                    <button
                      type="button"
                      className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
                      disabled={csvImporting}
                      onClick={() => runCsvCommit(csvFile)}
                    >
                      {csvImporting ? 'Loading…' : 'Confirm and load'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Import history */}
        <Card title="Position Load History">
          {loading && <LoadingRow />}
          {error && <ErrorNote message="Unable to load import history." />}
          {!loading && !error && logs.length === 0 && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No position imports found. Upload a CSV above to load positions.</p>
          )}
          <div className="mt-2 space-y-2">
            {logs.map((log) => (
              <div key={log.import_log_id} className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                <div className="grid flex-1 gap-1 sm:grid-cols-3">
                  <Field label="File">{log.source_filename}</Field>
                  <Field label="Rows">{log.rows_loaded ?? "—"}</Field>
                  <Field label="Loaded">{log.created_at ? new Date(log.created_at).toLocaleDateString() : "—"}</Field>
                </div>
                <Pill tone={STATUS_TONE[log.load_status] ?? "Draft"}>{log.load_status}</Pill>
              </div>
            ))}
          </div>
        </Card>
      </div>
</div>
  );
}

// ---------------------------------------------------------------------------
// PositionDetailPanel — S553: upload section added above position list
// ---------------------------------------------------------------------------

function PositionDetailPanel({ mode, period }) {
  const [positionsKey, setPositionsKey] = useState(0);
  const { data, loading, error } = useFetch(`${API_BASE}/positions?_k=${positionsKey}`);
  const positions = data?.positions ?? [];

  // Upload state
  const POSITION_REQUIRED_COLS = ['employee_id', 'full_name', 'role_code', 'segment_code', 'hire_date', 'is_active'];
  const POSITION_OPTIONAL_COLS = ['ramp_start_date', 'manager_employee_id', 'territory_code', 'business_unit_code', 'email', 'country_code', 'local_currency_code', 'end_date'];

  const [csvFile, setCsvFile]                     = useState(null);
  const [csvValidating, setCsvValidating]         = useState(false);
  const [csvUploading, setCsvUploading]           = useState(false);
  const [csvValidationResult, setCsvValidationResult] = useState(null);
  const [csvError, setCsvError]                   = useState('');
  const [csvResult, setCsvResult]                 = useState(null); // { loaded, unchanged, rejected, rejectedRows, logId }

  async function ensurePositionColumnMapping() {
    const mappings = [
      { source_column_name: 'employee_id',         target_column_name: 'employee_id',         is_required: true  },
      { source_column_name: 'full_name',            target_column_name: 'full_name',            is_required: true  },
      { source_column_name: 'role_code',            target_column_name: 'role_code',            is_required: true  },
      { source_column_name: 'segment_code',         target_column_name: 'segment_code',         is_required: true  },
      { source_column_name: 'hire_date',            target_column_name: 'hire_date',            is_required: true  },
      { source_column_name: 'is_active',            target_column_name: 'is_active',            is_required: true  },
      { source_column_name: 'ramp_start_date',      target_column_name: 'ramp_start_date',      is_required: false },
      { source_column_name: 'manager_employee_id',  target_column_name: 'manager_employee_id',  is_required: false },
      { source_column_name: 'territory_code',       target_column_name: 'territory_code',       is_required: false },
      { source_column_name: 'business_unit_code',   target_column_name: 'business_unit_code',   is_required: false },
      { source_column_name: 'email',                target_column_name: 'email',                is_required: false },
      { source_column_name: 'country_code',         target_column_name: 'country_code',         is_required: false },
      { source_column_name: 'local_currency_code',  target_column_name: 'local_currency_code',  is_required: false },
      { source_column_name: 'end_date',             target_column_name: 'end_date',             is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'position', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed position column mapping.');
  }

  function handleFileChange(e) {
    setCsvFile(e.target.files[0] || null);
    setCsvValidationResult(null);
    setCsvError('');
    setCsvResult(null);
  }

  async function handleValidate() {
    if (!csvFile) return;
    setCsvValidating(true);
    setCsvError('');
    setCsvValidationResult(null);
    setCsvResult(null);
    try {
      const text = await csvFile.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      const missing = POSITION_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const warnings = POSITION_OPTIONAL_COLS
        .filter((c) => !headers.includes(c))
        .map((c) => `${c} absent — will not be set.`);
      setCsvValidationResult({ rows: lines.length - 1, warnings });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvValidating(false); }
  }

  async function handleConfirmAndLoad() {
    if (!csvFile || !csvValidationResult) return;
    setCsvUploading(true);
    setCsvError('');
    try {
      await ensurePositionColumnMapping();
      const fd = new FormData();
      fd.append('file', csvFile);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'position');
      // S598: POST to position-specific endpoint which returns rows_inserted/updated/unchanged
      const res = await fetch(`${API_BASE}/positions/import`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));

      // 409 — all rows rejected (position-specific router)
      if (res.status === 409) {
        const rowResults = json?.error?.row_results ?? [];
        setCsvResult({
          status: 409,
          code: json?.error?.code ?? 'IMPORT_ALL_ROWS_REJECTED',
          message: json?.error?.message ?? 'All rows were rejected. No position data written.',
          rowResults,
        });
        return;
      }
      if (!res.ok) {
        setCsvError(json?.error?.message ?? json?.detail?.error?.message ?? `Error ${res.status}`);
        return;
      }
      if (res.status === 207) {
        setCsvResult({
          status: 207,
          inserted:   json.rows_inserted  ?? 0,
          updated:    json.rows_updated   ?? 0,
          unchanged:  json.rows_unchanged ?? 0,
          rejected:   json.rows_rejected  ?? 0,
          rowResults: json.row_results    ?? [],
          summary:    json.error_summary  ?? '',
          logId:      json.import_log_id  ?? '',
        });
        setPositionsKey((k) => k + 1);
        return;
      }
      // 200 full success
      setCsvResult({
        status: 200,
        inserted:  json.rows_inserted  ?? 0,
        updated:   json.rows_updated   ?? 0,
        unchanged: json.rows_unchanged ?? 0,
        logId:     json.import_log_id  ?? '',
      });
      setCsvFile(null);
      setCsvValidationResult(null);
      setPositionsKey((k) => k + 1);
    } catch (e) { setCsvError(e.message || 'Upload failed.'); }
    finally { setCsvUploading(false); }
  }

  return (
    <div className="w-full space-y-4">
        {/* Position Upload */}
        <Card title="Position Import">
          {/* Required fields callout */}
          <div className="mb-3 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5 text-tok-xs font-mono">
            <p className="mb-1 font-semibold text-[rgb(var(--color-text))]">Required columns</p>
            <p className="text-[rgb(var(--color-primary))]">employee_id, full_name, role_code, segment_code, hire_date, is_active</p>
            <p className="mt-1.5 mb-0.5 font-semibold text-[rgb(var(--color-text))]">Optional columns</p>
            <p className="text-[rgb(var(--color-text-muted))]">ramp_start_date, manager_employee_id, territory_code, business_unit_code, email, country_code, local_currency_code, end_date</p>
            <p className="mt-1.5 text-[rgb(var(--color-text-muted))]"><span className="text-[rgb(var(--color-text))]">is_active:</span> true / 1 / yes = active · anything else = inactive</p>
          </div>

          {csvError && (
            <div role="alert" className="mb-3 rounded-xl bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-3 py-2 text-tok-sm">{csvError}</div>
          )}

          {/* ── S598: Position import result display ── */}

          {/* 3a / 200 success — show inserted/updated/unchanged counts + field-change summary */}
          {csvResult?.status === 200 && (
            <div role="status" className="mb-3 rounded-xl bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-3 py-2.5 space-y-1.5">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">✓ Positions loaded successfully.</p>
              <div className="flex gap-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {csvResult.inserted  > 0 && <span>Inserted: <strong>{csvResult.inserted}</strong></span>}
                {csvResult.updated   > 0 && <span>Updated: <strong>{csvResult.updated}</strong></span>}
                {csvResult.unchanged > 0 && <span>Unchanged: <strong>{csvResult.unchanged}</strong></span>}
              </div>
              {/* 3c — BU change warning when updated rows exist */}
              {csvResult.updated > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-2.5 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
                  <span className="mt-0.5 shrink-0">⚠</span>
                  <span>
                    {csvResult.updated} position{csvResult.updated !== 1 ? 's were' : ' was'} updated.
                    If business unit or segment changed, quota and credit attribution may be affected.
                    Review the field-change audit log to confirm payout impact.
                  </span>
                </div>
              )}
              {csvResult.logId && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Load ID: {csvResult.logId}</p>}
            </div>
          )}

          {/* 207 partial — counts + per-row rejection details with lifecycle-aware messages */}
          {csvResult?.status === 207 && (
            <div className="mb-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5 space-y-2">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Partial load — {csvResult.rejected} row(s) rejected</p>
              <div className="flex gap-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {csvResult.inserted  > 0 && <span>Inserted: <strong>{csvResult.inserted}</strong></span>}
                {csvResult.updated   > 0 && <span>Updated: <strong>{csvResult.updated}</strong></span>}
                {csvResult.unchanged > 0 && <span>Unchanged: <strong>{csvResult.unchanged}</strong></span>}
                <span>Rejected: <strong>{csvResult.rejected}</strong></span>
              </div>
              {/* 3c — BU change payout impact warning */}
              {csvResult.updated > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-2.5 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
                  <span className="mt-0.5 shrink-0">⚠</span>
                  <span>
                    {csvResult.updated} position{csvResult.updated !== 1 ? 's were' : ' was'} updated.
                    If business unit or segment changed, review the field-change audit log to confirm payout impact.
                  </span>
                </div>
              )}
              {/* Rejected row details — lifecycle-aware error messages */}
              {csvResult.rowResults?.filter(r => r.status === 'rejected').length > 0 && (
                <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden">
                  <div className="max-h-56 overflow-y-auto">
                    {csvResult.rowResults.filter(r => r.status === 'rejected').slice(0, 50).map((r, i) => {
                      const isHireImmutable  = r.error_code === 'HIRE_DATE_IMMUTABLE';
                      const isRampImmutable  = r.error_code === 'RAMP_DATE_IMMUTABLE';
                      const isPostFinal      = r.error_code === 'PERIOD_POST_FINAL_LOCKED';
                      const rowNum = (r.row_index ?? 0) + 2; // +1 for header, +1 for 1-based
                      return (
                        <div key={i} className={`border-t border-[rgb(var(--color-border))] px-3 py-2 text-tok-xs ${i === 0 ? 'border-t-0' : ''}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-[rgb(var(--color-text-muted))]">Row {rowNum}</span>
                            <span className={`font-mono shrink-0 ${isPostFinal ? 'text-[rgb(var(--color-danger))]' : 'text-[rgb(var(--color-warning))]'}`}>{r.error_code}</span>
                          </div>
                          {/* 3b — Immutable field violations: human-readable with current/incoming values */}
                          {(isHireImmutable || isRampImmutable) && (
                            <div className="mt-1.5 rounded-lg bg-[rgb(var(--color-surface-muted))] px-2.5 py-1.5 space-y-0.5">
                              <p className="font-medium text-[rgb(var(--color-text))]">
                                {isHireImmutable ? 'Hire date' : 'Ramp start date'} cannot be changed via re-import
                              </p>
                              <p className="text-[rgb(var(--color-text-muted))]">
                                Current: <strong>{r.detail?.current ?? '—'}</strong> · Incoming: <strong>{r.detail?.incoming ?? '—'}</strong>
                              </p>
                              <p className="text-[rgb(var(--color-text-muted))]">
                                Contact your administrator or use the amendment process to make corrections.
                              </p>
                            </div>
                          )}
                          {/* 3d — Period post-final locked */}
                          {isPostFinal && (
                            <div className="mt-1.5 rounded-lg bg-[rgb(var(--color-danger))]/8 border border-[rgb(var(--color-danger))]/20 px-2.5 py-1.5">
                              <p className="font-medium text-[rgb(var(--color-danger))]">Period finalised — classification and financial changes are locked</p>
                              <p className="mt-0.5 text-[rgb(var(--color-text-muted))]">
                                This period has been finalised. Use the amendment process to make corrections.
                              </p>
                            </div>
                          )}
                          {/* Generic rejection message for other error codes */}
                          {!isHireImmutable && !isRampImmutable && !isPostFinal && (
                            <p className="mt-1 text-[rgb(var(--color-text-muted))]">{r.detail?.message ?? r.error_code}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {csvResult.rowResults.filter(r => r.status === 'rejected').length > 50 && (
                    <p className="px-3 py-1.5 text-tok-xs text-[rgb(var(--color-text-muted))] border-t border-[rgb(var(--color-border))]">
                      and {csvResult.rowResults.filter(r => r.status === 'rejected').length - 50} more
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 409 — all rows rejected */}
          {csvResult?.status === 409 && (
            <div className="mb-3 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5 space-y-2">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">All rows rejected — no positions written</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{csvResult.message}</p>
              {csvResult.rowResults?.length > 0 && (
                <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden">
                  <div className="max-h-48 overflow-y-auto">
                    {csvResult.rowResults.slice(0, 20).map((r, i) => {
                      const isHireImmutable = r.error_code === 'HIRE_DATE_IMMUTABLE';
                      const isRampImmutable = r.error_code === 'RAMP_DATE_IMMUTABLE';
                      const isPostFinal     = r.error_code === 'PERIOD_POST_FINAL_LOCKED';
                      const rowNum = (r.row_index ?? 0) + 2;
                      return (
                        <div key={i} className={`border-t border-[rgb(var(--color-border))] px-3 py-2 text-tok-xs ${i === 0 ? 'border-t-0' : ''}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-[rgb(var(--color-text-muted))]">Row {rowNum}</span>
                            <span className="font-mono text-[rgb(var(--color-danger))] shrink-0">{r.error_code}</span>
                          </div>
                          {(isHireImmutable || isRampImmutable) && (
                            <div className="mt-1.5 rounded-lg bg-[rgb(var(--color-surface-muted))] px-2.5 py-1.5 space-y-0.5">
                              <p className="font-medium text-[rgb(var(--color-text))]">
                                {isHireImmutable ? 'Hire date' : 'Ramp start date'} cannot be changed via re-import
                              </p>
                              <p className="text-[rgb(var(--color-text-muted))]">
                                Current: <strong>{r.detail?.current ?? '—'}</strong> · Incoming: <strong>{r.detail?.incoming ?? '—'}</strong>
                              </p>
                              <p className="text-[rgb(var(--color-text-muted))]">Contact your administrator or use the amendment process.</p>
                            </div>
                          )}
                          {isPostFinal && (
                            <div className="mt-1.5 rounded-lg bg-[rgb(var(--color-danger))]/8 border border-[rgb(var(--color-danger))]/20 px-2.5 py-1.5">
                              <p className="font-medium text-[rgb(var(--color-danger))]">Period finalised — changes are locked</p>
                              <p className="mt-0.5 text-[rgb(var(--color-text-muted))]">Use the amendment process to make corrections.</p>
                            </div>
                          )}
                          {!isHireImmutable && !isRampImmutable && !isPostFinal && (
                            <p className="mt-1 text-[rgb(var(--color-text-muted))]">{r.detail?.message ?? r.error_code}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
          {period?.period_status !== "open" ? (
            <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
              <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
              <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                Period is <strong>{period?.period_status ?? "not open"}</strong> — position imports are not available for this period.
                Switch to an open period to import positions.
              </p>
            </div>
          ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <input type="file" accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={handleFileChange}
            />
            <button type="button" onClick={handleValidate} disabled={!csvFile || csvValidating}
              className="px-3 py-1.5 rounded-lg border border-[rgb(var(--color-border))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">
              {csvValidating ? 'Validating…' : 'Validate'}
            </button>
          </div>
          )}
          {csvValidationResult && (
            <div className="mt-3 space-y-1">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Rows parsed: <strong>{csvValidationResult.rows}</strong></p>
              {csvValidationResult.warnings.map((w, i) => (
                <p key={i} className="text-tok-xs text-[rgb(var(--color-warning))]">⚠ {w}</p>
              ))}
              <div className="flex items-center gap-3 pt-1">
                <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Validation passed — {csvValidationResult.rows} position(s) ready to load.</span>
                <button type="button" onClick={handleConfirmAndLoad} disabled={csvUploading}
                  className="px-4 py-1.5 rounded-lg bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
                  {csvUploading ? 'Loading…' : 'Confirm and load'}
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* Position list — S579 OI-UI-TABLE-CONSISTENCY */}
        <PositionTable positions={positions} loading={loading} error={error} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanAssignmentPanel
// ---------------------------------------------------------------------------

function PlanAssignmentPanel({ mode, period }) {
  const periodId = period?.fiscal_period_id;

  // Assignment list — refreshKey triggers refetch after a successful upload
  const [logsKey, setLogsKey] = useState(0);
  const { data, loading, error } = useFetch(
    periodId ? `${API_BASE}/plan-assignments?fiscal_period_id=${periodId}&_k=${logsKey}` : null
  );
  const assignments = data?.data ?? data?.plan_assignments ?? [];
  const STATUS_TONE = { active: "Complete", superseded: "Draft", cancelled: "Blocked" };

  // CA-02: CSV upload state — POST /api/v1/import/upload, file_type=plan_assignments
  // Uses unified import endpoint (not legacy bulk-import?dry_run= path in PlanAssignmentManager)
  const PA_REQUIRED_COLS = ['employee_id', 'plan_code', 'effective_date', 'fiscal_period_code', 'business_unit_code'];

  const [csvFile, setCsvFile]           = useState(null);
  const [csvDryResult, setCsvDryResult] = useState(null);
  const [csvCommitted, setCsvCommitted] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError]         = useState('');

  // Column-mapping pre-flight — idempotent upsert
  async function ensurePlanAssignmentColumnMapping() {
    const mappings = [
      { source_column_name: 'employee_id',         target_column_name: 'employee_id',         is_required: true  },
      { source_column_name: 'plan_code',            target_column_name: 'plan_code',            is_required: true  },
      { source_column_name: 'effective_date',       target_column_name: 'effective_date',       is_required: true  },
      { source_column_name: 'fiscal_period_code',   target_column_name: 'fiscal_period_code',   is_required: true  },
      { source_column_name: 'business_unit_code',   target_column_name: 'business_unit_code',   is_required: true  },
      { source_column_name: 'end_date',             target_column_name: 'end_date',             is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'plan_assignments', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed plan assignment column mapping.');
  }

  async function runCsvValidate(file) {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = PA_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const warnings = [];
      if (!headers.includes('end_date')) warnings.push('end_date absent — assignment will have no end date.');
      setCsvDryResult({ rows: lines.length - 1, warnings, errors: [] });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvImporting(false); }
  }

  const [csvPartialResult, setCsvPartialResult] = useState(null); // 207 result

  async function runCsvCommit(file) {
    if (!file || !csvDryResult) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvPartialResult(null);
    try {
      await ensurePlanAssignmentColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'plan_assignments'); // NOTE: OI-S553-PLAN-ASSIGNMENT-FILE-TYPE — backend will return UNSUPPORTED_FILE_TYPE until resolved
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));

      if (res.status === 422) {
        setCsvError(`${json?.error?.code ?? 'IMPORT_ERROR'}: ${json?.error?.message ?? 'All rows rejected.'}`);
        return;
      }
      if (!res.ok) {
        setCsvError(json?.detail?.error?.message ?? json?.error?.message ?? `Error ${res.status}`);
        return;
      }
      if (res.status === 207) {
        setCsvPartialResult({
          loaded: json.rows_loaded ?? 0,
          unchanged: json.rows_unchanged ?? 0,
          rejected: json.rows_rejected ?? 0,
          rejectedRows: json.rejected_rows ?? [],
          summary: json.error_summary ?? '',
        });
        setLogsKey((k) => k + 1);
        return;
      }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      setLogsKey((k) => k + 1);
    } catch (e) { setCsvError(e.message || 'Commit failed.'); }
    finally { setCsvImporting(false); }
  }

  return (
    <div className="w-full space-y-4">
        {/* CA-02: CSV Bulk Import — unified import endpoint */}
        <Card title="Plan Assignment Import">
          {/* S553 OI-S553-PLAN-ASSIGNMENT-FILE-TYPE callout — non-blocking */}
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Plan assignment CSV import requires a backend update (<span className="font-mono text-tok-xs">OI-S553-PLAN-ASSIGNMENT-FILE-TYPE</span>). Upload is available but will return <span className="font-mono text-tok-xs">UNSUPPORTED_FILE_TYPE</span> until resolved.
            </p>
          </div>

          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">
            Upload <strong>PlanAssignments.csv</strong> to assign positions to plans. Validates before committing.
          </p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-3">
            Required: <span className="text-[rgb(var(--color-primary))]">employee_id, plan_code, effective_date, fiscal_period_code, business_unit_code</span>
            <span className="ml-2 text-[rgb(var(--color-text-muted))]">· Optional: end_date</span>
          </p>
          {csvError && (
            <div role="alert" className="mb-3 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{csvError}</div>
          )}
          {csvPartialResult && (
            <div className="mb-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-3 py-2 space-y-1">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Partial load — {csvPartialResult.rejected} row(s) rejected</p>
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loaded: {csvPartialResult.loaded} · Unchanged: {csvPartialResult.unchanged} · Rejected: {csvPartialResult.rejected}</p>
              {csvPartialResult.summary && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{csvPartialResult.summary}</p>}
              {csvPartialResult.rejectedRows?.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))]">
                  <table className="w-full text-tok-xs">
                    <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                      <tr>{['Row #','Field','Error Code','Message'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {csvPartialResult.rejectedRows.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-t border-[rgb(var(--color-border))]">
                          <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{r.row_number}</td>
                          <td className="px-2 py-1 font-mono text-[rgb(var(--color-text))]">{r.field ?? '—'}</td>
                          <td className="px-2 py-1 font-mono text-[rgb(var(--color-danger))]">{r.error_code ?? '—'}</td>
                          <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{r.message ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvPartialResult.rejectedRows.length > 50 && (
                    <p className="px-3 py-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">and {csvPartialResult.rejectedRows.length - 50} more</p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
          {period?.period_status !== "open" ? (
            <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
              <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
              <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                Period is <strong>{period?.period_status ?? "not open"}</strong> — assignment imports are not available for this period.
                Switch to an open period to import assignments.
              </p>
            </div>
          ) : (
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                accept=".csv"
                className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setCsvFile(f);
                  setCsvDryResult(null);
                  setCsvCommitted(false);
                  setCsvError('');
                }}
              />
              <button
                type="button"
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40"
                disabled={!csvFile || csvImporting}
                onClick={() => runCsvValidate(csvFile)}
              >
                {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
              </button>
              {csvCommitted && <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Loaded</span>}
            </div>
            {csvDryResult && (
              <div className="flex flex-col gap-2">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Rows parsed: <strong>{csvDryResult.rows}</strong> · Assignments to load: <strong>{csvDryResult.rows}</strong>
                </p>
                {csvDryResult.warnings?.map((w, i) => (
                  <p key={i} className="text-tok-xs text-[rgb(var(--color-warning))]">⚠ {w}</p>
                ))}
                {csvDryResult.errors?.length > 0 ? (
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-tok-sm text-[rgb(var(--color-success))]">
                      ✓ Validation passed — {csvDryResult.rows} assignment(s) ready to load.
                    </span>
                    <button
                      type="button"
                      className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
                      disabled={csvImporting}
                      onClick={() => runCsvCommit(csvFile)}
                    >
                      {csvImporting ? 'Loading…' : 'Confirm and load'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </Card>

        {/* Current assignments — S579 OI-UI-TABLE-CONSISTENCY */}
        <AssignmentTable assignments={assignments} loading={loading} error={error} periodId={periodId} statusTone={STATUS_TONE} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CA-04: RateTablesPanel
// Two sequential uploads: shells first (file_type=rate_table_shell),
// rows second (file_type=rate_table_row). Matches RateTableBuilder.jsx S471 pattern.
// Shells must be approved before rows can be loaded — surfaced as a note.
// ---------------------------------------------------------------------------

function RateTablesPanel({ mode, period }) {
  const [logsKey, setLogsKey] = useState(0);
  const { data: logsData, loading, error } = useFetch(`${API_BASE}/import-logs?file_type=rate_table_shell&_k=${logsKey}`);
  const logs = logsData?.data ?? [];
  const STATUS_TONE = { loaded: "Complete", failed: "Blocked", pending: "Draft" };

  // Shared importing/error state across both upload cards
  const [csvUnifiedImporting, setCsvUnifiedImporting] = useState(false);
  const [csvUnifiedError, setCsvUnifiedError]         = useState('');

  // Shells
  const [csvShellsFile, setCsvShellsFile]           = useState(null);
  const [csvShellsDryResult, setCsvShellsDryResult] = useState(null);
  const [csvShellsCommitted, setCsvShellsCommitted] = useState(false);

  // Rows
  const [csvRowsFile, setCsvRowsFile]           = useState(null);
  const [csvRowsDryResult, setCsvRowsDryResult] = useState(null);
  const [csvRowsCommitted, setCsvRowsCommitted] = useState(false);

  const SHELL_REQUIRED_COLS = ['rate_table_code', 'rate_table_name', 'plan_code', 'role_code', 'segment_code', 'default_ceiling_pct', 'attainment_ceiling_pct', 'effective_date'];
  const ROW_REQUIRED_COLS   = ['rate_table_code', 'from_pct', 'to_pct', 'payout_pct', 'interpolation'];

  async function ensureShellColumnMapping() {
    const mappings = [
      { source_column_name: 'rate_table_code',        target_column_name: 'rate_table_code',        is_required: true  },
      { source_column_name: 'rate_table_name',        target_column_name: 'rate_table_name',        is_required: true  },
      { source_column_name: 'plan_code',              target_column_name: 'plan_code',              is_required: true  },
      { source_column_name: 'role_code',              target_column_name: 'role_code',              is_required: true  },
      { source_column_name: 'segment_code',           target_column_name: 'segment_code',           is_required: true  },
      { source_column_name: 'default_ceiling_pct',    target_column_name: 'default_ceiling_pct',    is_required: true  },
      { source_column_name: 'attainment_ceiling_pct', target_column_name: 'attainment_ceiling_pct', is_required: true  },
      { source_column_name: 'effective_date',         target_column_name: 'effective_date',         is_required: true  },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_system: 'direct_api', file_type: 'rate_table_shell', mappings }) });
    if (!res.ok) throw new Error('Failed to seed rate table shell column mapping.');
  }

  async function ensureRowColumnMapping() {
    const mappings = [
      { source_column_name: 'rate_table_code', target_column_name: 'rate_table_code', is_required: true  },
      { source_column_name: 'from_pct',        target_column_name: 'from_pct',        is_required: true  },
      { source_column_name: 'to_pct',          target_column_name: 'to_pct',          is_required: true  },
      { source_column_name: 'payout_pct',      target_column_name: 'payout_pct',      is_required: true  },
      { source_column_name: 'interpolation',   target_column_name: 'interpolation',   is_required: true  },
      { source_column_name: 'is_accelerator',  target_column_name: 'is_accelerator',  is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_system: 'direct_api', file_type: 'rate_table_row', mappings }) });
    if (!res.ok) throw new Error('Failed to seed rate table row column mapping.');
  }

  async function runValidate(fileType, file) {
    if (!file) return;
    setCsvUnifiedImporting(true);
    setCsvUnifiedError('');
    if (fileType === 'rate_table_shell') { setCsvShellsDryResult(null); setCsvShellsCommitted(false); }
    else { setCsvRowsDryResult(null); setCsvRowsCommitted(false); }
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvUnifiedError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const required = fileType === 'rate_table_shell' ? SHELL_REQUIRED_COLS : ROW_REQUIRED_COLS;
      const missing = required.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvUnifiedError(`Missing required columns: ${missing.join(', ')}`); return; }
      const result = { rows: lines.length - 1, errors: [] };
      if (fileType === 'rate_table_shell') setCsvShellsDryResult(result);
      else setCsvRowsDryResult(result);
    } catch { setCsvUnifiedError('Failed to parse CSV.'); }
    finally { setCsvUnifiedImporting(false); }
  }

  async function runCommit(fileType, file, dryResult) {
    if (!file || !dryResult) return;
    setCsvUnifiedImporting(true);
    setCsvUnifiedError('');
    try {
      if (fileType === 'rate_table_shell') await ensureShellColumnMapping();
      else await ensureRowColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', fileType);
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setCsvUnifiedError(json?.message || json?.detail || `Error ${res.status}`); return; }
      if (fileType === 'rate_table_shell') {
        setCsvShellsCommitted(true); setCsvShellsDryResult(null); setCsvShellsFile(null);
        setLogsKey((k) => k + 1);
      } else {
        setCsvRowsCommitted(true); setCsvRowsDryResult(null); setCsvRowsFile(null);
      }
    } catch (e) { setCsvUnifiedError(e.message || 'Commit failed.'); }
    finally { setCsvUnifiedImporting(false); }
  }

  function UploadCard({ fileType, label, filename, requiredCols, file, setFile, dryResult, committed, note }) {
    return (
      <Card title={label}>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">Upload <strong>{filename}</strong>. Validates before committing.</p>
        <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-3">Required: <span className="text-[rgb(var(--color-primary))]">{requiredCols.join(', ')}</span></p>
        {note && <p className="mb-3 text-tok-xs text-[rgb(var(--color-warning))]">⚠ {note}</p>}
        {csvUnifiedError && <div role="alert" className="mb-3 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{csvUnifiedError}</div>}
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <input type="file" accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (fileType === 'rate_table_shell') { setCsvShellsDryResult(null); setCsvShellsCommitted(false); } else { setCsvRowsDryResult(null); setCsvRowsCommitted(false); } setCsvUnifiedError(''); }}
            />
            <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40" disabled={!file || csvUnifiedImporting} onClick={() => runValidate(fileType, file)}>
              {csvUnifiedImporting && !dryResult ? 'Validating…' : 'Validate'}
            </button>
            {committed && <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Loaded</span>}
          </div>
          {dryResult && (
            <div className="flex flex-col gap-2">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Rows parsed: <strong>{dryResult.rows}</strong></p>
              {dryResult.errors?.length > 0 ? (
                <p className="text-tok-sm text-[rgb(var(--color-danger))]">{dryResult.errors.length} error(s) — fix CSV and re-upload.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Validation passed — {dryResult.rows} row(s) ready to load.</span>
                  <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" disabled={csvUnifiedImporting} onClick={() => runCommit(fileType, file, dryResult)}>
                    {csvUnifiedImporting ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="space-y-4">
        <UploadCard fileType="rate_table_shell" label="Step 1 — Rate Table Shells" filename="RateTableShells.csv" requiredCols={SHELL_REQUIRED_COLS} file={csvShellsFile} setFile={setCsvShellsFile} dryResult={csvShellsDryResult} committed={csvShellsCommitted} />
        <UploadCard fileType="rate_table_row" label="Step 2 — Rate Table Rows (bands)" filename="RateTableRows.csv" requiredCols={ROW_REQUIRED_COLS} file={csvRowsFile} setFile={setCsvRowsFile} dryResult={csvRowsDryResult} committed={csvRowsCommitted} note="Rate table shells must be approved before rows can be loaded. Approval is done via Plan Approval (Stage 10)." />
        <Card title="Rate Table Load History">
          {loading && <LoadingRow />}
          {error && <ErrorNote message="Unable to load import history." />}
          {!loading && !error && logs.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No rate table imports found.</p>}
          <div className="mt-2 space-y-2">
            {logs.map((log) => (
              <div key={log.import_log_id} className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                <div className="grid flex-1 gap-1 sm:grid-cols-3"><Field label="File">{log.source_filename}</Field><Field label="Rows">{log.rows_loaded ?? "—"}</Field><Field label="Loaded">{log.created_at ? new Date(log.created_at).toLocaleDateString() : "—"}</Field></div>
                <Pill tone={STATUS_TONE[log.load_status] ?? "Draft"}>{log.load_status}</Pill>
              </div>
            ))}
          </div>
        </Card>
      </div>
      </div>
  );
}

// ---------------------------------------------------------------------------
// CA-05: RampSchedulesPanel
// Uses dedicated server-side two-step endpoint (ramp_schedule_import_router.py):
//   POST /ramp-schedules/import/validate → session_id + preview
//   POST /ramp-schedules/import/confirm?session_id=...
// No column_mapping pre-flight — not routed through /import/upload.
// Matches RampScheduleConfig.jsx S470 pattern exactly.
// ---------------------------------------------------------------------------

function RampSchedulesPanel({ mode, period }) {
  const { data: rampData, loading, error } = useFetch(`${API_BASE}/ramp-schedules`);
  const rampRows = rampData?.ramp_schedules ?? rampData?.rows ?? rampData?.data ?? [];

  const RAMP_REQUIRED_COLS = ['role_code', 'ramp_month', 'ramp_factor', 'effective_date'];

  const [csvFile, setCsvFile]           = useState(null);
  const [csvSession, setCsvSession]     = useState(null); // { session_id, rows_valid, rows_rejected, preview }
  const [csvCommitted, setCsvCommitted] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError]         = useState('');

  async function runCsvValidate(file) {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvSession(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = RAMP_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/ramp-schedules/import/validate`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const serverErrors = json?.detail?.error?.detail?.errors;
        setCsvError(Array.isArray(serverErrors) && serverErrors.length > 0 ? serverErrors.join('\n') : (json?.detail?.error?.message || json?.message || `Validation failed (${res.status})`));
        return;
      }
      setCsvSession(json);
    } catch (e) { setCsvError(e.message || 'Validation request failed.'); }
    finally { setCsvImporting(false); }
  }

  async function runCsvConfirm() {
    if (!csvSession?.session_id) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      const res = await fetch(`${API_BASE}/ramp-schedules/import/confirm?session_id=${encodeURIComponent(csvSession.session_id)}`, { method: 'POST', credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setCsvError(json?.detail?.error?.message || json?.message || `Confirm failed (${res.status})`); return; }
      setCsvCommitted(true);
      setCsvSession(null);
      setCsvFile(null);
    } catch (e) { setCsvError(e.message || 'Confirm request failed.'); }
    finally { setCsvImporting(false); }
  }

  return (
    <div className="w-full space-y-4">
      <div className="space-y-4">
        <Card title="Ramp Schedule Import">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">Upload <strong>RampSchedules.csv</strong> to load or amend ramp factors by role. Validates server-side before committing.</p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-3">Required: <span className="text-[rgb(var(--color-primary))]">role_code, ramp_month, ramp_factor, effective_date</span></p>
          {csvError && <div role="alert" className="mb-3 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]" style={{whiteSpace:'pre-line'}}>{csvError}</div>}
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept=".csv"
                className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
                onChange={(e) => { const f = e.target.files?.[0] || null; setCsvFile(f); setCsvSession(null); setCsvCommitted(false); setCsvError(''); }}
              />
              <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40" disabled={!csvFile || csvImporting} onClick={() => runCsvValidate(csvFile)}>
                {csvImporting && !csvSession ? 'Validating…' : 'Validate'}
              </button>
              {csvCommitted && <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Loaded</span>}
            </div>
            {csvSession && (
              <div className="flex flex-col gap-2">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Rows valid: <strong>{csvSession.rows_valid}</strong>{csvSession.rows_rejected > 0 ? ` · Rejected: ${csvSession.rows_rejected}` : ''}</p>
                {csvSession.rows_rejected > 0 ? (
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvSession.rows_rejected} row(s) rejected — fix CSV and re-upload.</p>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Validation passed — {csvSession.rows_valid} row(s) ready to load.</span>
                    <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" disabled={csvImporting} onClick={runCsvConfirm}>
                      {csvImporting ? 'Loading…' : 'Confirm and load'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
        <Card title="Current Ramp Schedules" action={rampRows.length > 0 ? <Pill tone="Ready">{rampRows.length} rows</Pill> : null}>
          {loading && <LoadingRow />}
          {error && <ErrorNote message="Unable to load ramp schedules." />}
          {!loading && !error && rampRows.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No ramp schedules found. Upload a CSV above to load ramp factors.</p>}
          <div className="mt-2 space-y-2">
            {rampRows.map((r) => (
              <div key={r.ramp_schedule_id} className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                <div className="grid flex-1 gap-1 sm:grid-cols-4">
                  <Field label="Role" value={r.role_code ?? r.role_id} />
                  <Field label="Month" value={r.ramp_month} />
                  <Field label="Factor" value={r.ramp_factor != null ? `${(Number(r.ramp_factor) * 100).toFixed(1)}%` : "—"} />
                  <Field label="Effective" value={r.effective_date} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      </div>
  );
}

// ---------------------------------------------------------------------------
// CA-06: OteHistoryPanel
// Matches OteHistoryDataReview.jsx pattern exactly.
// file_type = ote_history · endpoint = /import/upload
// Read path = GET /api/v1/positions/ote-history (with filters)
// Overlap note: date ranges per employee must not overlap existing records.
// ---------------------------------------------------------------------------

function OteHistoryPanel({ mode, period }) {
  const OTE_REQUIRED_COLS = ['employee_id', 'ote_amount', 'ote_currency_code', 'effective_date', 'end_date'];

  // CSV upload state
  const [csvFile, setCsvFile]           = useState(null);
  const [csvDryResult, setCsvDryResult] = useState(null);
  const [csvCommitted, setCsvCommitted] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError]         = useState('');

  // Read state with filters
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');
  const [oteData, setOteData]               = useState([]);
  const [oteLoading, setOteLoading]         = useState(false);
  const [oteError, setOteError]             = useState('');

  async function loadOteHistory() {
    setOteLoading(true);
    setOteError('');
    try {
      const params = new URLSearchParams();
      if (filterEmployee.trim()) params.set('employee_id', filterEmployee.trim());
      if (filterDateFrom.trim()) params.set('effective_date_from', filterDateFrom.trim());
      if (filterDateTo.trim())   params.set('effective_date_to', filterDateTo.trim());
      const qs = params.toString();
      const res = await fetch(`${API_BASE}/positions/ote-history${qs ? `?${qs}` : ''}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setOteData(data?.data ?? []);
    } catch (e) { setOteError(e.message || 'Unable to fetch OTE history.'); }
    finally { setOteLoading(false); }
  }

  useEffect(() => { loadOteHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function ensureOteHistoryColumnMapping() {
    const mappings = [
      { source_column_name: 'employee_id',      target_column_name: 'employee_id',      is_required: true },
      { source_column_name: 'ote_amount',        target_column_name: 'ote_amount',        is_required: true },
      { source_column_name: 'ote_currency_code', target_column_name: 'ote_currency_code', is_required: true },
      { source_column_name: 'effective_date',    target_column_name: 'effective_date',    is_required: true },
      { source_column_name: 'end_date',          target_column_name: 'end_date',          is_required: true },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_system: 'direct_api', file_type: 'ote_history', mappings }) });
    if (!res.ok) throw new Error('Failed to seed OTE history column mapping.');
  }

  async function runCsvValidate(file) {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = OTE_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      setCsvDryResult({ rows: lines.length - 1, errors: [] });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvImporting(false); }
  }

  async function runCsvCommit(file) {
    if (!file || !csvDryResult) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      await ensureOteHistoryColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'ote_history');
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = json?.error?.message || json?.message || json?.detail || `Error ${res.status}`;
        setCsvError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        return;
      }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      await loadOteHistory();
    } catch (e) { setCsvError(e.message || 'Commit failed.'); }
    finally { setCsvImporting(false); }
  }

  return (
    <div className="w-full space-y-4">
      <div className="space-y-4">
        <Card title="OTE History Import">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">Upload <strong>OteHistory.csv</strong> to load or amend OTE records. Validates before committing.</p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-1">Required: <span className="text-[rgb(var(--color-primary))]">employee_id, ote_amount, ote_currency_code, effective_date, end_date</span></p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-3">Date ranges per employee must not overlap existing records. Re-importing identical records (same dates, same amount) is safe — duplicates are skipped.</p>
          {csvError && <div role="alert" className="mb-3 rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{csvError}</div>}
          <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept=".csv"
                className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
                onChange={(e) => { const f = e.target.files?.[0] || null; setCsvFile(f); setCsvDryResult(null); setCsvCommitted(false); setCsvError(''); }}
              />
              <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40" disabled={!csvFile || csvImporting} onClick={() => runCsvValidate(csvFile)}>
                {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
              </button>
              {csvCommitted && <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Loaded</span>}
            </div>
            {csvDryResult && (
              <div className="flex flex-col gap-2">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Rows parsed: <strong>{csvDryResult.rows}</strong> · Records to load: <strong>{csvDryResult.rows}</strong></p>
                {csvDryResult.errors?.length > 0 ? (
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Validation passed — {csvDryResult.rows} record(s) ready to load.</span>
                    <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" disabled={csvImporting} onClick={() => runCsvCommit(csvFile)}>
                      {csvImporting ? 'Loading…' : 'Confirm and load'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        <Card title="OTE History Records">
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Employee ID</label>
              <input type="text" placeholder="e.g. EMP-001" value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-32" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective from</label>
              <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-36" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective to</label>
              <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] w-36" />
            </div>
            <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40" onClick={loadOteHistory} disabled={oteLoading}>
              {oteLoading ? 'Loading…' : 'Apply filters'}
            </button>
            <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))]" onClick={() => { setFilterEmployee(''); setFilterDateFrom(''); setFilterDateTo(''); }}>
              Clear
            </button>
          </div>
          {oteError && <ErrorNote message={oteError} />}
          {oteLoading && <LoadingRow />}
          {!oteLoading && !oteError && oteData.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No OTE history records found.</p>}
          <div className="mt-2 space-y-2">
            {oteData.map((r) => (
              <div key={r.ote_history_id} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                <div className="grid gap-1 sm:grid-cols-3">
                  <Field label="Employee" value={r.employee_id} />
                  <Field label="Name" value={r.full_name} />
                  <Field label="OTE" value={r.ote_amount != null ? `${r.ote_amount} ${r.ote_currency_code}` : "—"} />
                </div>
                <div className="mt-1 grid gap-1 sm:grid-cols-3">
                  <Field label="Effective" value={r.effective_date} />
                  <Field label="End" value={r.end_date ?? "—"} />
                  <Field label="Source" value={r.source_system} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      </div>
  );
}

// ---------------------------------------------------------------------------
// QuotaLoadPanel
// ---------------------------------------------------------------------------

function QuotaLoadPanel({ mode, period }) {
  // S391: CSV bulk import built — OI-S391 Phase 4
  // NN-VALIDATE: validate → show results → confirm → commit
  // NN-SCALE: CSV is primary ingestion path at 3K–30K rep scale
  // logsKey: replaces no-op refetchLogs — useFetch does not expose refetch
  const [logsKey, setLogsKey] = useState(0);
  const { data: logsData, loading, error } = useFetch(`${API_BASE}/import-logs?file_type=quota&_k=${logsKey}`);
  const logs = logsData?.data ?? [];
  const STATUS_TONE = { loaded: "Complete", failed: "Blocked", pending: "Draft" };

  const [csvFile, setCsvFile]                   = useState(null);
  const [csvValidating, setCsvValidating]       = useState(false);
  const [csvUploading, setCsvUploading]         = useState(false);
  const [csvValidationResult, setCsvValidationResult] = useState(null);
  const [csvError, setCsvError]                 = useState('');
  const [csvSuccess, setCsvSuccess]             = useState('');

  // S553: corrected field contract — fiscal_period_code (not id), removed server-generated cols
  const QUOTA_REQUIRED_COLS = ['fiscal_period_code', 'quota_local', 'quota_local_currency_code'];

  async function ensureQuotaColumnMapping() {
    const mappings = [
      { source_column_name: 'employee_id',                target_column_name: 'employee_id',                is_required: false },
      { source_column_name: 'fiscal_period_code',         target_column_name: 'fiscal_period_code',         is_required: true  },
      { source_column_name: 'quota_local',                target_column_name: 'quota_local',                is_required: true  },
      { source_column_name: 'quota_local_currency_code',  target_column_name: 'quota_local_currency_code',  is_required: true  },
      { source_column_name: 'business_unit_code',         target_column_name: 'business_unit_code',         is_required: false },
      { source_column_name: 'selling_motion',             target_column_name: 'selling_motion',             is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'quota', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed column mapping.');
  }

  function handleFileChange(e) {
    setCsvFile(e.target.files[0] || null);
    setCsvValidationResult(null);
    setCsvError('');
    setCsvSuccess('');
  }

  async function handleValidate() {
    if (!csvFile) return;
    setCsvValidating(true);
    setCsvError('');
    setCsvValidationResult(null);
    try {
      const text = await csvFile.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = QUOTA_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const warnings = [];
      if (!headers.includes('business_unit_code')) warnings.push('business_unit_code absent — business unit will not be set.');
      if (!headers.includes('selling_motion')) warnings.push('selling_motion absent — selling motion will not be set.');
      setCsvValidationResult({ rows: lines.length - 1, warnings });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvValidating(false); }
  }

  async function handleConfirmAndLoad() {
    if (!csvFile || !csvValidationResult) return;
    setCsvUploading(true);
    setCsvError('');
    try {
      await ensureQuotaColumnMapping();
      const fd = new FormData();
      fd.append('file', csvFile);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'quota');
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));

      if (res.status === 422) {
        setCsvError(`${json?.error?.code ?? 'IMPORT_ERROR'}: ${json?.error?.message ?? 'All rows rejected.'}`);
        if (json?.error?.rejected_rows?.length > 0) setCsvSuccess(''); // clear any prior success
        setCsvValidationResult((prev) => prev ? { ...prev, rejectedRows: json?.error?.rejected_rows ?? [] } : null);
        return;
      }
      if (!res.ok) {
        setCsvError(json?.detail?.error?.message ?? json?.error?.message ?? `Error ${res.status}`);
        return;
      }
      if (res.status === 207) {
        const loaded    = json.rows_loaded ?? 0;
        const unchanged = json.rows_unchanged ?? 0;
        const rejected  = json.rows_rejected ?? 0;
        const logId     = json.import_log?.import_log_id ?? '';
        setCsvSuccess(`Partial load: ${loaded} loaded, ${unchanged} unchanged, ${rejected} rejected.${logId ? ` Load ID: ${logId}` : ''}`);
        setCsvValidationResult((prev) => prev ? { ...prev, rejectedRows: json.rejected_rows ?? [] } : null);
        setLogsKey((k) => k + 1);
        return;
      }
      const logId = json?.import_log?.import_log_id ?? '';
      setCsvSuccess(`${csvValidationResult.rows} quota row(s) loaded.${logId ? ` Load ID: ${logId}` : ''}`);
      setCsvFile(null);
      setCsvValidationResult(null);
      setLogsKey((k) => k + 1);
    } catch (e) { setCsvError(e.message || 'Upload failed.'); }
    finally { setCsvUploading(false); }
  }

  return (
    <div className="w-full space-y-4">
        {/* CSV Bulk Import */}
        <Card title="CSV Bulk Import">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-1">
            Upload <strong>Quotas.csv</strong> to set rep quotas in one pass. Validates before committing.
          </p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] mb-3">
            Required: <span className="text-[rgb(var(--color-primary))]">fiscal_period_code, quota_local, quota_local_currency_code</span>
            <span className="ml-2 text-[rgb(var(--color-text-muted))]">· Optional: employee_id, business_unit_code, selling_motion</span>
          </p>
          {csvError && <div role="alert" className="mb-3 rounded-xl bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-3 py-2 text-tok-sm">{csvError}</div>}
          {csvSuccess && <div role="status" className="mb-3 rounded-xl bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-3 py-2 text-tok-sm">✓ {csvSuccess}</div>}
          {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
          {period?.period_status !== "open" ? (
            <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
              <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
              <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                Period is <strong>{period?.period_status ?? "not open"}</strong> — quota imports are not available for this period.
                Switch to an open period to import quotas.
              </p>
            </div>
          ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <input type="file" accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={handleFileChange}
            />
            <button type="button" onClick={handleValidate} disabled={!csvFile || csvValidating}
              className="px-3 py-1.5 rounded-lg border border-[rgb(var(--color-border))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">
              {csvValidating ? 'Validating…' : 'Validate'}
            </button>
          </div>
          )}
          {csvValidationResult && (
            <div className="mt-3 space-y-1">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Rows parsed: <strong>{csvValidationResult.rows}</strong></p>
              {csvValidationResult.warnings.map((w, i) => <p key={i} className="text-tok-xs text-[rgb(var(--color-warning))]">⚠ {w}</p>)}
              {!csvValidationResult.rejectedRows?.length && (
                <div className="flex items-center gap-3 pt-1">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Validation passed — {csvValidationResult.rows} quota(s) ready to load.</span>
                  <button type="button" onClick={handleConfirmAndLoad} disabled={csvUploading}
                    className="px-4 py-1.5 rounded-lg bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
                    {csvUploading ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
              {csvValidationResult.rejectedRows?.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))]">
                  <table className="w-full text-tok-xs">
                    <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                      <tr>{['Row #','Field','Error Code','Message'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {csvValidationResult.rejectedRows.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-t border-[rgb(var(--color-border))]">
                          <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{r.row_number}</td>
                          <td className="px-2 py-1 font-mono text-[rgb(var(--color-text))]">{r.field ?? '—'}</td>
                          <td className="px-2 py-1 font-mono text-[rgb(var(--color-danger))]">{r.error_code ?? '—'}</td>
                          <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{r.message ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvValidationResult.rejectedRows.length > 50 && (
                    <p className="px-3 py-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">and {csvValidationResult.rejectedRows.length - 50} more</p>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
        {/* Import history — S579 OI-UI-TABLE-CONSISTENCY */}
        <ImportLogTable title="Quota Load History" logs={logs} loading={loading} error={error} statusTone={STATUS_TONE} emptyText="No quota imports found. Upload a quota CSV to set rep quotas for this period." period={period} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CreditConfigPanel — S553: upload merged in from CreditLoadPanel (deleted).
// Field contracts corrected: employee_id, selling_motion, fiscal_period_code,
// business_unit_code (replaced broken _id variants from old CreditLoadPanel).
// ---------------------------------------------------------------------------

function CreditConfigPanel({ mode, period }) {
  const [logsKey, setLogsKey] = useState(0);
  const { data: logsData, loading, error } = useFetch(`${API_BASE}/import-logs?file_type=credit&_k=${logsKey}`);
  const logs = logsData?.data ?? [];
  const STATUS_TONE = { loaded: "Complete", failed: "Blocked", pending: "Draft" };

  const [csvFile, setCsvFile]                     = useState(null);
  const [csvValidating, setCsvValidating]         = useState(false);
  const [csvUploading, setCsvUploading]           = useState(false);
  const [csvValidationResult, setCsvValidationResult] = useState(null);
  const [csvError, setCsvError]                   = useState('');
  const [csvSuccess, setCsvSuccess]               = useState('');

  // S553: corrected — employee_id (not position_id), selling_motion (not selling_motion_id),
  // fiscal_period_code (not fiscal_period_id), business_unit_code (not business_unit_id)
  const CREDIT_REQUIRED_COLS = [
    'employee_id', 'source_reference_id', 'selling_motion', 'business_unit_code',
    'fiscal_period_code', 'credit_local', 'credit_local_currency_code',
    'credit_reporting', 'credit_reporting_currency_code',
  ];

  async function ensureCreditColumnMapping() {
    const mappings = [
      { source_column_name: 'employee_id',                    target_column_name: 'employee_id',                    is_required: true  },
      { source_column_name: 'source_reference_id',            target_column_name: 'source_reference_id',            is_required: true  },
      { source_column_name: 'selling_motion',                 target_column_name: 'selling_motion',                 is_required: true  },
      { source_column_name: 'business_unit_code',             target_column_name: 'business_unit_code',             is_required: true  },
      { source_column_name: 'fiscal_period_code',             target_column_name: 'fiscal_period_code',             is_required: true  },
      { source_column_name: 'credit_local',                   target_column_name: 'credit_local',                   is_required: true  },
      { source_column_name: 'credit_local_currency_code',     target_column_name: 'credit_local_currency_code',     is_required: true  },
      { source_column_name: 'credit_reporting',               target_column_name: 'credit_reporting',               is_required: true  },
      { source_column_name: 'credit_reporting_currency_code', target_column_name: 'credit_reporting_currency_code', is_required: true  },
      { source_column_name: 'overlay_flag',                   target_column_name: 'overlay_flag',                   is_required: false },
      { source_column_name: 'deal_close_date',                target_column_name: 'deal_close_date',                is_required: false },
      { source_column_name: 'revenue_type',                   target_column_name: 'revenue_type',                   is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'credit', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed credit column mapping.');
  }

  function handleFileChange(e) {
    setCsvFile(e.target.files[0] || null);
    setCsvValidationResult(null);
    setCsvError('');
    setCsvSuccess('');
  }

  async function handleValidate() {
    if (!csvFile) return;
    setCsvValidating(true);
    setCsvError('');
    setCsvValidationResult(null);
    try {
      const text = await csvFile.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      const missing = CREDIT_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) { setCsvError(`Missing required columns: ${missing.join(', ')}`); return; }
      const warnings = [];
      if (!headers.includes('overlay_flag'))   warnings.push('overlay_flag absent — all credits treated as non-overlay.');
      if (!headers.includes('deal_close_date')) warnings.push('deal_close_date absent — deal close date will not be recorded.');
      if (!headers.includes('revenue_type'))    warnings.push('revenue_type absent — revenue type will not be set.');
      setCsvValidationResult({ rows: lines.length - 1, warnings });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvValidating(false); }
  }

  async function handleConfirmAndLoad() {
    if (!csvFile || !csvValidationResult) return;
    setCsvUploading(true);
    setCsvError('');
    try {
      await ensureCreditColumnMapping();
      const fd = new FormData();
      fd.append('file', csvFile);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'credit');
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));

      if (res.status === 422) {
        setCsvError(`${json?.error?.code ?? 'IMPORT_ERROR'}: ${json?.error?.message ?? 'All rows rejected.'}`);
        setCsvValidationResult((prev) => prev ? { ...prev, rejectedRows: json?.error?.rejected_rows ?? [] } : null);
        return;
      }
      if (!res.ok) {
        setCsvError(json?.detail?.error?.message ?? json?.error?.message ?? `Error ${res.status}`);
        return;
      }
      if (res.status === 207) {
        const loaded    = json.rows_loaded ?? 0;
        const unchanged = json.rows_unchanged ?? 0;
        const rejected  = json.rows_rejected ?? 0;
        const logId     = json.import_log?.import_log_id ?? '';
        setCsvSuccess(`Partial load: ${loaded} loaded, ${unchanged} unchanged, ${rejected} rejected.${logId ? ` Load ID: ${logId}` : ''}`);
        setCsvValidationResult((prev) => prev ? { ...prev, rejectedRows: json.rejected_rows ?? [] } : null);
        setLogsKey((k) => k + 1);
        return;
      }
      const logId = json?.import_log?.import_log_id ?? '';
      setCsvSuccess(`${csvValidationResult.rows} credit row(s) loaded.${logId ? ` Load ID: ${logId}` : ''}`);
      setCsvFile(null);
      setCsvValidationResult(null);
      setLogsKey((k) => k + 1);
    } catch (e) { setCsvError(e.message || 'Upload failed.'); }
    finally { setCsvUploading(false); }
  }

  return (
    <div className="w-full space-y-4">
        {/* Period config summary */}
        {period && (
          <Card title="Credit Config">
            <div className="space-y-3">
              <div className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
                <div className="grid gap-1 sm:grid-cols-3"><Field label="Period" value={period.period_label ?? period.period_code} /><Field label="Status" value={period.period_status} /><Field label="Type" value={period.period_type} /></div>
              </div>
              <ValidationItem ok text="Credit configuration is managed via parameter_config. Overlay rules and selling motion mappings are applied at credit load time." />
              <ValidationItem ok text="Credit de-duplication is enforced by source_row_hash — re-uploading the same credit file is safe." />
            </div>
          </Card>
        )}

        {/* CSV Upload */}
        <Card title="Credit Import">
          {/* Required fields callout */}
          <div className="mb-3 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5 text-tok-xs font-mono">
            <p className="mb-1 font-semibold text-[rgb(var(--color-text))]">Required columns</p>
            <p className="text-[rgb(var(--color-primary))]">employee_id, source_reference_id, selling_motion, business_unit_code, fiscal_period_code, credit_local, credit_local_currency_code, credit_reporting, credit_reporting_currency_code</p>
            <p className="mt-1.5 mb-0.5 font-semibold text-[rgb(var(--color-text))]">Optional columns</p>
            <p className="text-[rgb(var(--color-text-muted))]">overlay_flag, deal_close_date, revenue_type</p>
          </div>

          {csvError && (
            <div role="alert" className="mb-3 rounded-xl bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-3 py-2 text-tok-sm">{csvError}</div>
          )}
          {csvSuccess && (
            <div role="status" className="mb-3 rounded-xl bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-3 py-2 text-tok-sm">✓ {csvSuccess}</div>
          )}

          {/* S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly */}
          {period?.period_status !== "open" ? (
            <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
              <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
              <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                Period is <strong>{period?.period_status ?? "not open"}</strong> — credit imports are not available for this period.
                Switch to an open period to import credits.
              </p>
            </div>
          ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <input type="file" accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={handleFileChange}
            />
            <button type="button" onClick={handleValidate} disabled={!csvFile || csvValidating}
              className="px-3 py-1.5 rounded-lg border border-[rgb(var(--color-border))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40">
              {csvValidating ? 'Validating…' : 'Validate'}
            </button>
          </div>
          )}

          {csvValidationResult && (
            <div className="mt-3 space-y-1">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Rows parsed: <strong>{csvValidationResult.rows}</strong></p>
              {csvValidationResult.warnings.map((w, i) => <p key={i} className="text-tok-xs text-[rgb(var(--color-warning))]">⚠ {w}</p>)}
              {!csvValidationResult.rejectedRows?.length && (
                <div className="flex items-center gap-3 pt-1">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">✓ Validation passed — {csvValidationResult.rows} credit(s) ready to load.</span>
                  <button type="button" onClick={handleConfirmAndLoad} disabled={csvUploading}
                    className="px-4 py-1.5 rounded-lg bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
                    {csvUploading ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
              {csvValidationResult.rejectedRows?.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-border))]">
                  <table className="w-full text-tok-xs">
                    <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                      <tr>{['Row #','Field','Error Code','Message'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold text-[rgb(var(--color-text-muted))]">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {csvValidationResult.rejectedRows.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-t border-[rgb(var(--color-border))]">
                          <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{r.row_number}</td>
                          <td className="px-2 py-1 font-mono text-[rgb(var(--color-text))]">{r.field ?? '—'}</td>
                          <td className="px-2 py-1 font-mono text-[rgb(var(--color-danger))]">{r.error_code ?? '—'}</td>
                          <td className="px-2 py-1 text-[rgb(var(--color-text-muted))]">{r.message ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvValidationResult.rejectedRows.length > 50 && (
                    <p className="px-3 py-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">and {csvValidationResult.rejectedRows.length - 50} more</p>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Import history — S579 OI-UI-TABLE-CONSISTENCY */}
        <ImportLogTable title="Credit Load History" logs={logs} loading={loading} error={error} statusTone={STATUS_TONE} emptyText="No credit imports found. Upload a credit CSV to load rep credits for this period." period={period} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TransactionPlansPanel
// ---------------------------------------------------------------------------
function TransactionPlansPanel({ mode, period, wizardSteps }) {
  return (
    <div className="w-full">
      <TransactionPlanManager mode={mode} period={period} wizardSteps={wizardSteps} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DrawPlansPanel
// ---------------------------------------------------------------------------

function DrawPlansPanel({ mode, period, wizardSteps }) {
  return (
    <div className="w-full">
      <DrawPlanManager mode={mode} period={period} wizardSteps={wizardSteps} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DealSplitsPanel  (DealSplitManager built in next session)
// ---------------------------------------------------------------------------
function DealSplitsPanel({ mode, period, wizardSteps }) {
  return (
    <div className="w-full">
      <DealSplitManager mode={mode} period={period} wizardSteps={wizardSteps} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SpiffPlansPanel  (SpiffPlanManager built in next session)
// ---------------------------------------------------------------------------

function SpiffPlansPanel({ mode, period, wizardSteps }) {
  return (
    <div className="w-full">
      <SpiffPlanManager mode={mode} period={period} wizardSteps={wizardSteps} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// P3 Plan Type Panels
// ---------------------------------------------------------------------------
function AcceleratorCapPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><AcceleratorCapManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function KickerPlanPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><KickerPlanManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function RevTypeRateTablePanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><RevTypeRateTableManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function ClawbackPolicyPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><ClawbackPolicyManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function TrueUpPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><TrueUpManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function CoSellPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><CoSellManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function OverlayAttributionPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><OverlayAttributionManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}

// Phase 4 — Pattern B panel wrappers (S377)
function TerritoryPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><TerritoryManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function PodPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><PodManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function ChannelPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><ChannelManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function ContestPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><ContestManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function BonusPoolPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><BonusPoolManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function NonCashAwardPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><NonCashAwardManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function PharmaPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><PharmaManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function MarketSharePanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><MarketShareManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}
function PlanVersioningPanel({ mode, period, wizardSteps }) {
  return <div className="w-full"><PlanVersioningManager mode={mode} period={period} wizardSteps={wizardSteps} /></div>;
}

// CreditLoadPanel — S553: DELETED. Merged into CreditConfigPanel above.
// Route /admin/credit-load now redirects to /admin/credit-config (see AdminPanel routes).

// ---------------------------------------------------------------------------
// StatementsPanel
// ---------------------------------------------------------------------------

function StatementsPanel({ mode, period }) {
  const periodId = period?.fiscal_period_id;
  const { data, loading, error } = useFetch(`${API_BASE}/statement-batches`);
  const batches = data?.data ?? [];
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const STATUS_TONE = { draft: "Draft", pending_approval: "Ready", released: "Complete", archived: "Draft" };

  async function handleCreateBatch() {
    if (!periodId) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/statement-batches`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ fiscal_period_id: periodId }) });
      if (!res.ok) { const body = await res.json().catch(() => ({})); setSubmitError(body?.error?.message ?? body?.detail ?? `Error ${res.status}`); return; }
      window.location.reload();
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  async function handleSubmit(batchId)  { await fetch(`${API_BASE}/statement-batches/${batchId}/submit`,  { method: "POST", credentials: "include" }); window.location.reload(); }
  async function handleRelease(batchId) { await fetch(`${API_BASE}/statement-batches/${batchId}/release`, { method: "POST", credentials: "include" }); window.location.reload(); }

  return (
    <div className="w-full space-y-4">
      <Card title="Statements" action={
        period?.period_status !== "open" ? (
          <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-2.5 py-1.5">
            <Lock className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
            <span className="text-tok-xs text-[rgb(var(--color-warning))]">Period {period?.period_status}</span>
          </div>
        ) : (
          <Button onClick={handleCreateBatch} disabled={!periodId || submitting}>{submitting ? "Creating…" : "Create Batch"}</Button>
        )
      }>
        {!periodId && <ErrorNote message="No open fiscal period — cannot create statement batch." />}
        {submitError && <ErrorNote message={submitError} />}
        {/* Batch list — S579 OI-UI-TABLE-CONSISTENCY */}
        <StatementBatchTable batches={batches} loading={loading} error={error} statusTone={STATUS_TONE} onSubmit={handleSubmit} onRelease={handleRelease} />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PayrollViewPanel — S573
// Mounts PayoutResultsViewer for comp_admin to review per-rep payouts
// from the latest final calculation run before payroll export.
function PayrollViewPanel({ mode, period }) {
  const fiscalPeriodId = period?.fiscal_period_id ?? null;
  return (
    <div className="space-y-4">
      {!fiscalPeriodId && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
          No active fiscal period. Select a period to view payout results.
        </div>
      )}
      {fiscalPeriodId && <PayoutResultsViewer fiscalPeriodId={fiscalPeriodId} />}
    </div>
  );
}

// PayrollPanel
// ---------------------------------------------------------------------------

function PayrollPanel({ mode, period }) {
  const { user } = useAuth();
  const periodId = period?.fiscal_period_id;
  const { data: batchData, loading: batchLoading, error: batchError } = useFetch(`${API_BASE}/payroll-export-batches`);
  const { data: stmtData } = useFetch(`${API_BASE}/statement-batches`);
  const batches = batchData?.data ?? [];
  const releasedStatements = (stmtData?.data ?? []).filter((s) => s.batch_status === "released");
  const [selectedStatementBatchId, setSelectedStatementBatchId] = useState("");
  const [exportFormat, setExportFormat] = useState("csv");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const STATUS_TONE = { draft: "Draft", approved: "Ready", exported: "Complete" };

  async function handleCreate() {
    if (!selectedStatementBatchId || !periodId) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/payroll-export-batches`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ fiscal_period_id: periodId, statement_batch_id: selectedStatementBatchId, export_format: exportFormat }) });
      if (!res.ok) { const body = await res.json(); setSubmitError(body?.error?.message ?? `Error ${res.status}`); }
      else { window.location.reload(); }
    } catch (e) { setSubmitError(e.message); } finally { setSubmitting(false); }
  }

  async function handleApprove(batchId) { await fetch(`${API_BASE}/payroll-export-batches/${batchId}/approve`, { method: "POST", credentials: "include" }); window.location.reload(); }
  async function handleExport(batchId)  { await fetch(`${API_BASE}/payroll-export-batches/${batchId}/export`,  { method: "POST", credentials: "include" }); window.location.reload(); }

  return (
    <div className="w-full space-y-4">
      <Card title="Payroll Export">
        <div className="mb-4 space-y-3 rounded-xl border border-[rgb(var(--color-border))] p-3">
          <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Create Export Batch</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Statement Batch</label>
              <select value={selectedStatementBatchId} onChange={(e) => setSelectedStatementBatchId(e.target.value)} className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none">
                <option value="">{releasedStatements.length === 0 ? "No released statement batches" : "Select statement batch…"}</option>
                {releasedStatements.map((s) => <option key={s.statement_batch_id} value={s.statement_batch_id}>{String(s.statement_batch_id).slice(0, 8)}… — {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Export Format</label>
              <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none">
                <option value="csv">CSV</option><option value="json">JSON</option><option value="fixed_width">Fixed Width</option>
              </select>
            </div>
          </div>
          {submitError && <ErrorNote message={submitError} />}
          <Button onClick={handleCreate} disabled={submitting || !selectedStatementBatchId || !periodId}>{submitting ? "Creating…" : "Create Export Batch"}</Button>
        </div>
        {batchLoading && <LoadingRow />}
        {batchError && <ErrorNote message="Unable to load payroll export batches." />}
        {!batchLoading && !batchError && batches.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No payroll export batches yet.</p>}
        <div className="space-y-2">
          {batches.map((batch) => (
            <div key={batch.payroll_export_batch_id} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="grid flex-1 gap-1 sm:grid-cols-3"><Field label="Format" value={batch.export_format} /><Field label="Rows" value={batch.row_count ?? "—"} /><Field label="Created" value={batch.created_at ? new Date(batch.created_at).toLocaleDateString() : "—"} /></div>
                <Pill tone={STATUS_TONE[batch.export_status] ?? "Draft"}>{batch.export_status}</Pill>
              </div>
              <div className="mt-2 flex gap-2">
                {batch.export_status === "draft" && batch.created_by !== user?.user_id && <Button onClick={() => handleApprove(batch.payroll_export_batch_id)}>Approve</Button>}
                {batch.export_status === "approved" && <Button onClick={() => handleExport(batch.payroll_export_batch_id)}>Export</Button>}
                {batch.export_status === "exported" && batch.export_url && (
                  <Button onClick={async () => {
                    const res = await fetch(`/api/v1/payroll-export-batches/${batch.payroll_export_batch_id}/download`, { credentials: "include" });
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = `payroll-export-${batch.payroll_export_batch_id.slice(0, 8)}.csv`; a.click();
                    URL.revokeObjectURL(url);
                  }}>Download</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
      </div>
  );
}

// ---------------------------------------------------------------------------
// CertificationPanel — extracted to ui/src/shells/CertificationPanel.jsx (S200)
// Imported at top of file. No inline definition here.

// ---------------------------------------------------------------------------
// GOVERN stubs — S228 (§10.2). Content deferred to dedicated GOVERN session.
// ---------------------------------------------------------------------------

function GovernStub({ title }) {
  return (
    <Card title={title}>
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        This area is managed in your Govern workspace. Content coming in a future release.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// FINANCE — FinanceView panel (OI-S394-05 — dual approval chain UX)
//
// Comp_admin approval window for two finance-owned artifacts:
//   1. Plan Input approval  — finance creates, comp_admin approves
//   2. Scenario Set approval — finance creates, comp_admin approves
//
// Both follow identical NN-36 actor separation pattern:
//   approved_by ≠ created_by — enforced server-side, surfaced in UI.
//
// "Waiting for [role]" messaging at each blocked step:
//   - Empty list   → "Waiting for Finance to create"
//   - Self-created → "You created this — a different comp_admin must approve"
//   - Approved     → locked read-only summary
//
// Endpoints:
//   GET  /api/v1/financial-plan-inputs              — list (filtered to period)
//   POST /api/v1/financial-plan-inputs/{id}/approve — approve (§17.2)
//   GET  /api/v1/scenario-sets                      — list
//   POST /api/v1/scenario-sets/{id}/approve         — approve
//
// DD-04: shared/ imports only. DD-05: role from JWT. DD-06: live API only.
// NN-COLUMNS: column names from ddl_summary_r39.md only.
// ---------------------------------------------------------------------------

function FinanceView({ period }) {
  const { user } = useAuth();
  const periodId = period?.fiscal_period_id ?? null;

  // ── Plan Input state ─────────────────────────────────────────────────────
  const [planInputs, setPlanInputs]           = useState([]);
  const [planInputsLoading, setPlanInputsLoading] = useState(true);
  const [planInputsError, setPlanInputsError]     = useState(null);
  const [approvingInput, setApprovingInput]       = useState(null);  // id being approved
  const [inputApproveErrors, setInputApproveErrors] = useState({});  // keyed by id

  function loadPlanInputs() {
    if (!periodId) return;
    setPlanInputsLoading(true);
    setPlanInputsError(null);
    fetch(
      `${API_BASE}/financial-plan-inputs?fiscal_period_id=${periodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setPlanInputs(d?.data ?? []))
      .catch((e) => setPlanInputsError(e.message))
      .finally(() => setPlanInputsLoading(false));
  }

  useEffect(() => { loadPlanInputs(); }, [periodId]);

  async function handleApproveInput(id) {
    setApprovingInput(id);
    setInputApproveErrors((prev) => ({ ...prev, [id]: null }));
    try {
      const r = await fetch(`${API_BASE}/financial-plan-inputs/${id}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const msg = body?.detail?.error?.message ?? body?.error?.message ?? `Error ${r.status}`;
        setInputApproveErrors((prev) => ({ ...prev, [id]: msg }));
        return;
      }
      loadPlanInputs();
    } catch (e) {
      setInputApproveErrors((prev) => ({ ...prev, [id]: e.message }));
    } finally {
      setApprovingInput(null);
    }
  }

  const pendingInputs  = planInputs.filter((i) => i.approval_status === "draft");
  const approvedInputs = planInputs.filter((i) => i.approval_status === "approved");

  // ── Scenario Set state ───────────────────────────────────────────────────
  const [sets, setSets]                       = useState([]);
  const [setsLoading, setSetsLoading]         = useState(true);
  const [setsError, setSetsError]             = useState(null);
  const [approvingSet, setApprovingSet]       = useState(null);  // scenario_set_id being approved
  const [setApproveErrors, setSetApproveErrors] = useState({}); // keyed by scenario_set_id

  function loadSets() {
    if (!periodId) return;
    setSetsLoading(true);
    setSetsError(null);
    fetch(
      `${API_BASE}/scenario-sets?fiscal_period_id=${periodId}&approved_only=false&active_only=false`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setSets(d.scenario_sets ?? []))
      .catch((e) => setSetsError(e.message))
      .finally(() => setSetsLoading(false));
  }

  useEffect(() => { loadSets(); }, [periodId]);

  async function handleApproveSet(ssId) {
    setApprovingSet(ssId);
    setSetApproveErrors((prev) => ({ ...prev, [ssId]: null }));
    try {
      const r = await fetch(`${API_BASE}/scenario-sets/${ssId}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const msg = body?.detail?.error?.message ?? body?.error?.message ?? `Error ${r.status}`;
        setSetApproveErrors((prev) => ({ ...prev, [ssId]: msg }));
        return;
      }
      // S482 Phase E — Commit any open scenario_definition amendment for this period.
      // Non-blocking: amendment commit failure does not block set approval.
      // Mirrors ScenarioSetBuilder.jsx commitAmendment() helper (lines 44–52).
      try {
        const amdRes = await fetch(
          `${API_BASE}/amendments?fiscal_period_id=${periodId}&status=open&entity_type=scenario_definition`,
          { credentials: "include" }
        );
        if (amdRes.ok) {
          const amdData = await amdRes.json().catch(() => ({}));
          const openAmd = (amdData?.data ?? amdData?.amendments ?? [])[0];
          if (openAmd?.amendment_id) {
            await fetch(`${API_BASE}/amendments/${openAmd.amendment_id}/commit`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
            });
          }
        }
      } catch (_) { /* non-blocking — amendment commit failure does not block set approval */ }
      loadSets();
    } catch (e) {
      setSetApproveErrors((prev) => ({ ...prev, [ssId]: e.message }));
    } finally {
      setApprovingSet(null);
    }
  }

  const pendingSets  = sets.filter((s) => !s.approved_at);
  const approvedSets = sets.filter((s) => s.approved_at);

  // ── Derived approval readiness ───────────────────────────────────────────
  const planInputApproved   = approvedInputs.length > 0;
  const scenarioSetApproved = approvedSets.length > 0;

  return (
    <div className="w-full space-y-4">

      {/* ── Main panel ── */}
      <div className="space-y-4">

        {/* ── Approval chain status strip ── */}
        <div className="flex flex-wrap gap-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              Approval Chain
            </span>
          </div>
          <div className="flex flex-wrap gap-2 ml-2">
            <div className="flex items-center gap-1.5">
              <Pill tone={planInputApproved ? "Complete" : "Not Ready"}>
                {planInputApproved ? "Plan Input ✓" : "Plan Input — Pending"}
              </Pill>
            </div>
            <div className="flex items-center gap-1.5">
              <Pill tone={scenarioSetApproved ? "Complete" : "Not Ready"}>
                {scenarioSetApproved ? "Scenario Set ✓" : "Scenario Set — Pending"}
              </Pill>
            </div>
            <div className="flex items-center gap-1.5">
              <Pill tone={planInputApproved && scenarioSetApproved ? "Complete" : "Not Ready"}>
                {planInputApproved && scenarioSetApproved ? "Engine Unblocked" : "Engine Blocked"}
              </Pill>
            </div>
          </div>
        </div>

        {/* ── 1. Plan Input Approval ── */}
        <Card title="Plan Input Approval">
          <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Finance submits revenue targets and variable comp budgets for the period.
            Comp admin countersigns to authorize payroll export. Actor separation enforced — you
            cannot approve a plan input you created (NN-36).
          </div>

          {!periodId && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period.</p>
          )}
          {periodId && planInputsLoading && <LoadingRow />}
          {periodId && planInputsError && <ErrorNote message="Unable to load plan inputs." />}

          {/* Waiting for Finance — empty state */}
          {periodId && !planInputsLoading && !planInputsError && planInputs.length === 0 && (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">
                Waiting for Finance
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                No plan inputs found for this period. Morgan Chen (Finance) must create and
                save a plan input before approval is available here.
              </p>
            </div>
          )}

          {/* All approved — nothing pending */}
          {periodId && !planInputsLoading && !planInputsError && planInputs.length > 0 && pendingInputs.length === 0 && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              All plan inputs for this period are approved.
            </p>
          )}

          {/* Pending inputs */}
          <div className="space-y-3">
            {pendingInputs.map((item) => {
              const isCreator = item.created_by && String(item.created_by) === String(user?.user_id);
              const errMsg    = inputApproveErrors[item.financial_plan_input_id];

              return (
                <div
                  key={item.financial_plan_input_id}
                  className="rounded-xl border border-[rgb(var(--color-border))] p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="grid flex-1 gap-2 sm:grid-cols-3">
                      <Field label="Period"
                        value={item.period_label ?? item.period_code ?? String(item.fiscal_period_id)} />
                      <Field label="Revenue Target"
                        value={item.revenue_target != null
                          ? Number(item.revenue_target).toLocaleString("en-US", { style: "currency", currency: item.budget_currency_code ?? "USD", maximumFractionDigits: 0 })
                          : "—"} />
                      <Field label="Variable Comp Budget"
                        value={item.variable_comp_budget != null
                          ? Number(item.variable_comp_budget).toLocaleString("en-US", { style: "currency", currency: item.budget_currency_code ?? "USD", maximumFractionDigits: 0 })
                          : "—"} />
                      <Field label="Source System"  value={item.source_system ?? "—"} />
                      <Field label="Effective Date" value={item.effective_date ?? "—"} />
                      <div>
                        <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                        <Pill tone="Draft">Draft — Pending Approval</Pill>
                      </div>
                    </div>

                    <div className="shrink-0">
                      {isCreator ? (
                        <Pill tone="Draft">You created this</Pill>
                      ) : (
                        <Button
                          onClick={() => handleApproveInput(item.financial_plan_input_id)}
                          disabled={approvingInput === item.financial_plan_input_id}
                        >
                          {approvingInput === item.financial_plan_input_id ? "Approving…" : "Approve"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isCreator && (
                    <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Actor separation (NN-36): a different comp_admin must approve this plan input.
                    </p>
                  )}
                  {errMsg && <ErrorNote message={errMsg} />}
                </div>
              );
            })}
          </div>

          {/* Approved inputs — read-only summary */}
          {approvedInputs.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                Approved
              </p>
              {approvedInputs.map((item) => (
                <div
                  key={item.financial_plan_input_id}
                  className="grid gap-2 rounded-xl border border-[rgb(var(--color-border))] p-3 sm:grid-cols-4"
                >
                  <Field label="Period"
                    value={item.period_label ?? item.period_code ?? String(item.fiscal_period_id)} />
                  <Field label="Revenue Target"
                    value={item.revenue_target != null
                      ? Number(item.revenue_target).toLocaleString("en-US", { style: "currency", currency: item.budget_currency_code ?? "USD", maximumFractionDigits: 0 })
                      : "—"} />
                  <Field label="Variable Comp Budget"
                    value={item.variable_comp_budget != null
                      ? Number(item.variable_comp_budget).toLocaleString("en-US", { style: "currency", currency: item.budget_currency_code ?? "USD", maximumFractionDigits: 0 })
                      : "—"} />
                  <div>
                    <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                    <Pill tone="Complete">Approved</Pill>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── 2. Scenario Set Approval ── */}
        <Card title="Scenario Set Approval">
          <div className="mb-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Finance configures scenario sets (P50 / P75 / P90 payout trajectories).
            Comp admin countersigns before the engine will accept calculation runs.
            Actor separation is enforced — you cannot approve a set you created (NN-36).
          </div>

          {!periodId && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period.</p>
          )}
          {periodId && setsLoading && <LoadingRow />}
          {periodId && setsError && <ErrorNote message="Unable to load scenario sets." />}

          {/* Waiting for Finance — empty state */}
          {periodId && !setsLoading && !setsError && sets.length === 0 && (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">
                Waiting for Finance
              </p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                No scenario sets found for this period. Finance must create a scenario set
                with P50 / P75 / P90 definitions before approval is available here.
              </p>
            </div>
          )}

          {/* All approved */}
          {periodId && !setsLoading && !setsError && sets.length > 0 && pendingSets.length === 0 && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              All scenario sets for this period are approved.
            </p>
          )}

          <div className="space-y-3">
            {pendingSets.map((s) => {
              const isCreator  = s.created_by && s.created_by === user?.user_id;
              const errMsg     = setApproveErrors[s.scenario_set_id];
              const isRunnable = s.definition_count >= 3 &&
                ["P50", "P75", "P90"].every((c) => (s.definition_codes ?? []).includes(c));

              return (
                <div
                  key={s.scenario_set_id}
                  className="rounded-xl border border-[rgb(var(--color-border))] p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="grid flex-1 gap-2 sm:grid-cols-3">
                      <Field label="Code"   value={s.scenario_set_code} />
                      <Field label="Name"   value={s.scenario_set_name} />
                      <Field label="Period" value={s.period_code ?? String(s.fiscal_period_id)} />
                      <Field
                        label="Definitions"
                        value={
                          s.definition_count > 0
                            ? `${s.definition_count} (${(s.definition_codes ?? []).join(", ")})`
                            : "None — cannot approve"
                        }
                      />
                      <Field
                        label="Effective"
                        value={s.effective_date ? new Date(s.effective_date).toLocaleDateString() : "—"}
                      />
                      <div>
                        <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Ready to approve</p>
                        <Pill tone={isRunnable ? "Ready" : "Not Ready"}>
                          {isRunnable ? "P50 / P75 / P90 present" : "Definitions incomplete"}
                        </Pill>
                      </div>
                    </div>

                    <div className="shrink-0">
                      {isCreator ? (
                        <Pill tone="Draft">You created this</Pill>
                      ) : (
                        <Button
                          onClick={() => handleApproveSet(s.scenario_set_id)}
                          disabled={approvingSet === s.scenario_set_id || !isRunnable}
                        >
                          {approvingSet === s.scenario_set_id ? "Approving…" : "Approve"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isCreator && (
                    <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Actor separation (NN-36): a different comp_admin must approve this set.
                    </p>
                  )}
                  {errMsg && <ErrorNote message={errMsg} />}
                </div>
              );
            })}
          </div>

          {/* Approved scenario sets — read-only summary */}
          {approvedSets.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                Approved
              </p>
              {approvedSets.map((s) => (
                <div
                  key={s.scenario_set_id}
                  className="grid gap-2 rounded-xl border border-[rgb(var(--color-border))] p-3 sm:grid-cols-4"
                >
                  <Field label="Code"   value={s.scenario_set_code} />
                  <Field label="Name"   value={s.scenario_set_name} />
                  <Field label="Period" value={s.period_code ?? String(s.fiscal_period_id)} />
                  <div>
                    <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Status</p>
                    <Pill tone="Complete">Approved</Pill>
                  </div>
                  <Field
                    label="Approved"
                    value={s.approved_at ? new Date(s.approved_at).toLocaleDateString() : "—"}
                  />
                  <Field
                    label="Definitions"
                    value={`${s.definition_count} (${(s.definition_codes ?? []).join(", ")})`}
                  />
                  <div>
                    <p className="mb-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Engine</p>
                    <Pill tone={s.is_runnable ? "Complete" : "Not Ready"}>
                      {s.is_runnable ? "Runnable" : "Not runnable"}
                    </Pill>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

      </div>

      {/* ── System Intelligence ── */}
      </div>
  );
}

// ---------------------------------------------------------------------------
// OperateDashboard — S230 SECOND
// Period selector + ExecuteFlowShell mount for the OPERATE unified flow.
// Route: /admin/operate
//
// Design rules:
// - Period list comes from shell-level fetch (prop: periods).
// - Default selection is shell's active period (prop: operatePeriodId).
// - User can switch period via <select>; fires onOperatePeriodChange so the
//   shell-level scenario set fetch re-runs with the new period.
// - scenarioSetId: shell resolves active scenario set for selected period.
//   If no scenario sets exist, ExecuteFlowShell degrades gracefully.
// - ExecuteFlowShell event bus: 'decisionflow:navigate' — no collision with
//   TenantAdminShell which uses 'decisionflow'.
// ---------------------------------------------------------------------------

function OperateDashboard({ periods, operatePeriodId, operatePeriodLabel, onOperatePeriodChange, scenarioSetId, onPreviewRerunComplete }) {
  const noPeriods = !periods || periods.length === 0;

  return (
    <div className="space-y-4">
      {/* Period selector bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
        <div className="flex items-center gap-2 shrink-0">
          <CalendarDays className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          <span className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
            Operating Period
          </span>
        </div>

        {noPeriods ? (
          <span className="text-tok-sm text-[rgb(var(--color-danger))]">
            No fiscal periods found — create one in SETUP › Calendar.
          </span>
        ) : (
          <select
            value={operatePeriodId ?? ""}
            onChange={(e) => onOperatePeriodChange(e.target.value || null)}
            className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
          >
            {periods.map((p) => (
              <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                {p.period_label ?? p.period_code}
                {p.period_status === "open" ? " (open)" : p.period_status === "closed" ? " (closed)" : " (draft)"}
              </option>
            ))}
          </select>
        )}

        {scenarioSetId && (
          <div className="ml-auto">
            <Pill tone="Ready">Scenario set loaded</Pill>
          </div>
        )}
        {!scenarioSetId && !noPeriods && operatePeriodId && (
          <div className="ml-auto">
            <Pill tone="Draft">No scenario set for period</Pill>
          </div>
        )}
      </div>

      {/* ExecuteFlowShell — mounts only when a period is resolved */}
      {!noPeriods && operatePeriodId ? (
        <ExecuteFlowShell
          fiscalPeriodId={operatePeriodId}
          fiscalPeriodLabel={operatePeriodLabel}
          scenarioSetId={scenarioSetId}
          onPreviewRerunComplete={onPreviewRerunComplete}
        />
      ) : !noPeriods ? (
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-8 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Select a fiscal period above to begin the Execute flow.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route → panel map
// ---------------------------------------------------------------------------

function AdminPanel({ mode, period, wizardSteps, wizardSessionId, periods, operatePeriodId, operatePeriodLabel, onOperatePeriodChange, scenarioSetId, onPreviewRerunComplete, role, tenantContext }) {
  return (
    <Routes>
      <Route index element={<Navigate to="import" replace />} />
      {/* SETUP */}
      <Route path="import"            element={<ImportDataPanel period={period} />} />
      <Route path="commercial-roles"  element={<CommercialRoleLibrary />} />
      <Route path="settings"          element={<SettingsPanel role={role} tenantContext={tenantContext} />} />
      {/* Legacy deep-link: setup-guide preserved */}
      <Route path="setup-guide"      element={<Navigate to="/admin/import" replace />} />
      <Route path="fiscal-calendar"  element={<FiscalCalendarPanel mode={mode} />} />
      <Route path="organization"     element={<OrganizationPanel mode={mode} />} />
      <Route path="sales-roles"      element={<SalesRolesPanel mode={mode} />} />
      <Route path="plans"            element={<PlansLandingPage period={period} />} />
      <Route path="plans/:planId"    element={<PlanDetailView mode={mode} period={period} />} />
      <Route path="plans/:planId/components/:componentId/:modifier" element={<PlanDetailView mode={mode} period={period} />} />
      <Route path="plan-wizard"      element={<PlansLandingPage period={period} />} />
      {/* S540: plan-approval redirects to workspace Approvals tab — PlanApprovalPanel now lives there */}
      <Route path="plan-approval" element={<Navigate to="/admin/compensation?tab=approvals" replace />} />
      {/* S540: Compensation Policies workspace — single entry point for all plan-related work */}
      <Route path="compensation/*" element={
        <CompensationPoliciesWorkspace
          mode={mode}
          period={period}
          wizardSteps={wizardSteps}
          wizardSessionId={wizardSessionId}
        />
      } />
      <Route path="position-load"    element={<PositionLoadPanel mode={mode} period={period} />} />
      <Route path="position-detail"  element={<PositionDetailPanel mode={mode} period={period} />} />
      <Route path="plan-assignment"  element={<PlanAssignmentPanel mode={mode} period={period} />} />
      <Route path="quota-load"       element={<QuotaLoadPanel mode={mode} period={period} />} />
      <Route path="credit-config"    element={<CreditConfigPanel mode={mode} period={period} />} />
      <Route path="rate-tables"      element={<RateTablesPanel mode={mode} period={period} />} />
      <Route path="ramp-schedules"   element={<RampSchedulesPanel mode={mode} period={period} />} />
      <Route path="ote-history"      element={<OteHistoryPanel mode={mode} period={period} />} />
      <Route path="transaction-plans" element={<TransactionPlansPanel mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="draw-plans"  element={<DrawPlansPanel  mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="deal-splits" element={<DealSplitsPanel mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="spiff-plans" element={<SpiffPlansPanel mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="accelerator-caps"    element={<AcceleratorCapPanel    mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="kicker-plans"        element={<KickerPlanPanel        mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="rev-type-rates"      element={<RevTypeRateTablePanel  mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="clawback-policies"   element={<ClawbackPolicyPanel    mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="transaction-plans"   element={<TransactionPlansPanel  mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="draw-plans"          element={<DrawPlansPanel         mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="deal-splits"         element={<DealSplitsPanel        mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="spiff-plans"         element={<SpiffPlansPanel        mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="true-up"             element={<TrueUpPanel            mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="cosell"              element={<CoSellPanel            mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="overlay-attribution" element={<OverlayAttributionPanel mode={mode} period={period} wizardSteps={wizardSteps} />} />
      {/* OPERATE */}
      <Route path="operate" element={
        <OperateDashboard
          periods={periods}
          operatePeriodId={operatePeriodId}
          operatePeriodLabel={operatePeriodLabel}
          onOperatePeriodChange={onOperatePeriodChange}
          scenarioSetId={scenarioSetId}
          onPreviewRerunComplete={onPreviewRerunComplete}
        />
      } />
      <Route path="credit-load"      element={<Navigate to="/admin/credit-config" replace />} />
      <Route path="readiness"        element={<ReadinessPanel mode={mode} period={period} />} />
      <Route path="calculation"      element={<CalculationPanel mode={mode} period={period} />} />
      <Route path="statements"       element={<StatementsPanel mode={mode} period={period} />} />
      <Route path="payroll-view"     element={<PayrollViewPanel mode={mode} period={period} />} />
      <Route path="payroll"          element={<PayrollPanel mode={mode} period={period} />} />
      <Route path="certification"    element={<CertificationPanel mode={mode} period={period} />} />
      <Route path="exceptions"       element={<CompException period={period} />} />
      <Route path="disputes"         element={<CompDispute period={period} />} />
      {/* GOVERN — S620: Change Management */}
      <Route path="change-management" element={
        <ChangeManagementPanel
          period={period}
          fiscalPeriodId={period?.fiscal_period_id ?? null}
          wizardSessionId={wizardSessionId}
        />
      } />
      <Route path="governance/*" element={
        <AdminFlowShell
          fiscalPeriodId={period?.fiscal_period_id ?? null}
          wizardSessionId={wizardSessionId}
        />
      } />
      <Route path="govern/org"            element={<GovernStub title="Org Changes" />} />
      <Route path="govern/integrations"   element={<GovernStub title="Integrations" />} />
      <Route path="govern/audit"          element={<GovernStub title="Audit & History" />} />
      <Route path="govern/parameters"     element={<Navigate to="/admin/parameters" replace />} />
      <Route path="parameters"            element={<ParameterConfigEditor />} />
      <Route path="trust-center" element={<TrustCenter />} />
      {/* FINANCE — S393: FinanceViewStub replaced with FinanceView (scenario set approval) */}
      <Route path="finance-view"          element={<FinanceView period={period} />} />
      {/* COMPENSATION POLICIES — Phase 4 standalone managers (S377/S379) */}
      <Route path="territory"       element={<TerritoryPanel       mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="pods"            element={<PodPanel             mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="channel"         element={<ChannelPanel         mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="contests"        element={<ContestPanel         mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="bonus-pools"     element={<BonusPoolPanel       mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="non-cash-awards" element={<NonCashAwardPanel    mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="pharma"          element={<PharmaPanel          mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="market-share"    element={<MarketSharePanel     mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="plan-versioning" element={<PlanVersioningPanel  mode={mode} period={period} wizardSteps={wizardSteps} />} />
      <Route path="*"                     element={<Navigate to="import" replace />} />
    </Routes>
  );
}

// ---------------------------------------------------------------------------
// CompAdminShell root
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// TrialBanner — shown when subscription_mode === 'trial'
// ---------------------------------------------------------------------------
function TrialBanner({ daysRemaining }) {
  const tone =
    daysRemaining === 0
      ? "bg-[rgb(var(--color-danger))]/10 border-[rgb(var(--color-danger))]/40 text-[rgb(var(--color-danger))]"
      : daysRemaining <= 3
      ? "bg-[rgb(var(--color-danger))]/8 border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))]"
      : daysRemaining <= 7
      ? "bg-[rgb(var(--color-warning))]/8 border-[rgb(var(--color-warning))]/30 text-[rgb(var(--color-warning))]"
      : "bg-[rgb(var(--color-surface-muted))] border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))]";

  if (daysRemaining === 0) {
    return (
      <div className={`flex items-center gap-3 border-b px-4 py-2 text-tok-sm font-medium ${tone}`}>
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>Trial expired — your account is suspended. Upgrade to restore access.</span>
        <NavLink to="/admin/settings" className="ml-auto rounded-lg border border-current px-3 py-1 text-tok-xs font-semibold hover:opacity-80">
          Upgrade Now
        </NavLink>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 border-b px-4 py-2 text-tok-sm font-medium ${tone}`}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>⚠ Trial · {daysRemaining} day{daysRemaining !== 1 ? "s" : ""} remaining</span>
      <NavLink to="/admin/settings" className="ml-auto rounded-lg border border-current px-3 py-1 text-tok-xs font-semibold hover:opacity-80">
        Upgrade Now
      </NavLink>
    </div>
  );
}

export default function CompAdminShell() {
  const { user } = useAuth();
  const location = useLocation();
  const mode = import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(true);

  // S657 OI-LEFT-NAV-HIDEABLE: nav hideable, persisted in sessionStorage (mirrors Copilot pattern)
  const [navOpen, setNavOpen] = useState(() => {
    const stored = sessionStorage.getItem("df_nav_open");
    return stored === null ? true : stored !== "false";
  });
  function handleToggleNav() {
    setNavOpen((v) => {
      sessionStorage.setItem("df_nav_open", String(!v));
      return !v;
    });
  }

  // Role from JWT — DD-05 (dev role switcher removed S623)
  const role = user?.user_type ?? "comp_admin";

  // Active period (spec §3.1 workspace block)
  // S134 — was filtering period_status=open but active period can be locked/closed.
  // Removed filter — API defaults to active_only=true (confirmed in network response S200).
  // S580 fix: M01 locked → shell was resolving M01 (earliest) instead of M02 (open).
  // Correct resolution: prefer open period first; fall back to earliest non-locked period
  // so the shell always hands downstream panels the period they can actually act on.
  //
  // S602 — OI-PERIOD-CONTEXT-SWITCHER: period is now user-controllable state.
  // selectedPeriodId: null means "use default resolution" (behaviour preserved from S580).
  // User selection via PeriodSelector in CompAdminContextHeader overrides the default.
  // All downstream panels, wizardSteps fetch, and CopilotPanel re-key automatically
  // because they all read from `period` which is derived from selectedPeriodId.
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const periods = periodsData?.data ?? periodsData?.fiscal_periods ?? [];
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  // Default period resolution — identical to S580 logic.
  // Used when selectedPeriodId is null (no explicit user selection yet).
  const defaultPeriod = (() => {
    if (!periods.length) return null;
    const open = periods.filter((p) => p.period_status === "open");
    if (open.length) {
      return open.reduce((earliest, p) =>
        p.period_start_date < earliest.period_start_date ? p : earliest
      );
    }
    // No open period — fall back to earliest non-locked (closed is readable)
    const nonLocked = periods.filter((p) => p.period_status !== "locked");
    if (nonLocked.length) {
      return nonLocked.reduce((earliest, p) =>
        p.period_start_date < earliest.period_start_date ? p : earliest
      );
    }
    // All locked — fall back to earliest
    return periods.reduce((earliest, p) =>
      p.period_start_date < earliest.period_start_date ? p : earliest
    );
  })();

  // `period` is the single source of truth for all downstream panels.
  // Resolves to user selection if set, otherwise falls back to defaultPeriod.
  const period = selectedPeriodId
    ? (periods.find((p) => p.fiscal_period_id === selectedPeriodId) ?? defaultPeriod)
    : defaultPeriod;

  // True when the resolved period is not the currently open period.
  // OI-SHELL-PERIOD-DEFAULT-STALE: check period_status dynamically rather than
  // comparing against the mount-time defaultPeriod ID. After a roll-forward the
  // open period changes; comparing IDs would incorrectly flag the new open period
  // as non-default if the user hadn't re-selected it.
  const isNonDefaultPeriod = period !== null && period.period_status !== "open";

  // S77 — tenant-context: proactive tier gate (Stage 10). Replaces attempt-then-discover 403.
  // ER-1: server is single source of truth. features.allows_ai_query drives render-time suppression.
  const { data: tenantContext } = useFetch(`${API_BASE}/tenant-context`);
  const aiQueryEnabled = tenantContext?.features?.allows_ai_query ?? null; // null = loading

  // S669 — financeViewEnabled/trustCenterEnabled removed.
  // Feature gating now unified through FeatureContext in CompAdminNav.

  // S552 — Trial banner: subscription_mode from tenant-context
  const subscriptionMode = tenantContext?.subscription_mode ?? null;
  const trialDaysRemaining = tenantContext?.trial_days_remaining ?? null;
  const isTrial = subscriptionMode === "trial";

  // S552 — Readiness badge: open exceptions + disputes count fetched on shell load
  const { data: exceptionsData } = useFetch(`${API_BASE}/exceptions?status=open&limit=1`);
  const { data: disputesData }   = useFetch(`${API_BASE}/disputes?status=open&limit=1`);
  const openExceptions = exceptionsData?.total ?? exceptionsData?.count ?? (exceptionsData?.data?.length ?? 0);
  const openDisputes   = disputesData?.total   ?? disputesData?.count   ?? (disputesData?.data?.length ?? 0);
  const resolveBadge   = openExceptions + openDisputes;

  // S657 OI-STATEMENT-ACKNOWLEDGEMENT: Statements nav badge — unacknowledged rep count
  const { data: stmtAckSummaryData } = useFetch(
    period?.fiscal_period_id
      ? `${API_BASE}/statement-acknowledgments/summary?fiscal_period_id=${period.fiscal_period_id}`
      : null
  );
  const stmtBadge = stmtAckSummaryData?.unacknowledged_count ?? 0;

  // AD-176 §4.5 — Comp workspace nav badges fetched at shell level.
  // Plans badge: CA-1 attention — required_modifier_missing_count + open_amendment_count across all plans.
  // Approvals badge: CA-2 pending queue — unapproved components + pending-approval plans.
  // Stateless-compatible: standard GET requests, no server affinity.
  const { data: allPlansData }     = useFetch(`${API_BASE}/plans`);
  const { data: pendingCompsData } = useFetch(`${API_BASE}/plan-components`);
  const { data: pendingPlansData } = useFetch(`${API_BASE}/plans?plan_status=pending_approval`);
  const _allPlans = allPlansData?.data ?? allPlansData?.plans ?? [];
  const _planMap  = Object.fromEntries(_allPlans.map((p) => [String(p.plan_id), p]));
  const compPlansBadge = _allPlans.reduce(
    (sum, p) => sum + (p.required_modifier_missing_count ?? 0) + (p.open_amendment_count ?? 0), 0
  );
  const compApprovalsBadge =
    (pendingCompsData?.plan_components ?? pendingCompsData?.data ?? []).filter(
      (c) => !c.approved_by && !c.approved_at && _planMap[String(c.plan_id)]?.plan_status !== "approved"
    ).length +
    (pendingPlansData?.data ?? pendingPlansData?.plans ?? []).length;

  // S620 — CCM §7.2: pending amendment + package count for Change Management nav badge
  const { data: pendingAmendmentsData } = useFetch(
    `${API_BASE}/amendments?amendment_status=pending_approval&limit=50`
  );
  const { data: pendingPackagesData } = useFetch(
    `${API_BASE}/amendments/packages?package_status=pending_approval&limit=50`
  );
  const pendingAmendmentCount =
    (pendingAmendmentsData?.amendments?.length ?? 0) +
    (pendingPackagesData?.packages?.length ?? 0);

  // Wizard session for progress bars (spec §6.2)
  // OI-S208-03: corrected path — router is mounted at /api/v1/wizard/session (not /wizard-session)
  const { data: wizardData } = useFetch(
    period?.fiscal_period_id
      ? `${API_BASE}/wizard/session?fiscal_period_id=${period.fiscal_period_id}`
      : null
  );
  const wizardSteps = wizardData?.steps ?? [];
  const wizardSessionId = wizardData?.wizard_session_id ?? wizardData?.session_id ?? null;

  // Resizable nav — persisted in sessionStorage so width survives route changes
  const [navWidth, setNavWidth] = useState(() => {
    const stored = sessionStorage.getItem("df_nav_width");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed >= NAV_MIN_WIDTH && parsed <= NAV_MAX_WIDTH
      ? parsed
      : NAV_DEFAULT_WIDTH;
  });

  function handleNavWidthChange(width) {
    setNavWidth(width);
    sessionStorage.setItem("df_nav_width", String(width));
  }

  // S230 SECOND — OPERATE period selector state for OperateDashboard / ExecuteFlowShell.
  // null means "use shell default" (earliest open period). User selection overrides.
  const [operatePeriodId, setOperatePeriodId] = useState(null);
  const [previewRunCompleted, setPreviewRunCompleted] = useState(false); // S247 Phase 10
  const effectiveOperatePeriodId = operatePeriodId ?? period?.fiscal_period_id ?? null;
  const effectiveOperatePeriodLabel =
    periods.find((p) => p.fiscal_period_id === effectiveOperatePeriodId)?.period_label ??
    periods.find((p) => p.fiscal_period_id === effectiveOperatePeriodId)?.period_code ??
    period?.period_label ??
    period?.period_code ??
    null;

  // Shell-level scenario set fetch — passed to ExecuteFlowShell via OperateDashboard.
  // Conditional on effectiveOperatePeriodId so no fetch fires before period resolves.
  const { data: shellScenarioData } = useFetch(
    effectiveOperatePeriodId
      ? `${API_BASE}/scenario-sets?fiscal_period_id=${effectiveOperatePeriodId}`
      : null
  );
  const shellScenarioSets = shellScenarioData?.scenario_sets ?? [];
  const defaultScenarioSetId =
    shellScenarioSets.find((s) => s.is_active)?.scenario_set_id ??
    shellScenarioSets[0]?.scenario_set_id ??
    null;

  // Active nav item from current route
  const activeNavItem = ROUTE_TO_NAV[location.pathname] ?? null;

  const userName   = user?.full_name ?? user?.email ?? null;
  const tenantName = user?.tenant_name ?? tenantContext?.tenant_name ?? tenantContext?.tenant_slug ?? null;

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      <TopBar
        userName={userName}
        userRole={role}
        userRoleLabel={ROLE_PROFILES[role]?.label ?? role}
        tenantName={tenantName}
        mode={mode}
        onMenuOpen={() => setMobileOpen(true)}
        copilotOpen={copilotOpen}
        onToggleCopilot={() => setCopilotOpen(!copilotOpen)}
        navOpen={navOpen}
        onToggleNav={handleToggleNav}
      />

      {/* S552 — Trial banner: persistent above main content when subscription_mode === 'trial' */}
      {isTrial && trialDaysRemaining !== null && (
        <TrialBanner daysRemaining={trialDaysRemaining} />
      )}

      {/* S660 — Seat limit banner: shown when active positions exceed purchased seats */}
      <SeatLimitBanner
        activePositions={tenantContext?.active_positions ?? null}
        maxPositions={tenantContext?.max_positions ?? null}
      />

      <div className="flex min-h-[calc(100vh-64px)] min-w-0">
        <CompAdminNav
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
          mode={mode}
          role={role}
          resolveBadge={resolveBadge}
          navWidth={navWidth}
          onNavWidthChange={handleNavWidthChange}
          stmtBadge={stmtBadge}
          compPlansBadge={compPlansBadge}
          compApprovalsBadge={compApprovalsBadge}
          navOpen={navOpen}
        />

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <CompAdminContextHeader
            navItem={activeNavItem}
            location={location}
            mode={mode}
            period={period}
            periods={periods}
            selectedPeriodId={selectedPeriodId}
            defaultPeriod={defaultPeriod}
            isNonDefaultPeriod={isNonDefaultPeriod}
            onPeriodSelect={setSelectedPeriodId}
            wizardSteps={wizardSteps}
            userName={userName}
            userRole={role}
            tenantName={tenantName}
            previewRunCompleted={previewRunCompleted}
          />

          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <AdminPanel
              mode={mode}
              period={period}
              wizardSteps={wizardSteps}
              wizardSessionId={wizardSessionId}
              periods={periods}
              operatePeriodId={effectiveOperatePeriodId}
              operatePeriodLabel={effectiveOperatePeriodLabel}
              onOperatePeriodChange={setOperatePeriodId}
              scenarioSetId={defaultScenarioSetId}
              onPreviewRerunComplete={() => setPreviewRunCompleted(true)}
              role={role}
              tenantContext={tenantContext}
            />
          </motion.div>
        </main>

        <div className="hidden xl:block">
          <CopilotPanel
            open={copilotOpen}
            stageName={activeNavItem?.label}
            aiQueryEnabled={aiQueryEnabled}
            fiscalPeriodId={period?.fiscal_period_id ?? null}
            stageId={typeof activeNavItem?.stageId === "number" ? activeNavItem.stageId : null}
            guideContent={SI_CONTENT[location.pathname] ?? null}
          />
        </div>
      </div>

      {/* Mobile copilot */}
      <div className="fixed bottom-4 right-4 xl:hidden flex gap-2">
        <Button onClick={() => setCopilotOpen(!copilotOpen)}>
          <Sparkles className="h-4 w-4" /> Copilot
        </Button>
      </div>
      <AnimatePresence>
        {copilotOpen && (
          <div className="xl:hidden">
            <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setCopilotOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              className="fixed right-0 top-0 z-50 h-full w-full max-w-sm overflow-auto"
            >
              <CopilotPanel
                open={true}
                stageName={activeNavItem?.label}
                aiQueryEnabled={aiQueryEnabled}
                fiscalPeriodId={period?.fiscal_period_id ?? null}
                stageId={typeof activeNavItem?.stageId === "number" ? activeNavItem.stageId : null}
                guideContent={SI_CONTENT[location.pathname] ?? null}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
